import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Chart } from "react-google-charts";
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import { Link, useSearchParams } from 'react-router-dom';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import tableHelpers from '../helpers/TableHelpers.js';
import { getLocalDate } from '../helpers/DateHelpers.js';
import AlgoFinder from '../algos/AlgoFinder';
import StockResearch from '../stock/StockResearch';

// Base price fields
const PRICE_FIELDS = ['close', 'open', 'high', 'low', 'volume'];

// Multi-value indicator output fields mapping
const INDICATOR_SUBFIELDS = {
    bollinger: [
        { key: 'upperBand', label: 'Upper Band' },
        { key: 'lowerBand', label: 'Lower Band' },
        { key: 'avg', label: 'Middle Band (SMA)' },
        { key: 'value', label: 'Normalized Distance' }
    ],
    macd: [
        { key: 'value', label: 'MACD Line' },
        { key: 'signal', label: 'Signal Line' }
    ],
    stochastic: [
        { key: 'value', label: '%K Line' },
        { key: 'd', label: '%D Line' }
    ],
    stochrsi: [
        { key: 'value', label: '%K Line' },
        { key: 'd', label: '%D Line' }
    ],
    adx: [
        { key: 'value', label: 'ADX Line' },
        { key: 'diplus', label: '+DI Line' },
        { key: 'diminus', label: '-DI Line' }
    ]
};

// Default indicator list as initial state while /api/indicators loads
const DEFAULT_INDICATORS = [
    { type: 'adx', location: 'lower', config: { period: 14 } },
    { type: 'atr', location: 'lower', config: { period: 20 } },
    { type: 'barsSinceRecentHigh', location: 'lower', config: { period: 252 } },
    { type: 'barsSinceRecentLow', location: 'lower', config: { period: 252 } },
    { type: 'bollinger', location: 'upper', config: { period: 20, stdDev: 2 } },
    { type: 'drawdown', location: 'lower', config: {} },
    { type: 'ema', location: 'upper', config: { period: 20 } },
    { type: 'ibs', location: 'lower', config: {} },
    { type: 'linearregressionangle', location: 'lower', config: { period: 20 } },
    { type: 'macd', location: 'lower', config: { period: 12, slowPeriod: 26, signalPeriod: 9 } },
    { type: 'roc', location: 'lower', config: { period: 20 } },
    { type: 'rsi', location: 'lower', config: { period: 14 } },
    { type: 'sma', location: 'upper', config: { period: 20 } },
    { type: 'smaslope', location: 'lower', config: { period: 50, measurePeriod: 1 } },
    { type: 'stochastic', location: 'lower', config: { period: 14, dPeriod: 3, smoothK: 3 } },
    { type: 'volumersi', location: 'lower', config: { period: 14 } },
    { type: 'williamsr', location: 'lower', config: { period: 14 } },
    { type: 'wma', location: 'upper', config: { period: 20 } }
];

const BULL_BEAR_MA_OPTIONS = [20, 50, 100, 150, 200, 250];
const DEFAULT_BULL_BEAR_MOVING_AVERAGE = 50;

const MARKET_SYMBOL_OPTIONS = ['SPY', 'QQQ', 'GLD', 'RSP'];
const DEFAULT_MARKET_SYMBOL = 'SPY';

const PRESET_ROW_DEFINITIONS = [
    {
        id: 'core',
        groups: [
            { id: 'cycle', label: 'Market Cycle', presetNames: ['Bull', 'Transition', 'Bear', 'Recovery'] },
            { id: 'vix', label: 'VIX', presetNames: ['Low', 'Medium', 'High'] },
            { id: 'concentration', label: 'Leadership', presetNames: ['Growth', 'Value', 'None'] },
            { id: 'adx', label: 'Trend Strength', presetNames: ['Choppy', 'Trending', 'Strong'] },
            { id: 'momentum', label: 'Momentum', presetNames: ['Up', 'Down'] },
        ]
    },
    {
        id: 'trend-strength',
        groups: [
            { id: 'drawdown', label: 'Drawdown', presetNames: ['Near ATH', 'In Correction', 'In Bear Market'] },
            { id: 'vol', label: 'Vol', presetNames: ['Compression', 'Drift', 'Spike'] },
        ]
    },
    {
        id: 'analysis',
        groups: [
            { id: 'sma200', label: '200-Day SMA', presetNames: ['Above', 'Below'] },
        ]
    }
];

const TRAJECTORY_METRICS = [
    { key: 'cagr', label: 'Regime CAGR (%)', format: 'percentChange' },
    { key: 'spyCagr', label: 'SPY CAGR (%)', format: 'percentChange' },
    { key: 'winRate', label: 'Regime Win Rate (%)', format: 'percent' },
    { key: 'sharpe', label: 'Regime Sharpe Ratio', format: 'decimal' },
    { key: 'spyWinRate', label: 'SPY Win Rate (%)', format: 'percent' },
    { key: 'spyAvgDailyReturn', label: 'SPY Avg Daily Return (%)', format: 'percentChange' },
    { key: 'cumulativeReturn', label: 'Regime Cumulative Return (%)', format: 'percentChange' },
    { key: 'maxDrawdown', label: 'Regime Max Drawdown (%)', format: 'percent' },
    { key: 'avgDrawdown', label: 'Regime Avg Drawdown (%)', format: 'percent' },
    { key: 'avgDailyReturn', label: 'Regime Avg Daily Return (%)', format: 'percentChange' },
    { key: 'capitalEfficiency', label: 'Regime Capital Efficiency', format: 'decimal' },
    { key: 'avgExposure', label: 'Regime Avg Exposure (%)', format: 'percent' },
    { key: 'episodeWinRate', label: 'Regime Episode Win Rate (%)', format: 'percent' },
    { key: 'inDaysPct', label: 'Time in Regime (%)', format: 'percent' }
];

const FORWARD_DURATIONS = [
    { key: '1w', label: '1W', days: 5, sublabel: '5d' },
    { key: '2w', label: '2W', days: 10, sublabel: '10d' },
    { key: '1m', label: '1M', days: 22, sublabel: '22d' },
    { key: '2m', label: '2M', days: 44, sublabel: '44d' },
    { key: '3m', label: '3M', days: 66, sublabel: '66d' }
];

function formatMetricDisplay(val, format) {
    if (val === null || val === undefined || isNaN(val)) return '0.0';
    if (format === 'percent') return `${Number(val).toFixed(1)}%`;
    if (format === 'percentChange') return (Number(val) > 0 ? '+' : '') + `${Number(val).toFixed(1)}%`;
    if (format === 'decimal') return Number(val).toFixed(2);
    return `${val}`;
}

function createTrajectoryTooltip(dStr, regimeName, leftLabel, leftFmt, rightLabel, rightFmt) {
    return `<div style="padding: 10px 14px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 12px; min-width: 220px; box-shadow: 0 4px 14px rgba(0,0,0,0.18); border-radius: 6px; background: #ffffff; border: 1px solid #ced4da; line-height: 1.4;">` +
        `<div style="font-weight: 700; font-size: 13px; color: #212529; margin-bottom: 5px; border-bottom: 1px solid #e9ecef; padding-bottom: 4px;">` +
        `<span>📅 ${tableHelpers.dateFormmatter(dStr)}</span>` +
        `</div>` +
        `<div style="margin-bottom: 8px;">` +
        `<div style="font-size: 10px; color: #6c757d; text-transform: uppercase; font-weight: bold; letter-spacing: 0.5px;">Active Regime</div>` +
        `<div style="font-weight: 600; color: #343a40; font-size: 12px;">${regimeName || 'None'}</div>` +
        `</div>` +
        `<div style="display: flex; justify-content: space-between; gap: 12px; margin-bottom: 3px;">` +
        `<span style="color: #0d6efd; font-weight: 600;">${leftLabel}:</span>` +
        `<span style="font-weight: 700; color: #0d6efd;">${leftFmt}</span>` +
        `</div>` +
        `<div style="display: flex; justify-content: space-between; gap: 12px;">` +
        `<span style="color: #198754; font-weight: 600;">${rightLabel}:</span>` +
        `<span style="font-weight: 700; color: #198754;">${rightFmt}</span>` +
        `</div>` +
        `</div>`;
}

function getRegimePresets(bullBearMovingAverage = DEFAULT_BULL_BEAR_MOVING_AVERAGE, marketSymbol = DEFAULT_MARKET_SYMBOL) {
    const ma = Number(bullBearMovingAverage) || DEFAULT_BULL_BEAR_MOVING_AVERAGE;
    const market = marketSymbol || DEFAULT_MARKET_SYMBOL;
    return [
        {
            name: 'Bull',
            description: `Average price is rising (${ma} SMA)`,
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'smaslope',
                    config1: { period: ma, measurePeriod: 1 },
                    subField1: '',
                    period1: ma,
                    operator: '>=',
                    compareType: 'value',
                    value: 0
                },
                {
                    id: 2,
                    symbol: market,
                    field1: 'close',
                    config1: {},
                    subField1: '',
                    period1: 1,
                    operator: '>=',
                    compareType: 'indicator',
                    value: 0,
                    compareSymbol: market,
                    field2: 'sma',
                    config2: { period: ma },
                    subField2: '',
                    period2: ma
                }
            ]
        },
        {
            name: 'Transition',
            description: `Average price is declining (${ma} SMA)`,
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'smaslope',
                    config1: { period: ma, measurePeriod: 1 },
                    subField1: '',
                    period1: ma,
                    operator: '>=',
                    compareType: 'value',
                    value: 0
                },
                {
                    id: 2,
                    symbol: market,
                    field1: 'close',
                    config1: {},
                    subField1: '',
                    period1: 1,
                    operator: '<',
                    compareType: 'indicator',
                    value: 0,
                    compareSymbol: market,
                    field2: 'sma',
                    config2: { period: ma },
                    subField2: '',
                    period2: ma
                }
            ]
        },
        {
            name: 'Bear',
            description: `Average price is falling (${ma} SMA)`,
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'smaslope',
                    config1: { period: ma, measurePeriod: 1 },
                    subField1: '',
                    period1: ma,
                    operator: '<',
                    compareType: 'value',
                    value: 0
                },
                {
                    id: 2,
                    symbol: market,
                    field1: 'close',
                    config1: {},
                    subField1: '',
                    period1: 1,
                    operator: '<',
                    compareType: 'indicator',
                    value: 0,
                    compareSymbol: market,
                    field2: 'sma',
                    config2: { period: ma },
                    subField2: '',
                    period2: ma
                }
            ]
        },
        {
            name: 'Recovery',
            description: `Average price is recovering (${ma} SMA)`,
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'smaslope',
                    config1: { period: ma, measurePeriod: 1 },
                    subField1: '',
                    period1: ma,
                    operator: '<',
                    compareType: 'value',
                    value: 0
                },
                {
                    id: 2,
                    symbol: market,
                    field1: 'close',
                    config1: {},
                    subField1: '',
                    period1: 1,
                    operator: '>',
                    compareType: 'indicator',
                    value: 0,
                    compareSymbol: market,
                    field2: 'sma',
                    config2: { period: ma },
                    subField2: '',
                    period2: ma
                }
            ]
        },
        {
            name: 'Up',
            description: `MACD > MACD Signal`,
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'macd',
                    config1: { period: 12, slowPeriod: 26, signalPeriod: 9 },
                    subField1: 'value',
                    period1: 1,
                    operator: '>',
                    compareType: 'indicator',
                    value: 0,
                    compareSymbol: market,
                    field2: 'macd',
                    config2: { period: 12, slowPeriod: 26, signalPeriod: 9 },
                    subField2: 'signal',
                    period2: 1
                },
            ]
        },
        {
            name: 'Down',
            description: `MACD < MACD Signal`,
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'macd',
                    config1: { period: 12, slowPeriod: 26, signalPeriod: 9 },
                    subField1: 'value',
                    period1: 1,
                    operator: '<',
                    compareType: 'indicator',
                    value: 0,
                    compareSymbol: market,
                    field2: 'macd',
                    config2: { period: 12, slowPeriod: 26, signalPeriod: 9 },
                    subField2: 'signal',
                    period2: 1
                },
            ]
        },
        {
            name: 'Choppy',
            description: 'Choppy Market',
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'adx',
                    config1: { period: 14 },
                    subField1: '',
                    period1: 1,
                    operator: '<',
                    compareType: 'value',
                    value: 20
                }
            ]
        },
        {
            name: 'Trending',
            description: 'Trending Market',
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'adx',
                    config1: { period: 14 },
                    subField1: '',
                    period1: 1,
                    operator: '>=',
                    compareType: 'value',
                    value: 20
                },
                {
                    id: 2,
                    symbol: market,
                    field1: 'adx',
                    config1: { period: 14 },
                    subField1: '',
                    period1: 1,
                    operator: '<',
                    compareType: 'value',
                    value: 35
                }
            ]
        },
        {
            name: 'Strong',
            description: 'Trending Market',
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'adx',
                    config1: { period: 14 },
                    subField1: '',
                    period1: 1,
                    operator: '>=',
                    compareType: 'value',
                    value: 35
                }
            ]
        },
        {
            name: 'Above',
            description: `${market} close is above its 200-day simple moving average`,
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'close',
                    config1: {},
                    subField1: '',
                    period1: 1,
                    operator: '>',
                    compareType: 'indicator',
                    value: 0,
                    compareSymbol: market,
                    field2: 'sma',
                    config2: { period: 200 },
                    subField2: '',
                    period2: 200
                }
            ]
        },
        {
            name: 'Below',
            description: `${market} close is below its 200-day simple moving average`,
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'close',
                    config1: {},
                    subField1: '',
                    period1: 1,
                    operator: '<',
                    compareType: 'indicator',
                    value: 0,
                    compareSymbol: market,
                    field2: 'sma',
                    config2: { period: 200 },
                    subField2: '',
                    period2: 200
                }
            ]
        },
        {
            name: 'Near ATH',
            description: `${market} close is within 3% of ATH`,
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'drawdown',
                    config1: {},
                    subField1: '',
                    period1: 1,
                    operator: '<=',
                    compareType: 'value',
                    value: 3,
                }
            ]
        },
        {
            name: 'In Correction',
            description: `${market} drawdown > 10%`,
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'drawdown',
                    config1: {},
                    subField1: '',
                    period1: 1,
                    operator: '>=',
                    compareType: 'value',
                    value: 10,
                },
                {
                    id: 1,
                    symbol: market,
                    field1: 'drawdown',
                    config1: {},
                    subField1: '',
                    period1: 1,
                    operator: '<',
                    compareType: 'value',
                    value: 20,
                }
            ]
        },
        {
            name: 'In Bear Market',
            description: `${market} drawdown > 20%`,
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'drawdown',
                    config1: {},
                    subField1: '',
                    period1: 1,
                    operator: '>=',
                    compareType: 'value',
                    value: 20,
                }
            ]
        },
        {
            name: 'Low',
            description: 'VIX < 18',
            conditions: [
                {
                    id: 1,
                    symbol: 'VIX',
                    field1: 'close',
                    config1: {},
                    subField1: '',
                    period1: 1,
                    operator: '<=',
                    compareType: 'value',
                    value: 18,
                }
            ]
        },
        {
            name: 'Medium',
            description: '18 <= VIX < 24',
            conditions: [
                {
                    id: 1,
                    symbol: 'VIX',
                    field1: 'close',
                    config1: {},
                    subField1: '',
                    period1: 1,
                    operator: '>=',
                    compareType: 'value',
                    value: 18,
                },
                {
                    id: 1,
                    symbol: 'VIX',
                    field1: 'close',
                    config1: {},
                    subField1: '',
                    period1: 1,
                    operator: '<',
                    compareType: 'value',
                    value: 24,
                }
            ]
        },
        {
            name: 'High',
            description: '24 >= VIX',
            conditions: [
                {
                    id: 1,
                    symbol: 'VIX',
                    field1: 'close',
                    config1: {},
                    subField1: '',
                    period1: 1,
                    operator: '>=',
                    compareType: 'value',
                    value: 24,
                }
            ]
        },
        {
            name: 'Compression',
            description: 'VIX near lower band',
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'atrratio',
                    config1: { period: 14, slowPeriod: 50 },
                    subField1: '',
                    period1: 1,
                    operator: '<=',
                    compareType: 'value',
                    value: 0,
                }
            ]
        },
        {
            name: 'Drift',
            description: 'VIX compressing',
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'atrratio',
                    config1: { period: 14, slowPeriod: 50 },
                    subField1: '',
                    period1: 1,
                    operator: '>',
                    compareType: 'value',
                    value: 0,
                },
                {
                    id: 2,
                    symbol: market,
                    field1: 'atrratio',
                    config1: { period: 14, slowPeriod: 50 },
                    subField1: '',
                    period1: 1,
                    operator: '<=',
                    compareType: 'value',
                    value: 20,
                },
            ]
        },
        {
            name: 'Spike',
            description: 'VIX near upper band',
            conditions: [
                {
                    id: 1,
                    symbol: market,
                    field1: 'atrratio',
                    config1: { period: 14, slowPeriod: 50 },
                    subField1: '',
                    period1: 1,
                    operator: '>',
                    compareType: 'value',
                    value: 20,
                }
            ]
        },
        {
            name: 'Growth',
            description: '',
            conditions: [
                {
                    id: 1,
                    symbol: 'QQQ',
                    field1: 'bollingerdistance',
                    config1: { period: ma },
                    subField1: '',
                    operator: '>',
                    compareType: 'value',
                    value: 0,
                },
            ]
        },
        {
            name: 'Value',
            description: '',
            conditions: [
                {
                    id: 1,
                    symbol: 'QQQ',
                    field1: 'bollingerdistance',
                    config1: { period: ma },
                    subField1: '',
                    operator: '<',
                    compareType: 'value',
                    value: 0,
                },
                {
                    id: 3,
                    symbol: 'XLP',
                    field1: 'bollingerdistance',
                    config1: { period: ma },
                    subField1: '',
                    operator: '>',
                    compareType: 'value',
                    value: 0,
                }
            ]
        },
        {
            name: 'None',
            description: '',
            conditions: [
                {
                    id: 1,
                    symbol: 'QQQ',
                    field1: 'bollingerdistance',
                    config1: { period: ma },
                    subField1: '',
                    operator: '<',
                    compareType: 'value',
                    value: 0,
                },
                {
                    id: 2,
                    symbol: 'XLP',
                    field1: 'bollingerdistance',
                    config1: { period: ma },
                    subField1: '',
                    operator: '<',
                    compareType: 'value',
                    value: 0,
                }
            ]

        }

    ];
}


function normalizeDate(dateVal) {
    if (!dateVal) return '';
    if (tableHelpers && typeof tableHelpers.dateYMDFormatter === 'function') {
        const res = tableHelpers.dateYMDFormatter(dateVal);
        if (res) return res;
    }
    if (typeof dateVal === 'string') {
        const trimmed = dateVal.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            return trimmed;
        }
        if (/^\d{4}-\d{2}-\d{2}T00:00:00(\.000)?Z?$/.test(trimmed)) {
            return trimmed.slice(0, 10);
        }
    }
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0) {
        return d.toISOString().slice(0, 10);
    }
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
    const parts = formatter.formatToParts(d);
    let year = '', month = '', day = '';
    for (const part of parts) {
        if (part.type === 'year') year = part.value;
        if (part.type === 'month') month = part.value;
        if (part.type === 'day') day = part.value;
    }
    return `${year}-${month}-${day}`;
}

function getMarketDay(baseDate, step = 0) {
    let d = baseDate ? new Date(baseDate) : new Date();
    // Noon to avoid any daylight savings or timezone midnight drift
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0);

    if (step === 0) {
        if (d.getDay() === 0) { // Sunday -> Friday
            d.setDate(d.getDate() - 2);
        } else if (d.getDay() === 6) { // Saturday -> Friday
            d.setDate(d.getDate() - 1);
        }
        return d;
    }

    let count = Math.abs(step);
    const dir = step > 0 ? 1 : -1;

    while (count > 0) {
        d.setDate(d.getDate() + dir);
        if (d.getDay() === 0) { // Sunday
            d.setDate(d.getDate() + (dir > 0 ? 1 : -2));
        } else if (d.getDay() === 6) { // Saturday
            d.setDate(d.getDate() + (dir > 0 ? 2 : -1));
        }
        count--;
    }

    return d;
}

function formatDecimalPercent(val, includeSign = true) {
    if (val === null || val === undefined || isNaN(val)) return '0.0%';
    const pct = parseFloat(val) * 100;
    const sign = includeSign && pct > 0 ? '+' : '';
    return `${sign}${pct.toFixed(1)}%`;
}

function applyRegimeFilters(rawMatches, consecutiveDaysToEnter = 1, minDuration = 1) {
    if (!rawMatches || rawMatches.length === 0) return [];

    const reqEnter = Math.max(1, parseInt(consecutiveDaysToEnter, 10) || 1);
    const reqMinDur = Math.max(1, parseInt(minDuration, 10) || 1);

    const result = [];
    let currentRegime = false;
    let daysInRegime = 0;
    let consecutiveMatches = 0;

    for (let i = 0; i < rawMatches.length; i++) {
        const match = Boolean(rawMatches[i]);

        if (!currentRegime) {
            // Currently out of regime
            if (match) {
                consecutiveMatches++;
                if (consecutiveMatches >= reqEnter) {
                    currentRegime = true;
                    daysInRegime = 1;
                    consecutiveMatches = 0;
                }
            } else {
                consecutiveMatches = 0;
            }
        } else {
            // Currently in regime
            consecutiveMatches = 0;

            if (daysInRegime < reqMinDur) {
                // Must remain in regime to satisfy minimum duration
                daysInRegime++;
            } else {
                // Minimum duration satisfied; exit on first day conditions fail
                if (!match) {
                    currentRegime = false;
                    daysInRegime = 0;
                } else {
                    daysInRegime++;
                }
            }
        }

        result.push(currentRegime);
    }

    return result;
}

function getIndicatorId(type, config) {
    if (!type) return '';
    if (config?.id) return config.id;
    if (!config) return type;
    const keys = Object.keys(config).filter(k => k !== 'id' && config[k] !== undefined && config[k] !== null && config[k] !== '');
    if (keys.length === 0) return type;
    if (keys.length === 1 && keys[0] === 'period') return `${type}${config.period}`;
    const sorted = keys.sort().map(k => `${k}${config[k]}`).join('_');
    return `${type}_${sorted}`;
}

function formatConditionOperand(symbol, field, config, subField, period) {
    if (PRICE_FIELDS.includes((field || '').toLowerCase())) {
        return `${symbol} ${field}`;
    }
    const resolvedConfig = config && Object.keys(config).length > 0 ? config : (period ? { period } : {});
    const cfgKeys = Object.keys(resolvedConfig).filter(k => k !== 'id' && resolvedConfig[k] !== undefined && resolvedConfig[k] !== '');
    let cfgStr = '';
    if (cfgKeys.length > 0) {
        cfgStr = `(${cfgKeys.map(k => `${k}: ${resolvedConfig[k]}`).join(', ')})`;
    }
    const subStr = subField ? `.${subField}` : '';
    return `${symbol} ${field}${cfgStr}${subStr}`;
}

function formatCondition(cond) {
    if (!cond) return '';
    const op1 = formatConditionOperand(cond.symbol, cond.field1, cond.config1, cond.subField1, cond.period1);
    const op2 = cond.compareType === 'value'
        ? cond.value
        : formatConditionOperand(cond.compareSymbol, cond.field2, cond.config2, cond.subField2, cond.period2);
    return `${op1} ${cond.operator} ${op2}`;
}

function getConditionConfig(cond, isField1, apiIndicators) {
    const field = (isField1 ? cond.field1 : cond.field2) || '';
    const rawConfig = isField1 ? cond.config1 : cond.config2;
    const period = isField1 ? cond.period1 : cond.period2;

    const indDef = apiIndicators?.find(i => i.type === field);
    const cfg = { ...(indDef?.config || {}) };

    if (rawConfig && typeof rawConfig === 'object') {
        Object.assign(cfg, rawConfig);
    }
    if (rawConfig?.period !== undefined && rawConfig?.period !== '') {
        cfg.period = Number(rawConfig.period);
    } else if (period !== undefined && period !== null && period !== '') {
        cfg.period = Number(period);
    }
    return cfg;
}

function extractFieldValue(dataPoint, field, configOrPeriod, subField) {
    if (!dataPoint) return null;
    const f = (field || '').toLowerCase();
    if (PRICE_FIELDS.includes(f)) {
        return typeof dataPoint[f] === 'number' ? dataPoint[f] : parseFloat(dataPoint[f]);
    }

    let config = configOrPeriod;
    if (typeof configOrPeriod === 'number' || typeof configOrPeriod === 'string') {
        config = { period: Number(configOrPeriod) };
    }

    const id = getIndicatorId(f, config);
    let val = undefined;
    if (dataPoint.upper) {
        if (dataPoint.upper[id] !== undefined) val = dataPoint.upper[id];
        else if (dataPoint.upper[f] !== undefined) val = dataPoint.upper[f];
    }
    if (val === undefined && dataPoint.lower) {
        if (dataPoint.lower[id] !== undefined) val = dataPoint.lower[id];
        else if (dataPoint.lower[f] !== undefined) val = dataPoint.lower[f];
    }

    // Robust fallback: if not matched by exact ID string, search upper and lower keys
    if (val === undefined) {
        const containers = [dataPoint.lower, dataPoint.upper];
        for (const container of containers) {
            if (!container) continue;
            const matchingKeys = Object.keys(container).filter(k => k.toLowerCase() === f || k.toLowerCase().startsWith(f));
            if (matchingKeys.length === 1) {
                val = container[matchingKeys[0]];
                break;
            } else if (matchingKeys.length > 1) {
                const targetPeriod = config?.period;
                const matchPeriodKey = targetPeriod !== undefined
                    ? matchingKeys.find(k => k.includes(String(targetPeriod)))
                    : null;
                if (matchPeriodKey) {
                    val = container[matchPeriodKey];
                    break;
                }
            }
        }
    }

    if (val === undefined || val === null) return null;
    if (typeof val === 'number') return val;
    if (typeof val === 'object') {
        if (subField && val[subField] !== undefined) {
            return typeof val[subField] === 'number' ? val[subField] : parseFloat(val[subField]);
        }
        if (val.value !== undefined) return typeof val.value === 'number' ? val.value : parseFloat(val.value);
        if (val.upperBand !== undefined) return typeof val.upperBand === 'number' ? val.upperBand : parseFloat(val.upperBand);
        if (val.lowerBand !== undefined) return typeof val.lowerBand === 'number' ? val.lowerBand : parseFloat(val.lowerBand);
        if (val.avg !== undefined) return typeof val.avg === 'number' ? val.avg : parseFloat(val.avg);
        if (val.middle !== undefined) return typeof val.middle === 'number' ? val.middle : parseFloat(val.middle);
        if (val.signal !== undefined) return typeof val.signal === 'number' ? val.signal : parseFloat(val.signal);
        if (val.d !== undefined) return typeof val.d === 'number' ? val.d : parseFloat(val.d);
        if (val.diplus !== undefined) return typeof val.diplus === 'number' ? val.diplus : parseFloat(val.diplus);
        if (val.diminus !== undefined) return typeof val.diminus === 'number' ? val.diminus : parseFloat(val.diminus);
        const vals = Object.values(val);
        return vals.length ? (typeof vals[0] === 'number' ? vals[0] : parseFloat(vals[0])) : null;
    }
    return parseFloat(val);
}

function evaluateConditionDetailed(cond, quote1, quote2, apiIndicators) {
    if (!quote1) {
        return {
            passed: false,
            val1: null,
            val2: null,
            val1Label: formatConditionOperand(cond?.symbol, cond?.field1, cond?.config1, cond?.subField1, cond?.period1),
            val2Label: cond?.compareType === 'value' ? `${cond?.value}` : formatConditionOperand(cond?.compareSymbol, cond?.field2, cond?.config2, cond?.subField2, cond?.period2),
            operator: cond?.operator || '>',
            reason: 'Missing quote data for ' + cond?.symbol
        };
    }
    const config1 = getConditionConfig(cond, true, apiIndicators);
    const val1 = extractFieldValue(quote1, cond.field1, config1, cond.subField1);
    const val1Label = formatConditionOperand(cond.symbol, cond.field1, config1, cond.subField1, cond.period1);

    let val2 = null;
    let val2Label = '';
    if (cond.compareType === 'value') {
        val2 = typeof cond.value === 'number' ? cond.value : parseFloat(cond.value);
        val2Label = `${val2}`;
    } else {
        if (!quote2) {
            return {
                passed: false,
                val1,
                val2: null,
                val1Label,
                val2Label: formatConditionOperand(cond.compareSymbol, cond.field2, cond.config2, cond.subField2, cond.period2),
                operator: cond.operator,
                reason: 'Missing comparison quote data for ' + cond.compareSymbol
            };
        }
        const config2 = getConditionConfig(cond, false, apiIndicators);
        val2 = extractFieldValue(quote2, cond.field2, config2, cond.subField2);
        val2Label = formatConditionOperand(cond.compareSymbol, cond.field2, config2, cond.subField2, cond.period2);
    }

    if (val1 === null || val1 === undefined || isNaN(val1) || val2 === null || val2 === undefined || isNaN(val2)) {
        return {
            passed: false,
            val1,
            val2,
            val1Label,
            val2Label,
            operator: cond.operator,
            reason: 'Indicator value unavailable'
        };
    }

    let passed = false;
    switch (cond.operator) {
        case '>': passed = val1 > val2; break;
        case '<': passed = val1 < val2; break;
        case '>=': passed = val1 >= val2; break;
        case '<=': passed = val1 <= val2; break;
        case '=': passed = Math.abs(val1 - val2) < 0.0001; break;
        case '<>': passed = Math.abs(val1 - val2) >= 0.0001; break;
        default: passed = false; break;
    }

    return {
        passed,
        val1,
        val2,
        val1Label,
        val2Label,
        operator: cond.operator
    };
}

function evaluateConditionOnQuotes(cond, quote1, quote2, apiIndicators) {
    return evaluateConditionDetailed(cond, quote1, quote2, apiIndicators).passed;
}

function formatValueDisplay(v) {
    if (v === null || v === undefined || isNaN(v)) return 'N/A';
    const num = Number(v);
    if (Math.abs(num) >= 100) return num.toFixed(2);
    if (Math.abs(num) >= 1) return num.toFixed(2);
    return num.toFixed(4);
}

function getEpisodeExitTrigger(exitIdx, dailyResults, conditions, quoteDataMap, apiIndicators, isOngoing) {
    if (isOngoing) {
        return {
            label: 'Active',
            badgeClass: 'badge bg-success-subtle text-success border border-success-subtle',
            tooltip: 'Regime is currently active (no exit conditions met)',
            failedRules: []
        };
    }

    if (!conditions || conditions.length === 0) {
        return {
            label: 'No Rules',
            badgeClass: 'badge bg-light text-secondary border',
            tooltip: 'No active regime conditions defined',
            failedRules: []
        };
    }

    if (!quoteDataMap || !dailyResults || exitIdx === null || exitIdx === undefined || exitIdx >= dailyResults.length) {
        return {
            label: 'Exited',
            badgeClass: 'badge bg-secondary-subtle text-secondary border',
            tooltip: 'Exited regime',
            failedRules: []
        };
    }

    const exitDateStr = normalizeDate(dailyResults[exitIdx]?.date);
    const prevDateStr = exitIdx > 0 ? normalizeDate(dailyResults[exitIdx - 1]?.date) : null;

    const evaluateOnDate = (dateStr) => {
        if (!dateStr) return [];
        const failed = [];
        conditions.forEach((cond, idx) => {
            const q1 = quoteDataMap[cond.symbol]?.[dateStr];
            const q2 = cond.compareType === 'indicator' ? quoteDataMap[cond.compareSymbol]?.[dateStr] : null;
            const evalResult = evaluateConditionDetailed(cond, q1, q2, apiIndicators);
            if (!evalResult.passed) {
                failed.push({
                    ruleNumber: idx + 1,
                    conditionText: formatCondition(cond),
                    val1: evalResult.val1,
                    val2: evalResult.val2,
                    val1Label: evalResult.val1Label,
                    val2Label: evalResult.val2Label,
                    operator: evalResult.operator,
                    reason: evalResult.reason
                });
            }
        });
        return failed;
    };

    let failedRules = evaluateOnDate(exitDateStr);
    let triggerDateStr = exitDateStr;

    if (failedRules.length === 0 && prevDateStr) {
        const prevFailed = evaluateOnDate(prevDateStr);
        if (prevFailed.length > 0) {
            failedRules = prevFailed;
            triggerDateStr = prevDateStr;
        }
    }

    if (failedRules.length === 0) {
        return {
            label: 'Exited',
            badgeClass: 'badge bg-secondary-subtle text-secondary border',
            tooltip: `Exited regime on ${tableHelpers.dateFormmatter(exitDateStr)}`,
            failedRules: []
        };
    }

    let label = '';
    if (failedRules.length === 1) {
        label = `Rule ${failedRules[0].ruleNumber} Failed`;
    } else {
        label = `${failedRules.length} Rules Failed`;
    }

    const dateFormatted = tableHelpers.dateFormmatter(triggerDateStr);
    const ruleLines = failedRules.map((f, i) => {
        const val1Disp = formatValueDisplay(f.val1);
        const val2Disp = formatValueDisplay(f.val2);
        const prefix = failedRules.length > 1 ? `${i + 1}) ` : '';
        return `${prefix}Rule ${f.ruleNumber}: ${f.conditionText}\n   Actual: ${f.val1Label} (${val1Disp}) not ${f.operator} ${f.val2Label} (${val2Disp})`;
    }).join('\n');

    const tooltip = `Exit Trigger on ${dateFormatted}:\n${ruleLines}`;

    return {
        label,
        badgeClass: 'badge bg-danger-subtle text-danger border border-danger-subtle',
        tooltip,
        failedRules
    };
}


function computeStats(days, isBenchmarkFixed = false) {
    if (!days || days.length === 0) {
        return {
            count: 0,
            cumulativeReturn: 0,
            cagr: 0,
            maxDrawdown: 0,
            avgDrawdown: 0,
            sharpe: 0,
            avgExposure: 0,
            peakExposure: 0,
            winRate: 0,
            avgDailyReturn: 0,
            capitalEfficiency: 0
        };
    }

    let cumReturnMultiplier = 1.0;
    let peakEquity = 1.0;
    let currentEquity = 1.0;
    let maxDrawdown = 0;
    let drawdownSum = 0;
    let positiveDays = 0;
    let returnSum = 0;
    let exposureSum = 0;
    let peakExposure = 0;
    const returnsArray = [];

    days.forEach(day => {
        const ret = day.ret; // daily percentage return
        returnsArray.push(ret);
        returnSum += ret;
        if (ret > 0) positiveDays++;

        cumReturnMultiplier *= (1 + ret);
        currentEquity *= (1 + ret);
        if (currentEquity > peakEquity) {
            peakEquity = currentEquity;
        }
        const dd = peakEquity > 0 ? (peakEquity - currentEquity) / peakEquity * 100 : 0;
        if (dd > maxDrawdown) {
            maxDrawdown = dd;
        }
        drawdownSum += dd;

        const exp = isBenchmarkFixed ? 100 : (day.exposure ?? 0);
        exposureSum += exp;
        if (exp > peakExposure) {
            peakExposure = exp;
        }
    });

    const count = days.length;
    const cumulativeReturn = (cumReturnMultiplier - 1) * 100;
    const avgDailyReturn = (returnSum / count) * 100;
    const avgDrawdown = count > 0 ? (drawdownSum / count) : 0;

    // Annualized CAGR for the period active
    const cagr = count > 0 && cumReturnMultiplier > 0
        ? (Math.pow(cumReturnMultiplier, 252 / count) - 1) * 100
        : 0;

    // Annualized Sharpe Ratio
    const meanReturn = returnSum / count;
    const variance = returnsArray.reduce((acc, r) => acc + Math.pow(r - meanReturn, 2), 0) / (count > 1 ? count - 1 : 1);
    const stdDev = Math.sqrt(variance);
    const sharpe = stdDev > 0 ? (meanReturn / stdDev) * Math.sqrt(252) : 0;

    const avgExposure = isBenchmarkFixed ? 100 : (exposureSum / count);
    // Annualized Capital Efficiency: (Annualized Return Rate) / (Average Exposure as decimal)
    // Represents return generated per unit of capital actively deployed
    const capitalEfficiency = avgExposure > 0
        ? (avgDailyReturn * 252) / (avgExposure / 100)
        : 0;

    return {
        count,
        cumulativeReturn,
        cagr,
        maxDrawdown,
        avgDrawdown,
        sharpe,
        avgExposure,
        peakExposure,
        winRate: (positiveDays / count) * 100,
        avgDailyReturn,
        capitalEfficiency
    };
}

function getEfficiencyComparison(inStats, outStats, spyStats) {
    if (!inStats || !outStats || inStats.count === 0 || outStats.count === 0) {
        return {
            ratioDisplay: 'N/A',
            badge: 'Insufficient Data',
            badgeColor: '#6c757d',
            subtitle: 'Both regimes required for comparison',
            inEff: inStats?.capitalEfficiency || 0,
            outEff: outStats?.capitalEfficiency || 0
        };
    }

    const inEff = inStats.capitalEfficiency;
    const outEff = outStats.capitalEfficiency;
    const spyEff = spyStats?.capitalEfficiency || 0;

    let ratioDisplay = '';
    let badge = '';
    let badgeColor = '#198754';
    let subtitle = '';

    const spySuffix = spyStats && spyStats.count > 0
        ? ` (SPY: ${tableHelpers.percentChangeFormatter(spyEff)})`
        : '';

    if (inEff > 0 && outEff > 0) {
        const ratio = inEff / outEff;
        if (ratio >= 1.05) {
            ratioDisplay = `${ratio.toFixed(2)}x`;
            badge = 'In-Regime Favored';
            badgeColor = '#198754'; // Green
            subtitle = `In: ${tableHelpers.percentChangeFormatter(inEff)} vs Out: ${tableHelpers.percentChangeFormatter(outEff)}${spySuffix}`;
        } else if (ratio <= 0.95) {
            const invRatio = outEff / inEff;
            ratioDisplay = `${ratio.toFixed(2)}x`;
            badge = `Out Favored (${invRatio.toFixed(1)}x)`;
            badgeColor = '#fd7e14'; // Orange
            subtitle = `In: ${tableHelpers.percentChangeFormatter(inEff)} vs Out: ${tableHelpers.percentChangeFormatter(outEff)}${spySuffix}`;
        } else {
            ratioDisplay = `${ratio.toFixed(2)}x`;
            badge = 'Equal Efficiency';
            badgeColor = '#0d6efd'; // Blue
            subtitle = `In: ${tableHelpers.percentChangeFormatter(inEff)} vs Out: ${tableHelpers.percentChangeFormatter(outEff)}${spySuffix}`;
        }
    } else if (inEff > 0 && outEff <= 0) {
        const spread = inEff - outEff;
        ratioDisplay = `${tableHelpers.percentChangeFormatter(spread)} Spread`;
        badge = 'In-Regime Dominant';
        badgeColor = '#198754'; // Green
        subtitle = `In: ${tableHelpers.percentChangeFormatter(inEff)} | Out: ${tableHelpers.percentChangeFormatter(outEff)}${spySuffix}`;
    } else if (inEff <= 0 && outEff > 0) {
        const spread = inEff - outEff;
        ratioDisplay = `${tableHelpers.percentChangeFormatter(spread)} Spread`;
        badge = 'Out-of-Regime Dominant';
        badgeColor = '#dc3545'; // Red
        subtitle = `In: ${tableHelpers.percentChangeFormatter(inEff)} | Out: ${tableHelpers.percentChangeFormatter(outEff)}${spySuffix}`;
    } else {
        // Both negative
        const spread = inEff - outEff;
        if (spread > 0) {
            ratioDisplay = `+${tableHelpers.percentFormatter(spread)} Spread`;
            badge = 'In-Regime Defensive';
            badgeColor = '#0d6efd'; // Blue
            subtitle = `In lost less (${tableHelpers.percentChangeFormatter(inEff)} vs ${tableHelpers.percentChangeFormatter(outEff)})${spySuffix}`;
        } else {
            ratioDisplay = `${tableHelpers.percentChangeFormatter(spread)} Spread`;
            badge = 'Out-of-Regime Defensive';
            badgeColor = '#fd7e14'; // Orange
            subtitle = `Out lost less (${tableHelpers.percentChangeFormatter(outEff)} vs ${tableHelpers.percentChangeFormatter(inEff)})${spySuffix}`;
        }
    }

    return {
        ratioDisplay,
        badge,
        badgeColor,
        subtitle,
        inEff,
        outEff
    };
}

function extractEpisodes(dailyResults, regimeSeries, targetState, zoomStart, zoomEnd, conditions, quoteDataMap, apiIndicators) {
    if (!dailyResults || dailyResults.length === 0) return [];
    const episodes = [];
    let inEpisode = false;
    let epStartIndex = null;
    let epStartTotal = null;
    let epStartSpy = null;

    const zoomStartStr = zoomStart ? normalizeDate(zoomStart) : null;
    const zoomEndStr = zoomEnd ? normalizeDate(zoomEnd) : null;

    for (let i = 0; i < dailyResults.length; i++) {
        const curr = dailyResults[i];
        const prev = i > 0 ? dailyResults[i - 1] : curr;
        const dStr = normalizeDate(curr.date);

        if (zoomStartStr && dStr < zoomStartStr) continue;
        if (zoomEndStr && dStr > zoomEndStr) {
            if (inEpisode) {
                const exitIdx = i - 1;
                if (exitIdx >= epStartIndex) {
                    const endTotal = dailyResults[exitIdx].total;
                    const endSpy = dailyResults[exitIdx].baseline;
                    const planReturn = epStartTotal > 0 ? (endTotal - epStartTotal) / epStartTotal : 0;
                    const spyReturn = epStartSpy > 0 ? (endSpy - epStartSpy) / epStartSpy : 0;
                    const midIdx = Math.floor((epStartIndex + exitIdx) / 2);
                    const finalDays = Math.max(1, exitIdx - epStartIndex + 1);
                    const exitTrigger = targetState
                        ? {
                            label: 'Timeframe End',
                            badgeClass: 'badge bg-light text-secondary border',
                            tooltip: `Episode reached end of selected timeframe (${tableHelpers.dateFormmatter(dailyResults[exitIdx].date)})`,
                            failedRules: []
                        }
                        : null;
                    episodes.push({
                        id: episodes.length + 1,
                        startIndex: epStartIndex,
                        endIndex: exitIdx,
                        startDate: dailyResults[epStartIndex].date,
                        endDate: dailyResults[exitIdx].date,
                        days: finalDays,
                        midDateStr: normalizeDate(dailyResults[midIdx].date),
                        startTotal: epStartTotal,
                        endTotal: endTotal,
                        startSpy: epStartSpy,
                        endSpy: endSpy,
                        planReturn,
                        spyReturn,
                        alpha: planReturn - spyReturn,
                        isOngoing: false,
                        exitTrigger
                    });
                }
                inEpisode = false;
            }
            break;
        }

        const isMatch = Boolean(regimeSeries[i]) === Boolean(targetState);

        if (isMatch) {
            if (!inEpisode) {
                inEpisode = true;
                epStartIndex = i;
                // Entry price on regime: close price on the day it entered
                epStartTotal = curr.total > 0 ? curr.total : (prev.total > 0 ? prev.total : 1);
                epStartSpy = curr.baseline > 0 ? curr.baseline : (prev.baseline > 0 ? prev.baseline : 1);
            }
        } else {
            if (inEpisode) {
                // Exit price on regime: close price on the day it exited (day i where isMatch becomes false)
                const exitIdx = i;
                const endTotal = dailyResults[exitIdx].total;
                const endSpy = dailyResults[exitIdx].baseline;
                const planReturn = epStartTotal > 0 ? (endTotal - epStartTotal) / epStartTotal : 0;
                const spyReturn = epStartSpy > 0 ? (endSpy - epStartSpy) / epStartSpy : 0;
                const midIdx = Math.floor((epStartIndex + exitIdx) / 2);
                const finalDays = Math.max(1, exitIdx - epStartIndex);
                const exitTrigger = targetState
                    ? getEpisodeExitTrigger(exitIdx, dailyResults, conditions, quoteDataMap, apiIndicators, false)
                    : null;
                episodes.push({
                    id: episodes.length + 1,
                    startIndex: epStartIndex,
                    endIndex: exitIdx,
                    startDate: dailyResults[epStartIndex].date,
                    endDate: dailyResults[exitIdx].date,
                    days: finalDays,
                    midDateStr: normalizeDate(dailyResults[midIdx].date),
                    startTotal: epStartTotal,
                    endTotal: endTotal,
                    startSpy: epStartSpy,
                    endSpy: endSpy,
                    planReturn,
                    spyReturn,
                    alpha: planReturn - spyReturn,
                    isOngoing: false,
                    exitTrigger
                });
                inEpisode = false;
                epStartIndex = null;
                epStartTotal = null;
                epStartSpy = null;
            }
        }
    }

    if (inEpisode && epStartIndex !== null) {
        let lastIdx = dailyResults.length - 1;
        if (zoomEndStr) {
            while (lastIdx > epStartIndex && normalizeDate(dailyResults[lastIdx].date) > zoomEndStr) {
                lastIdx--;
            }
        }
        if (lastIdx >= epStartIndex) {
            const endTotal = dailyResults[lastIdx].total;
            const endSpy = dailyResults[lastIdx].baseline;
            const planReturn = epStartTotal > 0 ? (endTotal - epStartTotal) / epStartTotal : 0;
            const spyReturn = epStartSpy > 0 ? (endSpy - epStartSpy) / epStartSpy : 0;
            const midIdx = Math.floor((epStartIndex + lastIdx) / 2);
            const isOngoing = lastIdx === dailyResults.length - 1;
            const finalDays = Math.max(1, lastIdx - epStartIndex + 1);
            const exitTrigger = targetState
                ? getEpisodeExitTrigger(lastIdx, dailyResults, conditions, quoteDataMap, apiIndicators, isOngoing)
                : null;
            episodes.push({
                id: episodes.length + 1,
                startIndex: epStartIndex,
                endIndex: lastIdx,
                startDate: dailyResults[epStartIndex].date,
                endDate: isOngoing ? null : dailyResults[lastIdx].date,
                days: finalDays,
                midDateStr: normalizeDate(dailyResults[midIdx].date),
                startTotal: epStartTotal,
                endTotal: endTotal,
                startSpy: epStartSpy,
                endSpy: endSpy,
                planReturn,
                spyReturn,
                alpha: planReturn - spyReturn,
                isOngoing,
                exitTrigger
            });
        }
    }

    return episodes;
}

function IndicatorConfigInputs({ indicatorType, config, onChange, indicatorDef, patterns }) {
    if (!indicatorType || !config) return null;

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
            {Object.keys(config).filter(key => key !== 'id').map(key => {
                const isNumber = typeof indicatorDef?.config?.[key] === 'number' || typeof config[key] === 'number';
                const isBoolean = typeof indicatorDef?.config?.[key] === 'boolean' || typeof config[key] === 'boolean';

                if (isBoolean) {
                    return (
                        <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '12px' }}>
                            <label style={{ margin: 0, cursor: 'pointer' }}>
                                <input type="checkbox" checked={Boolean(config[key])} onChange={e => onChange(key, e.target.checked)} />
                                {' '}{key}
                            </label>
                        </span>
                    );
                }

                if (indicatorType === 'pattern' && key === 'patternType' && patterns) {
                    return (
                        <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '12px' }}>
                            {key}: <select value={config[key]} onChange={e => onChange(key, e.target.value)} style={{ padding: '2px 4px', fontSize: '12px' }}>
                                {patterns.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </span>
                    );
                }

                return (
                    <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '12px' }}>
                        {key}: <input
                            type={isNumber ? "number" : "text"}
                            step={key === 'stdDev' ? '0.1' : 'any'}
                            style={{ width: isNumber ? (key === 'stdDev' ? '45px' : '55px') : '90px', padding: '2px 4px', fontSize: '12px' }}
                            value={config[key] ?? ''}
                            onChange={e => onChange(key, isNumber ? Number(e.target.value) : e.target.value)}
                        />
                    </span>
                );
            })}
        </span>
    );
}

function areConditionsEqual(c1, c2) {
    if (!c1 || !c2) return false;
    if (c1.symbol !== c2.symbol) return false;
    if ((c1.field1 || '').toLowerCase() !== (c2.field1 || '').toLowerCase()) return false;
    if (c1.operator !== c2.operator) return false;
    if (c1.compareType !== c2.compareType) return false;

    // Period / config comparison for field1
    const p1_a = c1.config1?.period ?? c1.period1;
    const p1_b = c2.config1?.period ?? c2.period1;
    if (p1_a !== undefined && p1_b !== undefined && Number(p1_a) !== Number(p1_b)) return false;

    if ((c1.subField1 || '') !== (c2.subField1 || '')) return false;

    if (c1.compareType === 'value') {
        return Number(c1.value) === Number(c2.value);
    } else {
        if (c1.compareSymbol !== c2.compareSymbol) return false;
        if ((c1.field2 || '').toLowerCase() !== (c2.field2 || '').toLowerCase()) return false;
        const p2_a = c1.config2?.period ?? c1.period2;
        const p2_b = c2.config2?.period ?? c2.period2;
        if (p2_a !== undefined && p2_b !== undefined && Number(p2_a) !== Number(p2_b)) return false;
        if ((c1.subField2 || '') !== (c2.subField2 || '')) return false;
        return true;
    }
}

function isPresetActive(preset, currentConditions) {
    if (!preset || !preset.conditions || preset.conditions.length === 0) return false;
    if (!currentConditions || currentConditions.length === 0) return false;
    return preset.conditions.every(pc => currentConditions.some(cc => areConditionsEqual(pc, cc)));
}

function PlanRegimes(props) {
    const [symbols, setSymbols] = useState(['SPY', 'QQQ', 'GLD', 'VIX', 'IWM', 'TLT', 'DIA', 'RSP']);
    const [apiIndicators, setApiIndicators] = useState(DEFAULT_INDICATORS);
    const [liveTradesOnly, setLiveTradesOnly] = useState(false);
    const [backtestData, setBacktestData] = useState(null);
    const [consecutiveDaysToEnter, setConsecutiveDaysToEnter] = useState(3);
    const [minDuration, setMinDuration] = useState(3);
    const [marketSymbol, setMarketSymbol] = useState(DEFAULT_MARKET_SYMBOL);
    const [bullBearMovingAverage, setBullBearMovingAverage] = useState(DEFAULT_BULL_BEAR_MOVING_AVERAGE);
    const regimePresets = useMemo(() => getRegimePresets(bullBearMovingAverage, marketSymbol), [bullBearMovingAverage, marketSymbol]);
    const [conditions, setConditions] = useState([]);
    const [activeTab, setActiveTab] = useState('episodes'); // 'segmented' | 'timeline' | 'episodes' | 'exposure'
    const [forwardViewMode, setForwardViewMode] = useState('all'); // 'all' | 'plan' | 'spy'
    const [episodeChartHorizon, setEpisodeChartHorizon] = useState('1m'); // '1m' | '1w' | '2w' | '2m' | '3m' | 'full'
    const [isLoading, setIsLoading] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [isCheckingCurrent, setIsCheckingCurrent] = useState(true);
    const [checkingTargetDate, setCheckingTargetDate] = useState('current');
    const [searchParams] = useSearchParams();
    const [selectedPresetDate, setSelectedPresetDate] = useState(null);
    const [selectedExitEpisode, setSelectedExitEpisode] = useState(null);
    const [showTimelineAnnotations, setShowTimelineAnnotations] = useState(true);
    const [drilldownStartDate, setDrilldownStartDate] = useState(null);
    const [drilldownEndDate, setDrilldownEndDate] = useState(null);
    const [isDrilldownOpen, setIsDrilldownOpen] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [currentStatusMsg, setCurrentStatusMsg] = useState(null);

    // Stock Research Drawer states
    const [isMarketChartOpen, setIsMarketChartOpen] = useState(false);
    const [marketChartAnnotations, setMarketChartAnnotations] = useState([]);
    const [marketChartStart, setMarketChartStart] = useState(null);
    const [marketChartEnd, setMarketChartEnd] = useState(null);
    const [marketChartTitle, setMarketChartTitle] = useState('');

    // Prepopulate analysis date from query param (?date=YYYY-MM-DD or ?analysisDate=YYYY-MM-DD)
    useEffect(() => {
        const dateParam = searchParams.get('date') || searchParams.get('analysisDate');
        if (dateParam) {
            let year, month, day;
            if (dateParam.includes('-')) {
                const parts = dateParam.slice(0, 10).split('-');
                if (parts.length === 3) {
                    year = parseInt(parts[0], 10);
                    month = parseInt(parts[1], 10) - 1;
                    day = parseInt(parts[2], 10);
                }
            } else if (dateParam.includes('/')) {
                const parts = dateParam.split('/');
                if (parts.length === 3) {
                    month = parseInt(parts[0], 10) - 1;
                    day = parseInt(parts[1], 10);
                    year = parseInt(parts[2], 10);
                }
            }
            if (year && !isNaN(year) && month !== undefined && !isNaN(month) && day && !isNaN(day)) {
                const parsed = new Date(year, month, day, 12, 0, 0);
                if (!isNaN(parsed.getTime())) {
                    setSelectedPresetDate(parsed);
                }
            }
        }
    }, [searchParams]);

    const handleOpenDrilldown = useCallback((startDateVal, endDateVal) => {
        setDrilldownStartDate(startDateVal ? getLocalDate(startDateVal) : new Date());
        setDrilldownEndDate(endDateVal ? getLocalDate(endDateVal) : new Date());
        setIsDrilldownOpen(true);
    }, []);

    const dateToAnalyze = selectedPresetDate ? normalizeDate(selectedPresetDate) : '';

    const isNextDisabled = useMemo(() => {
        if (!selectedPresetDate) return true;
        const nextDay = getMarketDay(selectedPresetDate, 1);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        return nextDay > today;
    }, [selectedPresetDate]);

    const handlePrevMarketDay = useCallback(() => {
        const prev = getMarketDay(selectedPresetDate || new Date(), -1);
        setSelectedPresetDate(prev);
    }, [selectedPresetDate]);

    const handleNextMarketDay = useCallback(() => {
        if (!selectedPresetDate) return;
        const next = getMarketDay(selectedPresetDate, 1);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (next <= today) {
            setSelectedPresetDate(next);
        }
    }, [selectedPresetDate]);

    // Preset evaluation selection states (for Current & Date analysis)
    const [evalPresetNames, setEvalPresetNames] = useState(null); // null = all presets evaluated by default
    const [isEvalFilterOpen, setIsEvalFilterOpen] = useState(false);
    const evalFilterRef = useRef(null);

    const allPresetNames = useMemo(() => regimePresets.map(p => p.name), [regimePresets]);

    const activeEvalPresetNames = useMemo(() => {
        if (!evalPresetNames) return allPresetNames;
        return evalPresetNames.filter(name => allPresetNames.includes(name));
    }, [evalPresetNames, allPresetNames]);

    const isAllEvalSelected = activeEvalPresetNames.length === allPresetNames.length;

    const activeEvalPresetNamesRef = useRef(activeEvalPresetNames);
    useEffect(() => {
        activeEvalPresetNamesRef.current = activeEvalPresetNames;
    }, [activeEvalPresetNames]);

    // Handle click outside to close popover
    useEffect(() => {
        if (!isEvalFilterOpen) return;
        const handleClickOutside = (e) => {
            if (evalFilterRef.current && !evalFilterRef.current.contains(e.target)) {
                setIsEvalFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isEvalFilterOpen]);

    const handleTogglePresetEval = useCallback((presetName) => {
        setEvalPresetNames(prev => {
            const currentList = prev !== null ? prev : allPresetNames;
            if (currentList.includes(presetName)) {
                return currentList.filter(name => name !== presetName);
            } else {
                return [...currentList, presetName];
            }
        });
    }, [allPresetNames]);

    const handleToggleGroupEval = useCallback((groupPresetNames) => {
        setEvalPresetNames(prev => {
            const currentList = prev !== null ? prev : allPresetNames;
            const allInGroupSelected = groupPresetNames.every(name => currentList.includes(name));
            if (allInGroupSelected) {
                return currentList.filter(name => !groupPresetNames.includes(name));
            } else {
                const toAdd = groupPresetNames.filter(name => !currentList.includes(name));
                return [...currentList, ...toAdd];
            }
        });
    }, [allPresetNames]);

    const handleSelectAllEval = useCallback(() => {
        setEvalPresetNames(allPresetNames);
    }, [allPresetNames]);

    const handleClearAllEval = useCallback(() => {
        setEvalPresetNames([]);
    }, []);

    const handleSelectCoreOnlyEval = useCallback(() => {
        const coreDef = PRESET_ROW_DEFINITIONS.find(r => r.id === 'core');
        const coreNames = coreDef ? coreDef.groups.flatMap(g => g.presetNames) : [];
        setEvalPresetNames(coreNames);
    }, []);

    // Recent Market Regime Trajectory states
    const [trajectoryLookbackDays, setTrajectoryLookbackDays] = useState(22);
    const [trajectoryLeftMetric, setTrajectoryLeftMetric] = useState('cagr');
    const [trajectoryRightMetric, setTrajectoryRightMetric] = useState('spyCagr');
    const [trajectoryChartStyle, setTrajectoryChartStyle] = useState('both_lines');
    const [trajectoryData, setTrajectoryData] = useState([]);
    const [isCalculatingTrajectory, setIsCalculatingTrajectory] = useState(false);
    const [trajectoryError, setTrajectoryError] = useState(null);
    const [isTrajectoryExpanded, setIsTrajectoryExpanded] = useState(false);
    const [isConditionsExpanded, setIsConditionsExpanded] = useState(false);

    const cachedTrajectoryQuotesRef = useRef(null);
    const cachedTrajectoryQuotesKeyRef = useRef(null);
    const trajectoryRegimeCache = useRef({});

    // Automatically hide the trajectory chart anytime the selected Regime Conditions change
    const initialConditionsSetRef = useRef(false);
    useEffect(() => {
        if (!initialConditionsSetRef.current) {
            if (conditions && conditions.length > 0) {
                initialConditionsSetRef.current = true;
            }
            return;
        }
        setIsTrajectoryExpanded(false);
    }, [conditions]);

    // Full evaluated time series across history
    const [fullAnalysisSeries, setFullAnalysisSeries] = useState(null);

    // Timeframe & Zoom State
    const [selectedTimeframe, setSelectedTimeframe] = useState('All');
    const [zoomStart, setZoomStart] = useState(null);
    const [zoomEnd, setZoomEnd] = useState(null);
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    // Form builder state for adding a new condition
    const [patterns, setPatterns] = useState([]);

    const [newSymbol, setNewSymbol] = useState('SPY');
    const [newField1, setNewField1] = useState('close');
    const [newConfig1, setNewConfig1] = useState({});
    const [newSubField1, setNewSubField1] = useState('');
    const [newPeriod1, setNewPeriod1] = useState(1);

    const [newOperator, setNewOperator] = useState('>');
    const [newCompareType, setNewCompareType] = useState('indicator');
    const [newValue, setNewValue] = useState(0);

    const [newCompareSymbol, setNewCompareSymbol] = useState('SPY');
    const [newField2, setNewField2] = useState('sma');
    const [newConfig2, setNewConfig2] = useState({ period: 200 });
    const [newSubField2, setNewSubField2] = useState('');
    const [newPeriod2, setNewPeriod2] = useState(200);

    // Load available symbols from /api/strategySymbols and indicators from /api/indicators
    useEffect(() => {
        (async function () {
            try {
                const symsRes = await fetch('/api/strategySymbols');
                const syms = await symsRes.json();
                if (syms && Array.isArray(syms) && syms.length > 0) {
                    const unique = Array.from(new Set(['SPY', 'QQQ', 'GLD', 'VIX', 'IWM', 'RSP', ...syms])).sort();
                    setSymbols(unique);
                }
            } catch (e) {
                console.error("Could not load strategy symbols", e);
            }

            try {
                const indRes = await fetch('/api/indicators');
                const indData = await indRes.json();
                const list = indData.indicators || (Array.isArray(indData) ? indData : []);
                if (list && list.length > 0) {
                    setApiIndicators(list);
                }
                if (indData.patterns && Array.isArray(indData.patterns)) {
                    setPatterns(indData.patterns);
                }
            } catch (e) {
                console.error("Could not load indicators", e);
            }
        })();
    }, []);


    // Field change handlers
    function handleField1Change(val) {
        setNewField1(val);
        if (PRICE_FIELDS.includes(val)) {
            setNewConfig1({});
            setNewSubField1('');
            setNewPeriod1(1);
        } else {
            const ind = apiIndicators.find(i => i.type === val);
            const cfg = { ...(ind?.config || {}) };
            if (val === 'sma' || val === 'ema' || val === 'wma') {
                cfg.period = 200;
            }
            setNewConfig1(cfg);
            const sfs = INDICATOR_SUBFIELDS[val];
            setNewSubField1(sfs ? sfs[0].key : '');
            setNewPeriod1(cfg.period || 1);
        }
    }

    function handleConfig1Change(key, value) {
        setNewConfig1(prev => ({
            ...prev,
            [key]: value
        }));
        if (key === 'period') {
            setNewPeriod1(value);
        }
    }

    function handleField2Change(val) {
        setNewField2(val);
        if (PRICE_FIELDS.includes(val)) {
            setNewConfig2({});
            setNewSubField2('');
            setNewPeriod2(1);
        } else {
            const ind = apiIndicators.find(i => i.type === val);
            const cfg = { ...(ind?.config || {}) };
            if (val === 'sma' || val === 'ema' || val === 'wma') {
                cfg.period = 200;
            }
            setNewConfig2(cfg);
            const sfs = INDICATOR_SUBFIELDS[val];
            setNewSubField2(sfs ? sfs[0].key : '');
            setNewPeriod2(cfg.period || 1);
        }
    }

    function handleConfig2Change(key, value) {
        setNewConfig2(prev => ({
            ...prev,
            [key]: value
        }));
        if (key === 'period') {
            setNewPeriod2(value);
        }
    }

    // Load latest backtest results for this plan
    const loadBacktestData = useCallback(async () => {
        if (!props.planId) return;
        setIsLoading(true);
        setErrorMsg(null);
        try {
            const url = `/api/lastBacktestResults?planId=${props.planId}&liveTradesOnly=${liveTradesOnly ? '1' : '0'}`;
            const res = await fetch(url);
            const data = await res.json();
            if (data && data.dailyResults && data.dailyResults.length > 0) {
                setBacktestData(data);
            } else {
                setErrorMsg("No backtest results found for this plan. Please run a backtest first.");
            }
        } catch (e) {
            console.error("Failed to load backtest data", e);
            setErrorMsg("Error loading backtest data: " + e.message);
        } finally {
            setIsLoading(false);
        }
    }, [props.planId, liveTradesOnly]);

    useEffect(() => {
        loadBacktestData();
    }, [loadBacktestData]);

    // Available years extracted from backtest data
    const availableYears = useMemo(() => {
        if (!backtestData || !backtestData.dailyResults) return [];
        const yrSet = new Set();
        backtestData.dailyResults.forEach(d => {
            const dStr = normalizeDate(d.date);
            if (dStr && dStr.length >= 4) {
                const yr = parseInt(dStr.slice(0, 4), 10);
                if (!isNaN(yr)) yrSet.add(yr);
            }
        });
        return Array.from(yrSet).sort((a, b) => a - b); // chronological: 2018, 2019, ..., 2026
    }, [backtestData]);

    // Timeframe / Zoom selector handler
    function handleTimeframeSelect(tf) {
        setSelectedTimeframe(tf);
        setCustomStartDate('');
        setCustomEndDate('');
        if (tf === 'All') {
            setZoomStart(null);
            setZoomEnd(null);
        } else if (tf === 'YTD') {
            const now = new Date();
            setZoomStart(new Date(now.getFullYear(), 0, 1));
            setZoomEnd(now);
        } else if (tf === '1Y') {
            const now = new Date();
            const start = new Date();
            start.setFullYear(now.getFullYear() - 1);
            setZoomStart(start);
            setZoomEnd(now);
        } else if (tf === '3Y') {
            const now = new Date();
            const start = new Date();
            start.setFullYear(now.getFullYear() - 3);
            setZoomStart(start);
            setZoomEnd(now);
        } else if (tf === '5Y') {
            const now = new Date();
            const start = new Date();
            start.setFullYear(now.getFullYear() - 5);
            setZoomStart(start);
            setZoomEnd(now);
        } else {
            // Specific year like '2024'
            const yr = parseInt(tf, 10);
            if (!isNaN(yr)) {
                setZoomStart(new Date(yr, 0, 1));
                setZoomEnd(new Date(yr, 11, 31, 23, 59, 59));
            }
        }
    }

    // Apply custom start and end date range
    function handleApplyCustomDates() {
        if (!customStartDate) return;
        const start = new Date(customStartDate + 'T00:00:00');
        const end = customEndDate ? new Date(customEndDate + 'T23:59:59') : new Date();
        setSelectedTimeframe('Custom');
        setZoomStart(start);
        setZoomEnd(end);
    }

    // Zoom in on a specific regime episode
    const handleZoomToEpisode = useCallback((ep) => {
        if (!ep) return;
        const sDate = new Date(ep.startDate);
        const eDate = ep.endDate ? new Date(ep.endDate) : (ep.isOngoing ? new Date() : new Date(ep.startDate));
        // Add a 5-day buffer before and after so context is clearly visible
        const bufStart = new Date(sDate);
        bufStart.setDate(bufStart.getDate() - 5);
        const bufEnd = new Date(eDate);
        bufEnd.setDate(bufEnd.getDate() + 5);

        setZoomStart(bufStart);
        setZoomEnd(bufEnd);
        setSelectedTimeframe('Custom');
        setCustomStartDate(normalizeDate(bufStart));
        setCustomEndDate(normalizeDate(bufEnd));
        setActiveTab('timeline');
    }, []);

    // Handle adding condition
    function handleAddCondition() {
        const isPrice1 = PRICE_FIELDS.includes((newField1 || '').toLowerCase());
        const isPrice2 = PRICE_FIELDS.includes((newField2 || '').toLowerCase());

        const ind1 = apiIndicators.find(i => i.type === newField1);
        const ind2 = apiIndicators.find(i => i.type === newField2);

        const resolvedConfig1 = isPrice1 ? {} : { ...(ind1?.config || {}), ...newConfig1 };
        const resolvedConfig2 = isPrice2 ? {} : { ...(ind2?.config || {}), ...newConfig2 };

        const newCond = {
            id: Date.now(),
            symbol: newSymbol,
            field1: newField1,
            config1: resolvedConfig1,
            subField1: isPrice1 ? '' : newSubField1,
            period1: resolvedConfig1.period || Number(newPeriod1) || 1,
            operator: newOperator,
            compareType: newCompareType,
            value: Number(newValue) || 0,
            compareSymbol: newCompareSymbol,
            field2: newField2,
            config2: resolvedConfig2,
            subField2: isPrice2 ? '' : newSubField2,
            period2: resolvedConfig2.period || Number(newPeriod2) || 1
        };
        setConditions(prev => [...prev, newCond]);
    }

    function handleRemoveCondition(id) {
        setConditions(prev => prev.filter(c => c.id !== id));
    }

    function handleApplyPreset(preset) {
        setConditions(prev => {
            const existingConditions = prev || [];
            const active = isPresetActive(preset, existingConditions);
            if (active) {
                // Preset is currently active: toggle off by removing matching conditions
                return existingConditions.filter(existing => (
                    !preset.conditions.some(c => areConditionsEqual(c, existing))
                ));
            } else {
                // Preset is not active: add missing conditions
                const toAdd = [];
                (preset.conditions || []).forEach(c => {
                    const alreadyExists = existingConditions.some(existing => areConditionsEqual(c, existing));
                    if (!alreadyExists) {
                        toAdd.push({
                            ...c,
                            id: Date.now() + Math.floor(Math.random() * 100000) + toAdd.length
                        });
                    }
                });
                return [...existingConditions, ...toAdd];
            }
        });
        setCurrentStatusMsg(null);
    }

    const handleResetPresets = useCallback(() => {
        setConditions([]);
        setSelectedPresetDate(null);
        setErrorMsg(null);
        setCurrentStatusMsg(null);
    }, []);

    const handleApplyDateConditions = async (targetDateStr = null, replaceConditions = true, isInitialLoad = false, presetsOverride = null) => {
        const targetPresetNames = presetsOverride || activeEvalPresetNamesRef.current;
        const presetsToEvaluate = regimePresets.filter(p => targetPresetNames.includes(p.name));

        if (presetsToEvaluate.length === 0) {
            setErrorMsg("No presets selected to evaluate. Please select at least one preset using the 'Presets to Evaluate' dropdown.");
            return;
        }

        setIsCheckingCurrent(true);
        setCheckingTargetDate(targetDateStr || 'current');
        setErrorMsg(null);
        setCurrentStatusMsg(null);

        if (replaceConditions) {
            setConditions([]);
            setFullAnalysisSeries(null);
        }

        try {
            // Step 1: Collect symbols and indicator configurations needed across evaluated presets
            const symbolIndicatorMap = {};
            if (!symbolIndicatorMap[marketSymbol]) symbolIndicatorMap[marketSymbol] = [];
            presetsToEvaluate.forEach(preset => {
                (preset.conditions || []).forEach(c => {
                    if (!symbolIndicatorMap[c.symbol]) symbolIndicatorMap[c.symbol] = [];
                    if (!PRICE_FIELDS.includes((c.field1 || '').toLowerCase())) {
                        const ind1 = apiIndicators.find(i => i.type === c.field1);
                        const config1 = getConditionConfig(c, true, apiIndicators);
                        const id1 = getIndicatorId(c.field1, config1);
                        if (!symbolIndicatorMap[c.symbol].some(i => i.id === id1)) {
                            symbolIndicatorMap[c.symbol].push({
                                type: c.field1,
                                location: ind1?.location || 'lower',
                                id: id1,
                                config: config1
                            });
                        }
                    }

                    if (c.compareType === 'indicator') {
                        if (!symbolIndicatorMap[c.compareSymbol]) symbolIndicatorMap[c.compareSymbol] = [];
                        if (!PRICE_FIELDS.includes((c.field2 || '').toLowerCase())) {
                            const ind2 = apiIndicators.find(i => i.type === c.field2);
                            const config2 = getConditionConfig(c, false, apiIndicators);
                            const id2 = getIndicatorId(c.field2, config2);
                            if (!symbolIndicatorMap[c.compareSymbol].some(i => i.id === id2)) {
                                symbolIndicatorMap[c.compareSymbol].push({
                                    type: c.field2,
                                    location: ind2?.location || 'lower',
                                    id: id2,
                                    config: config2
                                });
                            }
                        }
                    }
                });
            });

            // Step 2: Fetch quote history and indicators for each symbol up to target date (lookback adjusted for MA length)
            const endStr = targetDateStr ? targetDateStr.slice(0, 10) : normalizeDate(new Date());
            const lookbackDays = Math.max(700, Math.ceil(bullBearMovingAverage * 3.5));
            const [endY, endM, endD] = endStr.split('-').map(Number);
            const startDateObj = new Date(Date.UTC(endY, endM - 1, endD - lookbackDays));
            const startStr = startDateObj.toISOString().slice(0, 10);

            const quoteByDateMap = {};
            const quoteMap = {};
            await Promise.all(Object.keys(symbolIndicatorMap).map(async (sym) => {
                const indicatorsPayload = symbolIndicatorMap[sym] || [];
                const url = `/api/quoteHistoryWithIndicators?symbol=${encodeURIComponent(sym)}&startDate=${startStr}&endDate=${endStr}`;
                const res = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ indicators: indicatorsPayload })
                });
                const result = await res.json();
                if (result && result.data && Array.isArray(result.data) && result.data.length > 0) {
                    const byDate = {};
                    result.data.forEach(item => {
                        const dStr = normalizeDate(item.date);
                        if (dStr) byDate[dStr] = item;
                    });
                    quoteByDateMap[sym] = byDate;

                    if (targetDateStr) {
                        const exactMatch = result.data.find(item => normalizeDate(item.date) === targetDateStr);
                        if (exactMatch) {
                            quoteMap[sym] = exactMatch;
                        } else {
                            const filtered = result.data.filter(item => normalizeDate(item.date) <= targetDateStr);
                            quoteMap[sym] = filtered.length > 0 ? filtered[filtered.length - 1] : result.data[result.data.length - 1];
                        }
                    } else {
                        quoteMap[sym] = result.data[result.data.length - 1];
                    }
                }
            }));

            // Step 3: Determine sorted market dates up to target date
            const primaryQuotes = quoteByDateMap[marketSymbol] || quoteByDateMap[Object.keys(quoteByDateMap)[0]] || {};
            const effectiveTargetDate = targetDateStr ? targetDateStr.slice(0, 10) : normalizeDate(new Date());
            const allMarketDates = Object.keys(primaryQuotes)
                .filter(d => !targetDateStr || d <= effectiveTargetDate)
                .sort();

            // Step 4: Iterate through each evaluated quick preset with consecutiveDaysToEnter & minDuration
            const matchingRules = [];
            const matchedNames = [];

            presetsToEvaluate.forEach(preset => {
                const conds = preset.conditions || [];
                if (conds.length === 0) return;

                let isMatch = false;
                if (allMarketDates.length > 0) {
                    const rawMatches = allMarketDates.map(dStr => {
                        return conds.every(cond => {
                            const q1 = quoteByDateMap[cond.symbol]?.[dStr];
                            const q2 = cond.compareType === 'indicator' ? quoteByDateMap[cond.compareSymbol]?.[dStr] : null;
                            return evaluateConditionOnQuotes(cond, q1, q2, apiIndicators);
                        });
                    });

                    const smoothedMatches = applyRegimeFilters(rawMatches, consecutiveDaysToEnter, minDuration);
                    isMatch = smoothedMatches.length > 0 && smoothedMatches[smoothedMatches.length - 1] === true;
                } else {
                    isMatch = conds.every(cond => {
                        const q1 = quoteMap[cond.symbol];
                        const q2 = cond.compareType === 'indicator' ? quoteMap[cond.compareSymbol] : null;
                        return evaluateConditionOnQuotes(cond, q1, q2, apiIndicators);
                    });
                }

                if (isMatch) {
                    matchedNames.push(preset.name);
                    conds.forEach(c => {
                        const exists = matchingRules.some(existing => areConditionsEqual(c, existing));
                        if (!exists) {
                            matchingRules.push({
                                ...c,
                                id: Date.now() + matchingRules.length + Math.floor(Math.random() * 1000)
                            });
                        }
                    });
                }
            });

            const lastDate = allMarketDates.length > 0 ? allMarketDates[allMarketDates.length - 1] : null;
            const dateLabel = targetDateStr ? targetDateStr : (lastDate || 'current market data');
            const filterInfo = (consecutiveDaysToEnter > 1 || minDuration > 1)
                ? ` [enforcing ≥${consecutiveDaysToEnter} enter day${consecutiveDaysToEnter > 1 ? 's' : ''}, ≥${minDuration} min duration]`
                : '';
            const evalCountInfo = presetsToEvaluate.length < regimePresets.length
                ? ` (${presetsToEvaluate.length} presets evaluated)`
                : '';

            if (matchingRules.length > 0) {
                setConditions(matchingRules);
                setFullAnalysisSeries(null);
                setCurrentStatusMsg(`Added ${matchingRules.length} matching condition${matchingRules.length > 1 ? 's' : ''} for ${dateLabel}${filterInfo}${evalCountInfo}: ${matchedNames.join(', ')}`);
            } else {
                if (isInitialLoad) {
                    setConditions(presetsToEvaluate[0]?.conditions || regimePresets[0].conditions);
                    setFullAnalysisSeries(null);
                } else if (replaceConditions) {
                    setConditions([]);
                    setFullAnalysisSeries(null);
                }
                setErrorMsg(`None of the quick preset conditions matched on ${dateLabel}${filterInfo}${evalCountInfo}.`);
            }
        } catch (e) {
            console.error("Failed to evaluate market conditions for date", e);
            setErrorMsg("Error evaluating market conditions: " + (e.message || e.toString()));
            if (isInitialLoad) {
                setConditions(presetsToEvaluate[0]?.conditions || regimePresets[0].conditions);
            }
        } finally {
            setIsCheckingCurrent(false);
            setCheckingTargetDate(null);
        }
    };

    // Automatically run current condition analysis on page load
    const hasRunInitialCheckRef = useRef(false);
    useEffect(() => {
        if (!hasRunInitialCheckRef.current) {
            hasRunInitialCheckRef.current = true;
            handleApplyDateConditions(null, true, true);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAnalyzeDate = (dateVal) => {
        if (!dateVal) return;
        const d = getLocalDate(dateVal);
        setSelectedPresetDate(d);
        const normStr = normalizeDate(d);
        handleApplyDateConditions(normStr, true, false);
        const elem = document.getElementById('analysis-row') || document.getElementById('market-symbol-select');
        if (elem) {
            elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const handleOpenMarketChart = useCallback((targetDateOrStart = null, maybeEndDate = null) => {
        const annotations = [];
        let start = null;
        let end = null;
        let titleDesc = '';

        if (targetDateOrStart) {
            const dStart = getLocalDate(targetDateOrStart);
            annotations.push({ date: dStart });

            if (maybeEndDate) {
                const dEnd = getLocalDate(maybeEndDate);
                annotations.push({ date: dEnd });
                titleDesc = `Regime Episode: ${tableHelpers.dateFormmatter(dStart)} - ${tableHelpers.dateFormmatter(dEnd)}`;

                const winStart = new Date(dStart);
                winStart.setFullYear(winStart.getFullYear() - 1);
                start = normalizeDate(winStart);

                const winEnd = new Date(dEnd);
                winEnd.setMonth(winEnd.getMonth() + 2);
                const today = new Date();
                end = normalizeDate(winEnd > today ? today : winEnd);
            } else {
                titleDesc = `Analyzed Date: ${tableHelpers.dateFormmatter(dStart)}`;

                const winStart = new Date(dStart);
                winStart.setMonth(winStart.getMonth() - 18);
                start = normalizeDate(winStart);

                const winEnd = new Date(dStart);
                winEnd.setMonth(winEnd.getMonth() + 2);
                const today = new Date();
                end = normalizeDate(winEnd > today ? today : winEnd);
            }
        } else {
            titleDesc = `Current Market Context`;
            const winStart = new Date();
            winStart.setFullYear(winStart.getFullYear() - 1);
            start = normalizeDate(winStart);
            end = normalizeDate(new Date());
        }

        setMarketChartAnnotations(annotations);
        setMarketChartStart(start);
        setMarketChartEnd(end);
        setMarketChartTitle(titleDesc);
        setIsMarketChartOpen(true);
    }, []);

    // Core Analysis Engine: runs once per condition/plan change and generates the complete series
    const runRegimeAnalysis = useCallback(async () => {
        if (!backtestData || !backtestData.dailyResults || backtestData.dailyResults.length < 2) {
            setErrorMsg("Backtest data is not ready or has insufficient days.");
            return;
        }
        if (!conditions || conditions.length === 0) {
            setErrorMsg("Please define at least one regime condition.");
            return;
        }

        setIsAnalyzing(true);
        setErrorMsg(null);

        try {
            const dailyResults = backtestData.dailyResults;
            const startDate = new Date(dailyResults[0].date);
            const endDate = new Date(dailyResults[dailyResults.length - 1].date);

            // Step 1: Collect symbols and indicator configurations needed
            const symbolIndicatorMap = {};
            conditions.forEach(c => {
                if (!symbolIndicatorMap[c.symbol]) symbolIndicatorMap[c.symbol] = [];
                if (!PRICE_FIELDS.includes((c.field1 || '').toLowerCase())) {
                    const ind1 = apiIndicators.find(i => i.type === c.field1);
                    const config1 = getConditionConfig(c, true, apiIndicators);
                    const id1 = getIndicatorId(c.field1, config1);
                    if (!symbolIndicatorMap[c.symbol].some(i => i.id === id1)) {
                        symbolIndicatorMap[c.symbol].push({
                            type: c.field1,
                            location: ind1?.location || 'lower',
                            id: id1,
                            config: config1
                        });
                    }
                }

                if (c.compareType === 'indicator') {
                    if (!symbolIndicatorMap[c.compareSymbol]) symbolIndicatorMap[c.compareSymbol] = [];
                    if (!PRICE_FIELDS.includes((c.field2 || '').toLowerCase())) {
                        const ind2 = apiIndicators.find(i => i.type === c.field2);
                        const config2 = getConditionConfig(c, false, apiIndicators);
                        const id2 = getIndicatorId(c.field2, config2);
                        if (!symbolIndicatorMap[c.compareSymbol].some(i => i.id === id2)) {
                            symbolIndicatorMap[c.compareSymbol].push({
                                type: c.field2,
                                location: ind2?.location || 'lower',
                                id: id2,
                                config: config2
                            });
                        }
                    }
                }
            });

            // Step 2: Fetch quote and indicator data for each symbol
            const quoteDataMap = {};
            const symbolsToFetch = Object.keys(symbolIndicatorMap);

            await Promise.all(symbolsToFetch.map(async (sym) => {
                const indicatorsPayload = symbolIndicatorMap[sym] || [];
                const startStr = normalizeDate(startDate);
                const endStr = normalizeDate(endDate);
                const url = `/api/quoteHistoryWithIndicators?symbol=${encodeURIComponent(sym)}&startDate=${startStr}&endDate=${endStr}`;
                const res = await fetch(url, {
                    method: 'POST',
                    body: JSON.stringify({ indicators: indicatorsPayload })
                });
                const result = await res.json();
                const byDate = {};
                if (result && result.data && Array.isArray(result.data)) {
                    result.data.forEach(item => {
                        const dStr = normalizeDate(item.date);
                        if (dStr) byDate[dStr] = item;
                    });
                }
                quoteDataMap[sym] = byDate;
            }));

            // Step 3: Evaluate raw conditions for each backtest date
            const rawMatches = [];
            const dateList = [];

            dailyResults.forEach((day) => {
                const dStr = normalizeDate(day.date);
                dateList.push(dStr);

                let allMatch = true;
                for (const cond of conditions) {
                    const quote1 = quoteDataMap[cond.symbol]?.[dStr];
                    const quote2 = cond.compareType === 'indicator' ? quoteDataMap[cond.compareSymbol]?.[dStr] : null;
                    if (!evaluateConditionOnQuotes(cond, quote1, quote2, apiIndicators)) {
                        allMatch = false;
                        break;
                    }
                }
                rawMatches.push(allMatch);
            });

            // Step 4: Apply Regime Filters (Entry consecutive days + Minimum lock-in duration)
            const smoothedMatches = applyRegimeFilters(rawMatches, consecutiveDaysToEnter, minDuration);

            // Step 5: Transition attribution (Day T counted in former regime, T+1 counted in new regime)
            const effectiveRegime = [];
            for (let i = 0; i < smoothedMatches.length; i++) {
                if (i === 0) {
                    effectiveRegime.push(smoothedMatches[0]);
                } else {
                    effectiveRegime.push(smoothedMatches[i - 1]);
                }
            }

            // Save the complete evaluated series
            setFullAnalysisSeries({
                dailyResults,
                rawMatches,
                effectiveRegime,
                dateList,
                quoteDataMap
            });

        } catch (e) {
            console.error("Error running regime analysis", e);
            setErrorMsg("Analysis error: " + (e.message || e.toString()));
        } finally {
            setIsAnalyzing(false);
        }
    }, [backtestData, conditions, consecutiveDaysToEnter, minDuration, apiIndicators]);

    // Automatically run initial analysis once backtest data is loaded and current condition check completes
    useEffect(() => {
        if (backtestData && !fullAnalysisSeries && !isAnalyzing && !isCheckingCurrent) {
            runRegimeAnalysis();
        }
    }, [backtestData, fullAnalysisSeries, isAnalyzing, isCheckingCurrent, runRegimeAnalysis]);

    // Recent Market Days Regime Trajectory Analysis
    const runRecentDaysTrajectory = useCallback(async (forceRefresh = false) => {
        if (!backtestData || !backtestData.dailyResults || backtestData.dailyResults.length < 2) return;

        setIsCalculatingTrajectory(true);
        setTrajectoryError(null);

        try {
            const dailyResults = backtestData.dailyResults;
            const targetDateStr = selectedPresetDate
                ? normalizeDate(selectedPresetDate)
                : normalizeDate(dailyResults[dailyResults.length - 1].date);

            // Step 1: Collect symbols and indicators needed across evaluated presets
            const targetPresetNames = activeEvalPresetNamesRef.current;
            const presetsToEvaluate = regimePresets.filter(p => targetPresetNames.includes(p.name));
            const symbolIndicatorMap = {};
            if (!symbolIndicatorMap[marketSymbol]) symbolIndicatorMap[marketSymbol] = [];
            presetsToEvaluate.forEach(preset => {
                (preset.conditions || []).forEach(c => {
                    if (!symbolIndicatorMap[c.symbol]) symbolIndicatorMap[c.symbol] = [];
                    if (!PRICE_FIELDS.includes((c.field1 || '').toLowerCase())) {
                        const ind1 = apiIndicators.find(i => i.type === c.field1);
                        const config1 = getConditionConfig(c, true, apiIndicators);
                        const id1 = getIndicatorId(c.field1, config1);
                        if (!symbolIndicatorMap[c.symbol].some(i => i.id === id1)) {
                            symbolIndicatorMap[c.symbol].push({
                                type: c.field1,
                                location: ind1?.location || 'lower',
                                id: id1,
                                config: config1
                            });
                        }
                    }

                    if (c.compareType === 'indicator') {
                        if (!symbolIndicatorMap[c.compareSymbol]) symbolIndicatorMap[c.compareSymbol] = [];
                        if (!PRICE_FIELDS.includes((c.field2 || '').toLowerCase())) {
                            const ind2 = apiIndicators.find(i => i.type === c.field2);
                            const config2 = getConditionConfig(c, false, apiIndicators);
                            const id2 = getIndicatorId(c.field2, config2);
                            if (!symbolIndicatorMap[c.compareSymbol].some(i => i.id === id2)) {
                                symbolIndicatorMap[c.compareSymbol].push({
                                    type: c.field2,
                                    location: ind2?.location || 'lower',
                                    id: id2,
                                    config: config2
                                });
                            }
                        }
                    }
                });
            });

            // Step 2: Fetch quote history with indicators from backtest start (with warmup) to target date
            const backtestStartDate = normalizeDate(dailyResults[0].date);
            const lookbackCalDays = Math.max(700, Math.ceil(bullBearMovingAverage * 3.5));
            const [by, bm, bd] = backtestStartDate.split('-').map(Number);
            const warmupStartDateObj = new Date(Date.UTC(by, bm - 1, bd - lookbackCalDays));
            const quoteStartStr = warmupStartDateObj.toISOString().slice(0, 10);
            const quoteEndStr = targetDateStr > normalizeDate(new Date()) ? targetDateStr : normalizeDate(new Date());

            let quoteDataMap = cachedTrajectoryQuotesRef.current;
            const cacheKey = `${marketSymbol}_${bullBearMovingAverage}_${quoteStartStr}_${quoteEndStr}`;
            if (forceRefresh || !quoteDataMap || cachedTrajectoryQuotesKeyRef.current !== cacheKey) {
                quoteDataMap = {};
                await Promise.all(Object.keys(symbolIndicatorMap).map(async (sym) => {
                    const indicatorsPayload = symbolIndicatorMap[sym] || [];
                    const url = `/api/quoteHistoryWithIndicators?symbol=${encodeURIComponent(sym)}&startDate=${quoteStartStr}&endDate=${quoteEndStr}`;
                    const res = await fetch(url, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ indicators: indicatorsPayload })
                    });
                    const result = await res.json();
                    const byDate = {};
                    if (result && result.data && Array.isArray(result.data)) {
                        result.data.forEach(item => {
                            const dStr = normalizeDate(item.date);
                            if (dStr) byDate[dStr] = item;
                        });
                    }
                    quoteDataMap[sym] = byDate;
                }));
                cachedTrajectoryQuotesRef.current = quoteDataMap;
                cachedTrajectoryQuotesKeyRef.current = cacheKey;
                trajectoryRegimeCache.current = {};
            }

            // Step 3: Find the last X market days up to targetDateStr
            const marketQuotes = quoteDataMap[marketSymbol] || {};
            const allMarketDates = Object.keys(marketQuotes)
                .filter(d => d <= targetDateStr)
                .sort();

            const countDays = Math.min(trajectoryLookbackDays, allMarketDates.length);
            const recentDates = allMarketDates.slice(-countDays);

            if (recentDates.length === 0) {
                setTrajectoryData([]);
                setIsCalculatingTrajectory(false);
                return;
            }

            // Step 4: Precompute smoothed regime matches for each preset across allMarketDates
            // respecting consecutiveDaysToEnter and minDuration
            const presetSmoothedMap = {};
            presetsToEvaluate.forEach(preset => {
                const conds = preset.conditions || [];
                if (conds.length === 0) return;
                const rawMatches = allMarketDates.map(d => {
                    return conds.every(cond => {
                        const q1 = quoteDataMap[cond.symbol]?.[d];
                        const q2 = cond.compareType === 'indicator' ? quoteDataMap[cond.compareSymbol]?.[d] : null;
                        return evaluateConditionOnQuotes(cond, q1, q2, apiIndicators);
                    });
                });
                presetSmoothedMap[preset.name] = applyRegimeFilters(rawMatches, consecutiveDaysToEnter, minDuration);
            });

            const marketDateIndexMap = {};
            allMarketDates.forEach((d, idx) => {
                marketDateIndexMap[d] = idx;
            });

            // Step 5: For each date in recentDates, evaluate presets and pull regime metrics
            const points = [];
            for (const dStr of recentDates) {
                const dateIdx = marketDateIndexMap[dStr];
                const matchedPresets = [];
                const matchedNames = [];
                presetsToEvaluate.forEach(preset => {
                    if (presetSmoothedMap[preset.name]?.[dateIdx]) {
                        matchedPresets.push(preset);
                        matchedNames.push(preset.name);
                    }
                });

                const combinedRules = [];
                matchedPresets.forEach(preset => {
                    (preset.conditions || []).forEach(c => {
                        if (!combinedRules.some(existing => areConditionsEqual(c, existing))) {
                            combinedRules.push(c);
                        }
                    });
                });

                const regimeKey = `${matchedNames.slice().sort().join('__')}__enter${consecutiveDaysToEnter}__min${minDuration}`;
                let metrics = trajectoryRegimeCache.current[regimeKey];

                if (!metrics) {
                    if (combinedRules.length === 0) {
                        metrics = {
                            regimeName: 'Unclassified',
                            winRate: 0,
                            episodeWinRate: 0,
                            sharpe: 0,
                            cagr: 0,
                            cumulativeReturn: 0,
                            maxDrawdown: 0,
                            avgDrawdown: 0,
                            capitalEfficiency: 0,
                            avgDailyReturn: 0,
                            avgExposure: 0,
                            inDaysPct: 0,
                            spyWinRate: 0,
                            spyAvgDailyReturn: 0,
                            spyCagr: 0
                        };
                    } else {
                        const rawMatches = [];
                        dailyResults.forEach((day) => {
                            const dayStr = normalizeDate(day.date);
                            let match = true;
                            for (const cond of combinedRules) {
                                const q1 = quoteDataMap[cond.symbol]?.[dayStr];
                                const q2 = cond.compareType === 'indicator' ? quoteDataMap[cond.compareSymbol]?.[dayStr] : null;
                                if (!evaluateConditionOnQuotes(cond, q1, q2, apiIndicators)) {
                                    match = false;
                                    break;
                                }
                            }
                            rawMatches.push(match);
                        });

                        const smoothedMatches = applyRegimeFilters(rawMatches, consecutiveDaysToEnter, minDuration);

                        const effectiveRegime = [];
                        for (let i = 0; i < smoothedMatches.length; i++) {
                            effectiveRegime.push(i === 0 ? smoothedMatches[0] : smoothedMatches[i - 1]);
                        }

                        const inRegimeDays = [];
                        const inRegimeSpyDays = [];
                        for (let i = 1; i < dailyResults.length; i++) {
                            if (effectiveRegime[i]) {
                                const prev = dailyResults[i - 1];
                                const curr = dailyResults[i];
                                const planRet = prev.total > 0 ? (curr.total - prev.total) / prev.total : 0;
                                const spyRet = prev.baseline > 0 ? (curr.baseline - prev.baseline) / prev.baseline : 0;
                                inRegimeDays.push({
                                    date: normalizeDate(curr.date),
                                    ret: planRet,
                                    exposure: curr.long_positions ?? 0
                                });
                                inRegimeSpyDays.push({
                                    date: normalizeDate(curr.date),
                                    ret: spyRet,
                                    exposure: 100
                                });
                            }
                        }

                        const stats = computeStats(inRegimeDays, false);
                        const spyStats = computeStats(inRegimeSpyDays, true);
                        const episodes = extractEpisodes(dailyResults, smoothedMatches, true, null, null, combinedRules, quoteDataMap, apiIndicators);
                        const winningEpisodes = episodes.filter(e => e.planReturn > 0).length;
                        const episodeWinRate = episodes.length > 0 ? (winningEpisodes / episodes.length) * 100 : 0;
                        const inDaysPct = dailyResults.length > 1 ? (inRegimeDays.length / (dailyResults.length - 1)) * 100 : 0;

                        metrics = {
                            regimeName: matchedNames.join(', ') || 'Unclassified',
                            winRate: stats.winRate,
                            episodeWinRate: episodeWinRate,
                            sharpe: stats.sharpe,
                            cagr: stats.cagr,
                            cumulativeReturn: stats.cumulativeReturn,
                            maxDrawdown: stats.maxDrawdown,
                            avgDrawdown: stats.avgDrawdown,
                            capitalEfficiency: stats.capitalEfficiency,
                            avgDailyReturn: stats.avgDailyReturn,
                            avgExposure: stats.avgExposure,
                            inDaysPct: inDaysPct,
                            spyWinRate: spyStats.winRate,
                            spyAvgDailyReturn: spyStats.avgDailyReturn,
                            spyCagr: spyStats.cagr
                        };
                    }
                    trajectoryRegimeCache.current[regimeKey] = metrics;
                }

                const [py, pm, pd] = dStr.split('-').map(Number);
                points.push({
                    dateStr: dStr,
                    dateObj: new Date(py, pm - 1, pd),
                    ...metrics
                });
            }

            setTrajectoryData(points);
        } catch (e) {
            console.error("Error computing recent days regime trajectory", e);
            setTrajectoryError(e.message || e.toString());
        } finally {
            setIsCalculatingTrajectory(false);
        }
    }, [backtestData, selectedPresetDate, trajectoryLookbackDays, bullBearMovingAverage, marketSymbol, consecutiveDaysToEnter, minDuration, apiIndicators, regimePresets]);

    // Automatically trigger trajectory calculation whenever the date, lookback days, or plan changes
    useEffect(() => {
        if (backtestData && backtestData.dailyResults && backtestData.dailyResults.length > 2) {
            runRecentDaysTrajectory();
        }
    }, [runRecentDaysTrajectory, backtestData]);

    // Trajectory Chart rows for Google Charts ComboChart (discrete string date domain to omit non-market days)
    const trajectoryChartData = useMemo(() => {
        if (!trajectoryData || trajectoryData.length === 0) return null;

        const leftMetricObj = TRAJECTORY_METRICS.find(m => m.key === trajectoryLeftMetric) || TRAJECTORY_METRICS[0];
        const rightMetricObj = TRAJECTORY_METRICS.find(m => m.key === trajectoryRightMetric) || TRAJECTORY_METRICS[1];

        const rows = [
            [
                { type: 'string', label: 'Date' },
                leftMetricObj.label,
                { type: 'string', role: 'tooltip', p: { html: true } },
                rightMetricObj.label,
                { type: 'string', role: 'tooltip', p: { html: true } }
            ]
        ];

        trajectoryData.forEach(pt => {
            const leftVal = Number(pt[trajectoryLeftMetric]) || 0;
            const rightVal = Number(pt[trajectoryRightMetric]) || 0;
            const leftFmt = formatMetricDisplay(leftVal, leftMetricObj.format);
            const rightFmt = formatMetricDisplay(rightVal, rightMetricObj.format);
            const tooltip = createTrajectoryTooltip(
                pt.dateStr,
                pt.regimeName,
                leftMetricObj.label,
                leftFmt,
                rightMetricObj.label,
                rightFmt
            );
            const comp = tableHelpers.getEasternDateComponents(pt.dateStr);
            const dateLabel = comp ? `${comp.month}/${comp.day}` : pt.dateStr;
            rows.push([
                dateLabel,
                leftVal,
                tooltip,
                rightVal,
                tooltip
            ]);
        });

        return rows;
    }, [trajectoryData, trajectoryLeftMetric, trajectoryRightMetric]);

    // Trajectory Chart Options for Dual-Axis ComboChart
    const trajectoryChartOptions = useMemo(() => {
        const leftMetricObj = TRAJECTORY_METRICS.find(m => m.key === trajectoryLeftMetric) || TRAJECTORY_METRICS[0];
        const rightMetricObj = TRAJECTORY_METRICS.find(m => m.key === trajectoryRightMetric) || TRAJECTORY_METRICS[1];

        // Vertical buffer so the graph does not stretch from extreme top to extreme bottom
        const leftVals = (trajectoryData || []).map(pt => Number(pt[trajectoryLeftMetric])).filter(v => v !== null && !isNaN(v));
        const rightVals = (trajectoryData || []).map(pt => Number(pt[trajectoryRightMetric])).filter(v => v !== null && !isNaN(v));

        const getBufferWindow = (vals, format) => {
            if (!vals || vals.length === 0) return undefined;
            const min = Math.min(...vals);
            const max = Math.max(...vals);
            if (!isFinite(min) || !isFinite(max)) return undefined;
            const span = max - min;
            const pad = span > 0 ? span * 0.15 : (Math.abs(min) > 0 ? Math.abs(min) * 0.15 : 0.05);
            let winMin = min - pad;
            let winMax = max + pad;
            if (format === 'percent' && min >= 0 && winMin < 0) {
                winMin = 0;
            }
            return { min: winMin, max: winMax };
        };

        const leftViewWindow = getBufferWindow(leftVals, leftMetricObj.format);
        const rightViewWindow = getBufferWindow(rightVals, rightMetricObj.format);

        const getMetricFormatPattern = (format) => {
            if (format === 'percent') return '#,##0.0\'%\'';
            if (format === 'percentChange') return '+#,##0.0\'%\';-#,##0.0\'%\';0.0\'%\'';
            return '#,##0.00';
        };

        return {
            legend: { position: 'top', textStyle: { fontSize: 12, bold: true } },
            chartArea: { width: '88%', height: '70%', top: 35, bottom: 45, left: 65, right: 65 },
            tooltip: { isHtml: true },
            focusTarget: 'category',
            hAxis: {
                gridlines: { color: '#f0f0f0' },
                textStyle: { fontSize: 11, color: '#495057' }
            },
            vAxes: {
                0: {
                    title: leftMetricObj.label,
                    titleTextStyle: { fontSize: 12, bold: true, color: '#0d6efd' },
                    textStyle: { fontSize: 11, color: '#0d6efd' },
                    gridlines: { color: '#f8f9fa' },
                    format: getMetricFormatPattern(leftMetricObj.format),
                    ...(leftViewWindow ? { viewWindow: leftViewWindow } : {})
                },
                1: {
                    title: rightMetricObj.label,
                    titleTextStyle: { fontSize: 12, bold: true, color: '#198754' },
                    textStyle: { fontSize: 11, color: '#198754' },
                    gridlines: { color: 'none' },
                    format: getMetricFormatPattern(rightMetricObj.format),
                    ...(rightViewWindow ? { viewWindow: rightViewWindow } : {})
                }
            },
            seriesType: trajectoryChartStyle === 'both_lines' ? 'line' : 'bars',
            series: {
                0: {
                    targetAxisIndex: 0,
                    type: trajectoryChartStyle === 'both_lines' ? 'line' : 'bars',
                    color: '#0d6efd',
                    lineWidth: 2.5,
                    pointSize: trajectoryChartStyle === 'both_lines' ? 5 : 0
                },
                1: {
                    targetAxisIndex: 1,
                    type: 'line',
                    color: '#198754',
                    lineWidth: 2.5,
                    pointSize: 5
                }
            }
        };
    }, [trajectoryData, trajectoryLeftMetric, trajectoryRightMetric, trajectoryChartStyle]);

    // Trajectory trend analysis comparing the first 1/3 days of the window to the latest 1/3 days
    const trajectoryTrendSummary = useMemo(() => {
        if (!trajectoryData || trajectoryData.length < 2) return null;

        const leftMetricObj = TRAJECTORY_METRICS.find(m => m.key === trajectoryLeftMetric) || TRAJECTORY_METRICS[0];
        const rightMetricObj = TRAJECTORY_METRICS.find(m => m.key === trajectoryRightMetric) || TRAJECTORY_METRICS[1];

        const k = Math.min(5, Math.max(1, Math.floor(trajectoryData.length / 3)));
        const firstK = trajectoryData.slice(0, k);
        const lastK = trajectoryData.slice(-k);

        const firstLeftAvg = firstK.reduce((s, p) => s + (Number(p[trajectoryLeftMetric]) || 0), 0) / k;
        const lastLeftAvg = lastK.reduce((s, p) => s + (Number(p[trajectoryLeftMetric]) || 0), 0) / k;
        const deltaLeft = lastLeftAvg - firstLeftAvg;

        const firstRightAvg = firstK.reduce((s, p) => s + (Number(p[trajectoryRightMetric]) || 0), 0) / k;
        const lastRightAvg = lastK.reduce((s, p) => s + (Number(p[trajectoryRightMetric]) || 0), 0) / k;
        const deltaRight = lastRightAvg - firstRightAvg;

        const latest = trajectoryData[trajectoryData.length - 1];

        const isHigherBetter = (key) => key !== 'maxDrawdown' && key !== 'avgDrawdown';
        const leftScore = isHigherBetter(trajectoryLeftMetric) ? deltaLeft : -deltaLeft;
        const rightScore = isHigherBetter(trajectoryRightMetric) ? deltaRight : -deltaRight;

        let trendLabel = 'Stable';
        let trendBadge = 'badge bg-secondary-subtle text-secondary border';
        let trendIcon = '→';

        if (leftScore > 0.05 && rightScore > 0.05) {
            trendLabel = 'Strongly Improving';
            trendBadge = 'badge bg-success text-white';
            trendIcon = '⇈';
        } else if (leftScore > 0.02 || rightScore > 0.02) {
            trendLabel = 'Improving';
            trendBadge = 'badge bg-success-subtle text-success border border-success-subtle';
            trendIcon = '↗';
        } else if (leftScore < -0.05 && rightScore < -0.05) {
            trendLabel = 'Strongly Deteriorating';
            trendBadge = 'badge bg-danger text-white';
            trendIcon = '⇊';
        } else if (leftScore < -0.02 || rightScore < -0.02) {
            trendLabel = 'Deteriorating';
            trendBadge = 'badge bg-danger-subtle text-danger border border-danger-subtle';
            trendIcon = '↘';
        }

        return {
            latestDateStr: latest.dateStr,
            latestRegimeName: latest.regimeName,
            latestLeftVal: Number(latest[trajectoryLeftMetric]) || 0,
            latestRightVal: Number(latest[trajectoryRightMetric]) || 0,
            deltaLeft,
            deltaRight,
            trendLabel,
            trendBadge,
            trendIcon,
            leftMetricObj,
            rightMetricObj
        };
    }, [trajectoryData, trajectoryLeftMetric, trajectoryRightMetric]);

    // Click on trajectory day to select and evaluate that date
    const trajectoryChartEvents = useMemo(() => [
        {
            eventName: 'select',
            callback: ({ chartWrapper }) => {
                const chart = chartWrapper.getChart();
                const selection = chart.getSelection();
                if (selection && selection.length > 0 && selection[0].row !== undefined) {
                    const pt = trajectoryData[selection[0].row];
                    if (pt && pt.dateStr) {
                        const [y, m, d] = pt.dateStr.split('-').map(Number);
                        setSelectedPresetDate(new Date(y, m - 1, d));
                    }
                }
            }
        }
    ], [trajectoryData]);

    // Dynamic filtering for zoom / selected timeframe
    const zoomedResults = useMemo(() => {
        if (!fullAnalysisSeries) return null;

        const { dailyResults, rawMatches, dateList, quoteDataMap } = fullAnalysisSeries;
        if (!dailyResults || dailyResults.length < 2) return null;

        // Dynamically compute smoothedMatches and effectiveRegime from rawMatches so changing
        // consecutiveDaysToEnter or minDuration updates all stats and charts immediately
        const smoothedMatches = (rawMatches && rawMatches.length > 0)
            ? applyRegimeFilters(rawMatches, consecutiveDaysToEnter, minDuration)
            : (fullAnalysisSeries.effectiveRegime || []);

        const effectiveRegime = [];
        for (let i = 0; i < smoothedMatches.length; i++) {
            if (i === 0) {
                effectiveRegime.push(smoothedMatches[0]);
            } else {
                effectiveRegime.push(smoothedMatches[i - 1]);
            }
        }

        const inRegimePlanDays = [];
        const outRegimePlanDays = [];
        const allPlanDays = [];

        const inRegimeSpyDays = [];
        const outRegimeSpyDays = [];
        const allSpyDays = [];

        const chartRowsSegmented = [
            ["Date", "In-Regime Plan", "Out-of-Regime Plan", "Full History Plan", "SPY Baseline"]
        ];

        const chartRowsTimeline = [
            ["Date", "Plan Return", { role: 'annotation' }, "SPY Baseline", "Regime Active (Right Axis)"]
        ];

        const chartRowsExposure = [
            ["Date", "Plan Net Exposure (%)", "Invested Capital (%)", "Regime Active (0-100)"]
        ];

        let synInMult = 1.0;
        let synOutMult = 1.0;
        let synFullMult = 1.0;
        let synSpyMult = 1.0;

        // Determine slice start & baseline reference for the zoom window
        const zoomStartStr = zoomStart ? normalizeDate(zoomStart) : null;
        const zoomEndStr = zoomEnd ? normalizeDate(zoomEnd) : null;

        let firstDayIndex = 1;
        if (zoomStartStr) {
            for (let i = 1; i < dailyResults.length; i++) {
                if (normalizeDate(dailyResults[i].date) >= zoomStartStr) {
                    firstDayIndex = i;
                    break;
                }
            }
        }

        const startTotal = dailyResults[firstDayIndex - 1]?.total || 100000;
        const startSpy = dailyResults[firstDayIndex - 1]?.baseline || 100000;

        // 1. Extract all contiguous in-regime and out-of-regime episodes within the slice
        const regimeEpisodes = extractEpisodes(dailyResults, smoothedMatches, true, zoomStart, zoomEnd, conditions, quoteDataMap, apiIndicators);
        const outRegimeEpisodes = extractEpisodes(dailyResults, smoothedMatches, false, zoomStart, zoomEnd, conditions, quoteDataMap, apiIndicators);

        // Episode Win Rates (Plan vs SPY)
        const inPlanWinningEpisodes = regimeEpisodes.filter(e => e.planReturn > 0).length;
        const inSpyWinningEpisodes = regimeEpisodes.filter(e => e.spyReturn > 0).length;
        const inPlanRegimeWinRate = regimeEpisodes.length > 0 ? (inPlanWinningEpisodes / regimeEpisodes.length) * 100 : 0;
        const inSpyRegimeWinRate = regimeEpisodes.length > 0 ? (inSpyWinningEpisodes / regimeEpisodes.length) * 100 : 0;

        const outPlanWinningEpisodes = outRegimeEpisodes.filter(e => e.planReturn > 0).length;
        const outSpyWinningEpisodes = outRegimeEpisodes.filter(e => e.spyReturn > 0).length;
        const outPlanRegimeWinRate = outRegimeEpisodes.length > 0 ? (outPlanWinningEpisodes / outRegimeEpisodes.length) * 100 : 0;
        const outSpyRegimeWinRate = outRegimeEpisodes.length > 0 ? (outSpyWinningEpisodes / outRegimeEpisodes.length) * 100 : 0;

        const allEpisodesCount = regimeEpisodes.length + outRegimeEpisodes.length;
        const allPlanWinningEpisodes = inPlanWinningEpisodes + outPlanWinningEpisodes;
        const allSpyWinningEpisodes = inSpyWinningEpisodes + outSpyWinningEpisodes;
        const allPlanRegimeWinRate = allEpisodesCount > 0 ? (allPlanWinningEpisodes / allEpisodesCount) * 100 : 0;
        const allSpyRegimeWinRate = allEpisodesCount > 0 ? (allSpyWinningEpisodes / allEpisodesCount) * 100 : 0;

        // Build midpoint annotation map for timeline chart
        const isZoomed = selectedTimeframe !== 'All' || Boolean(zoomStart) || Boolean(zoomEnd);
        const shouldShowAnnotations = showTimelineAnnotations && (isZoomed || regimeEpisodes.length <= 8);

        const episodeMidpointMap = {};
        if (shouldShowAnnotations) {
            regimeEpisodes.forEach(ep => {
                if (ep.midDateStr) {
                    episodeMidpointMap[ep.midDateStr] = ep;
                }
            });
        }

        // Build Column Chart rows and episode stats (no annotations)
        const chartRowsEpisodes = [
            [
                "Regime Episode",
                "Plan Return",
                "SPY Baseline"
            ]
        ];

        let totalPlanEpisodeRet = 0;
        let totalSpyEpisodeRet = 0;
        let winEpisodesCount = 0;
        let cumPlanMult = 1.0;
        let cumSpyMult = 1.0;

        regimeEpisodes.forEach((ep) => {
            totalPlanEpisodeRet += ep.planReturn;
            totalSpyEpisodeRet += ep.spyReturn;
            cumPlanMult *= (1 + ep.planReturn);
            cumSpyMult *= (1 + ep.spyReturn);
            if (ep.planReturn > ep.spyReturn) {
                winEpisodesCount++;
            }

            const sNorm = normalizeDate(ep.startDate);
            const [sY, sM, sD] = sNorm.split('-').map(Number);
            const sYear = sY ? sY.toString().slice(2) : '';
            let epLabel;

            if (ep.isOngoing || !ep.endDate) {
                const sStr = `${sM}/${sD}/${sYear}`;
                epLabel = `#${ep.id}: ${sStr}-Active (${ep.days}d) *`;
            } else {
                const eNorm = normalizeDate(ep.endDate);
                const [eY, eM, eD] = eNorm.split('-').map(Number);
                const eYear = eY ? eY.toString().slice(2) : '';
                const sStr = `${sM}/${sD}${sYear !== eYear ? `/${sYear}` : ''}`;
                const eStr = `${eM}/${eD}/${eYear}`;
                epLabel = `#${ep.id}: ${sStr}-${eStr} (${ep.days}d)`;
            }

            chartRowsEpisodes.push([
                epLabel,
                ep.planReturn,
                ep.spyReturn
            ]);
        });

        const avgPlanEpisodeReturn = regimeEpisodes.length > 0 ? totalPlanEpisodeRet / regimeEpisodes.length : 0;
        const avgSpyEpisodeReturn = regimeEpisodes.length > 0 ? totalSpyEpisodeRet / regimeEpisodes.length : 0;
        const winRate = regimeEpisodes.length > 0 ? (winEpisodesCount / regimeEpisodes.length) * 100 : 0;
        const cumulativeInPlanRet = cumPlanMult - 1;
        const cumulativeInSpyRet = cumSpyMult - 1;

        const totalInRegimeEpisodeDays = regimeEpisodes.reduce((s, ep) => s + (ep.days || 0), 0);
        const avgRegimeDays = regimeEpisodes.length > 0
            ? totalInRegimeEpisodeDays / regimeEpisodes.length
            : (inRegimePlanDays.length > 0 ? inRegimePlanDays.length : 0);

        const totalOutRegimeEpisodeDays = outRegimeEpisodes.reduce((s, ep) => s + (ep.days || 0), 0);
        const avgOutRegimeDays = outRegimeEpisodes.length > 0
            ? totalOutRegimeEpisodeDays / outRegimeEpisodes.length
            : (outRegimePlanDays.length > 0 ? outRegimePlanDays.length : 0);

        // Push baseline anchor point at T_start so lines originate cleanly from 0%
        if (firstDayIndex >= 1 && dailyResults[firstDayIndex - 1]) {
            const anchorDateStr = dateList[firstDayIndex - 1];
            const anchorComp = tableHelpers.getEasternDateComponents(anchorDateStr);
            const anchorLabel = anchorComp ? `${anchorComp.month}/${anchorComp.day}/${anchorComp.yearStr.slice(-2)}` : tableHelpers.dateFormmatter(anchorDateStr);
            const anchorRegime = effectiveRegime[firstDayIndex - 1];
            chartRowsSegmented.push([
                anchorLabel,
                0,
                0,
                0,
                0
            ]);
            chartRowsTimeline.push([
                anchorLabel,
                0,
                null,
                0,
                anchorRegime ? 1 : 0
            ]);
            chartRowsExposure.push([
                anchorLabel,
                dailyResults[firstDayIndex - 1].long_positions ?? 0,
                dailyResults[firstDayIndex - 1].total > 0
                    ? ((dailyResults[firstDayIndex - 1].total - (dailyResults[firstDayIndex - 1].cash ?? 0)) / dailyResults[firstDayIndex - 1].total) * 100
                    : 0,
                anchorRegime ? 100 : 0
            ]);
        }

        let regimeChanges = 0;
        let prevRegime = null;

        for (let i = 1; i < dailyResults.length; i++) {
            const prev = dailyResults[i - 1];
            const curr = dailyResults[i];
            const dStr = dateList[i];

            // Date filtering
            if (zoomStartStr && dStr < zoomStartStr) continue;
            if (zoomEndStr && dStr > zoomEndStr) continue;

            const dComp = tableHelpers.getEasternDateComponents(dStr);
            const dateLabel = dComp ? `${dComp.month}/${dComp.day}/${dComp.yearStr.slice(-2)}` : tableHelpers.dateFormmatter(dStr);

            const planRet = prev.total > 0 ? (curr.total - prev.total) / prev.total : 0;
            const spyRet = prev.baseline > 0 ? (curr.baseline - prev.baseline) / prev.baseline : 0;

            const exposure = curr.long_positions ?? 0;
            const investedCap = curr.total > 0 ? ((curr.total - (curr.cash ?? 0)) / curr.total) * 100 : 0;
            const isInRegime = Boolean(effectiveRegime[i]);

            if (prevRegime !== null && isInRegime !== prevRegime) {
                regimeChanges++;
            }
            prevRegime = isInRegime;

            const planDayObj = { date: dStr, ret: planRet, exposure: exposure, investedCap };
            const spyDayObj = { date: dStr, ret: spyRet, exposure: 100, investedCap: 100 };

            allPlanDays.push(planDayObj);
            allSpyDays.push(spyDayObj);

            if (isInRegime) {
                inRegimePlanDays.push(planDayObj);
                inRegimeSpyDays.push(spyDayObj);
                synInMult *= (1 + planRet);
            } else {
                outRegimePlanDays.push(planDayObj);
                outRegimeSpyDays.push(spyDayObj);
                synOutMult *= (1 + planRet);
            }

            synFullMult *= (1 + planRet);
            synSpyMult *= (1 + spyRet);

            chartRowsSegmented.push([
                dateLabel,
                synInMult - 1,
                synOutMult - 1,
                synFullMult - 1,
                synSpyMult - 1
            ]);

            const planPct = startTotal > 0 ? (curr.total - startTotal) / startTotal : 0;
            const spyPct = startSpy > 0 ? (curr.baseline - startSpy) / startSpy : 0;

            const matchingEp = episodeMidpointMap[dStr];
            const timelineAnnotation = matchingEp
                ? `Plan: ${formatDecimalPercent(matchingEp.planReturn)}\nSPY: ${formatDecimalPercent(matchingEp.spyReturn)}`
                : null;

            chartRowsTimeline.push([
                dateLabel,
                planPct,
                timelineAnnotation,
                spyPct,
                smoothedMatches[i] ? 1 : 0
            ]);

            chartRowsExposure.push([
                dateLabel,
                exposure,
                investedCap,
                smoothedMatches[i] ? 100 : 0
            ]);
        }

        const inPlanStats = computeStats(inRegimePlanDays, false);
        const inSpyStats = computeStats(inRegimeSpyDays, true);

        const outPlanStats = computeStats(outRegimePlanDays, false);
        const outSpyStats = computeStats(outRegimeSpyDays, true);

        const allPlanStats = computeStats(allPlanDays, false);
        const allSpyStats = computeStats(allSpyDays, true);

        return {
            totalDays: allPlanDays.length,
            inDays: inRegimePlanDays.length,
            outDays: outRegimePlanDays.length,
            inPct: allPlanDays.length > 0 ? (inRegimePlanDays.length / allPlanDays.length) * 100 : 0,
            regimeChanges,
            avgRegimeDays,
            avgOutRegimeDays,
            inPlanStats,
            inSpyStats,
            outPlanStats,
            outSpyStats,
            allPlanStats,
            allSpyStats,
            chartRowsSegmented,
            chartRowsTimeline,
            chartRowsExposure,
            regimeEpisodes,
            outRegimeEpisodes,
            chartRowsEpisodes,
            isZoomed,
            avgPlanEpisodeReturn,
            avgSpyEpisodeReturn,
            winEpisodesCount,
            winRate,
            cumulativeInPlanRet,
            cumulativeInSpyRet,
            inPlanWinningEpisodes,
            inSpyWinningEpisodes,
            inPlanRegimeWinRate,
            inSpyRegimeWinRate,
            outPlanWinningEpisodes,
            outSpyWinningEpisodes,
            outPlanRegimeWinRate,
            outSpyRegimeWinRate,
            allEpisodesCount,
            allPlanWinningEpisodes,
            allSpyWinningEpisodes,
            allPlanRegimeWinRate,
            allSpyRegimeWinRate
        };
    }, [fullAnalysisSeries, zoomStart, zoomEnd, selectedTimeframe, showTimelineAnnotations, conditions, apiIndicators, consecutiveDaysToEnter, minDuration]);

    // In-Regime vs Out-of-Regime Capital Efficiency Comparison
    const efficiencyComparison = useMemo(() => {
        if (!zoomedResults) return null;
        return getEfficiencyComparison(
            zoomedResults.inPlanStats,
            zoomedResults.outPlanStats,
            zoomedResults.inSpyStats
        );
    }, [zoomedResults]);

    // Post-Regime Forward Performance Analysis (1W, 2W, 1M, 2M, 3M market days)
    const forwardPerformance = useMemo(() => {
        if (!zoomedResults || !zoomedResults.regimeEpisodes || !fullAnalysisSeries?.dailyResults) {
            return null;
        }
        const dailyResults = fullAnalysisSeries.dailyResults;
        const episodes = zoomedResults.regimeEpisodes;

        return FORWARD_DURATIONS.map(dur => {
            const days = dur.days;
            let totalPlanRet = 0;
            let totalSpyRet = 0;
            let planPositiveCount = 0;
            let spyPositiveCount = 0;
            let planBeatSpyCount = 0;
            let totalMaxDdPlan = 0;
            let totalMaxDdSpy = 0;
            let worstDdPlan = 0;
            let worstDdSpy = 0;
            let bestPlanRet = null;
            let bestSpyRet = null;
            let qualifyingCount = 0;

            episodes.forEach(ep => {
                const startIdx = ep.startIndex;
                const targetIdx = startIdx + days;

                if (targetIdx < dailyResults.length) {
                    const startPlanEquity = ep.startTotal || dailyResults[startIdx]?.total;
                    const endPlanEquity = dailyResults[targetIdx]?.total;
                    const startSpyEquity = ep.startSpy || dailyResults[startIdx]?.baseline;
                    const endSpyEquity = dailyResults[targetIdx]?.baseline;

                    if (startPlanEquity > 0 && endPlanEquity > 0) {
                        const planRet = (endPlanEquity - startPlanEquity) / startPlanEquity;
                        const spyRet = startSpyEquity > 0 ? (endSpyEquity - startSpyEquity) / startSpyEquity : 0;

                        totalPlanRet += planRet;
                        totalSpyRet += spyRet;

                        if (bestPlanRet === null || planRet > bestPlanRet) bestPlanRet = planRet;
                        if (bestSpyRet === null || spyRet > bestSpyRet) bestSpyRet = spyRet;

                        if (planRet > 0) planPositiveCount++;
                        if (spyRet > 0) spyPositiveCount++;
                        if (planRet > spyRet) planBeatSpyCount++;

                        // Starting price to trough drawdown over the window [startIdx, targetIdx]
                        let minPlan = startPlanEquity;
                        let minSpy = startSpyEquity;

                        for (let k = startIdx; k <= targetIdx; k++) {
                            const curPlan = dailyResults[k]?.total || 0;
                            if (curPlan > 0 && curPlan < minPlan) minPlan = curPlan;

                            const curSpy = dailyResults[k]?.baseline || 0;
                            if (curSpy > 0 && curSpy < minSpy) minSpy = curSpy;
                        }

                        const epMaxDdPlan = startPlanEquity > 0 && minPlan < startPlanEquity
                            ? (startPlanEquity - minPlan) / startPlanEquity
                            : 0;
                        const epMaxDdSpy = startSpyEquity > 0 && minSpy < startSpyEquity
                            ? (startSpyEquity - minSpy) / startSpyEquity
                            : 0;

                        totalMaxDdPlan += epMaxDdPlan;
                        totalMaxDdSpy += epMaxDdSpy;

                        if (epMaxDdPlan > worstDdPlan) worstDdPlan = epMaxDdPlan;
                        if (epMaxDdSpy > worstDdSpy) worstDdSpy = epMaxDdSpy;

                        qualifyingCount++;
                    }
                }
            });

            const avgPlanReturn = qualifyingCount > 0 ? totalPlanRet / qualifyingCount : 0;
            const avgSpyReturn = qualifyingCount > 0 ? totalSpyRet / qualifyingCount : 0;
            const avgAlpha = avgPlanReturn - avgSpyReturn;

            const bestPlanReturn = (qualifyingCount > 0 && bestPlanRet !== null) ? bestPlanRet : 0;
            const bestSpyReturn = (qualifyingCount > 0 && bestSpyRet !== null) ? bestSpyRet : 0;

            const planWinRate = qualifyingCount > 0 ? (planPositiveCount / qualifyingCount) * 100 : 0;
            const spyWinRate = qualifyingCount > 0 ? (spyPositiveCount / qualifyingCount) * 100 : 0;
            const beatSpyRate = qualifyingCount > 0 ? (planBeatSpyCount / qualifyingCount) * 100 : 0;

            const avgMaxDdPlan = qualifyingCount > 0 ? (totalMaxDdPlan / qualifyingCount) * 100 : 0;
            const avgMaxDdSpy = qualifyingCount > 0 ? (totalMaxDdSpy / qualifyingCount) * 100 : 0;

            return {
                key: dur.key,
                label: dur.label,
                days: dur.days,
                sublabel: dur.sublabel,
                count: qualifyingCount,
                totalEpisodes: episodes.length,
                avgPlanReturn,
                avgSpyReturn,
                avgAlpha,
                bestPlanReturn,
                bestSpyReturn,
                planWinRate,
                spyWinRate,
                beatSpyRate,
                planPositiveCount,
                spyPositiveCount,
                planBeatSpyCount,
                avgMaxDdPlan,
                avgMaxDdSpy,
                worstDdPlan: worstDdPlan * 100,
                worstDdSpy: worstDdSpy * 100
            };
        });
    }, [zoomedResults, fullAnalysisSeries]);

    // Format zoom label for UI display
    const zoomLabel = useMemo(() => {
        if (selectedTimeframe === 'All') return 'Full History';
        if (selectedTimeframe === 'YTD') return `YTD (${new Date().getFullYear()})`;
        if (selectedTimeframe === '1Y') return 'Past 1 Year';
        if (selectedTimeframe === '3Y') return 'Past 3 Years';
        if (selectedTimeframe === '5Y') return 'Past 5 Years';
        if (selectedTimeframe === 'Custom') return `${customStartDate || 'Start'} to ${customEndDate || 'Present'}`;
        return `Year ${selectedTimeframe}`;
    }, [selectedTimeframe, customStartDate, customEndDate]);

    const forward1W = forwardPerformance?.find(f => f.key === '1w');
    const forward2W = forwardPerformance?.find(f => f.key === '2w');

    const formatForwardRet = (val, count) => {
        if (!count || val === null || val === undefined || isNaN(val)) return '—';
        return formatDecimalPercent(val);
    };

    const formatForwardWinRate = (val, count) => {
        if (!count || val === null || val === undefined || isNaN(val)) return '—';
        return tableHelpers.percentFormatterOneDecimal(val);
    };

    const formatForwardDd = (val, count) => {
        if (!count || val === null || val === undefined || isNaN(val)) return '—';
        if (val > 0) return `-${tableHelpers.percentFormatterOneDecimal(val)}`;
        return '0.0%';
    };

    const activeHorizonConfig = useMemo(() => {
        if (episodeChartHorizon === '1w') return { key: '1w', label: '1W', days: 5 };
        if (episodeChartHorizon === '2w') return { key: '2w', label: '2W', days: 10 };
        if (episodeChartHorizon === '1m') return { key: '1m', label: '1M', days: 22 };
        if (episodeChartHorizon === '2m') return { key: '2m', label: '2M', days: 44 };
        if (episodeChartHorizon === '3m') return { key: '3m', label: '3M', days: 66 };
        return { key: 'full', label: 'Full Episode', days: null };
    }, [episodeChartHorizon]);

    const activeHorizonStats = useMemo(() => {
        if (!zoomedResults) return null;
        if (episodeChartHorizon === 'full') {
            return {
                avgPlanReturn: zoomedResults.avgPlanEpisodeReturn,
                avgSpyReturn: zoomedResults.avgSpyEpisodeReturn,
                winRate: zoomedResults.winRate,
                winCount: zoomedResults.winEpisodesCount,
                totalCount: zoomedResults.regimeEpisodes.length,
                planWinRate: zoomedResults.inPlanRegimeWinRate,
                spyWinRate: zoomedResults.inSpyRegimeWinRate,
                planWonCount: zoomedResults.inPlanWinningEpisodes,
                spyWonCount: zoomedResults.inSpyWinningEpisodes,
                label: 'Episode'
            };
        }
        const fwd = forwardPerformance?.find(f => f.key === episodeChartHorizon);
        if (fwd) {
            return {
                avgPlanReturn: fwd.avgPlanReturn,
                avgSpyReturn: fwd.avgSpyReturn,
                winRate: fwd.beatSpyRate,
                winCount: fwd.planBeatSpyCount,
                totalCount: fwd.count,
                planWinRate: fwd.planWinRate,
                spyWinRate: fwd.spyWinRate,
                planWonCount: fwd.planPositiveCount,
                spyWonCount: fwd.spyPositiveCount,
                label: fwd.label
            };
        }
        return {
            avgPlanReturn: 0,
            avgSpyReturn: 0,
            winRate: 0,
            winCount: 0,
            totalCount: 0,
            planWinRate: 0,
            spyWinRate: 0,
            planWonCount: 0,
            spyWonCount: 0,
            label: activeHorizonConfig.label
        };
    }, [zoomedResults, forwardPerformance, episodeChartHorizon, activeHorizonConfig]);

    const episodeChartData = useMemo(() => {
        if (!zoomedResults || !zoomedResults.regimeEpisodes || !fullAnalysisSeries?.dailyResults) {
            return null;
        }

        const dailyResults = fullAnalysisSeries.dailyResults;
        const episodes = zoomedResults.regimeEpisodes;
        const isFullEpisode = episodeChartHorizon === 'full' || !activeHorizonConfig.days;
        const horizonDays = activeHorizonConfig.days;
        const horizonLabel = activeHorizonConfig.label;

        const chartRows = [
            [
                "Regime Episode",
                isFullEpisode ? "Plan Return" : `Plan ${horizonLabel} Return`,
                isFullEpisode ? "SPY Baseline" : `SPY ${horizonLabel} Baseline`
            ]
        ];

        episodes.forEach((ep) => {
            const sNorm = normalizeDate(ep.startDate);
            const [sY, sM, sD] = sNorm.split('-').map(Number);
            const sYear = sY ? sY.toString().slice(2) : '';

            if (isFullEpisode) {
                let epLabel;
                if (ep.isOngoing || !ep.endDate) {
                    const sStr = `${sM}/${sD}/${sYear}`;
                    epLabel = `#${ep.id}: ${sStr}-Active (${ep.days}d) *`;
                } else {
                    const eNorm = normalizeDate(ep.endDate);
                    const [eY, eM, eD] = eNorm.split('-').map(Number);
                    const eYear = eY ? eY.toString().slice(2) : '';
                    const sStr = `${sM}/${sD}${sYear !== eYear ? `/${sYear}` : ''}`;
                    const eStr = `${eM}/${eD}/${eYear}`;
                    epLabel = `#${ep.id}: ${sStr}-${eStr} (${ep.days}d)`;
                }

                chartRows.push([
                    epLabel,
                    ep.planReturn,
                    ep.spyReturn
                ]);
            } else {
                const startIdx = ep.startIndex;
                const targetIdx = startIdx + horizonDays;

                const startPlanEquity = ep.startTotal || dailyResults[startIdx]?.total;
                const startSpyEquity = ep.startSpy || dailyResults[startIdx]?.baseline;

                if (targetIdx < dailyResults.length) {
                    const endPlanEquity = dailyResults[targetIdx]?.total;
                    const endSpyEquity = dailyResults[targetIdx]?.baseline;
                    const planRet = (startPlanEquity > 0 && endPlanEquity > 0) ? (endPlanEquity - startPlanEquity) / startPlanEquity : 0;
                    const spyRet = (startSpyEquity > 0 && endSpyEquity > 0) ? (endSpyEquity - startSpyEquity) / startSpyEquity : 0;

                    const targetDate = dailyResults[targetIdx]?.date;
                    const [tY, tM, tD] = normalizeDate(targetDate).split('-').map(Number);
                    const tYear = tY ? tY.toString().slice(2) : '';
                    const sStr = `${sM}/${sD}${sYear !== tYear ? `/${sYear}` : ''}`;
                    const tStr = `${tM}/${tD}/${tYear}`;
                    const epLabel = `#${ep.id}: ${sStr}-${tStr} (${horizonLabel})`;

                    chartRows.push([
                        epLabel,
                        planRet,
                        spyRet
                    ]);
                } else {
                    const lastIdx = dailyResults.length - 1;
                    const endPlanEquity = dailyResults[lastIdx]?.total;
                    const endSpyEquity = dailyResults[lastIdx]?.baseline;
                    const planRet = (startPlanEquity > 0 && endPlanEquity > 0) ? (endPlanEquity - startPlanEquity) / startPlanEquity : 0;
                    const spyRet = (startSpyEquity > 0 && endSpyEquity > 0) ? (endSpyEquity - startSpyEquity) / startSpyEquity : 0;
                    const daysElapsed = Math.max(0, lastIdx - startIdx);

                    const epLabel = `#${ep.id}: ${sM}/${sD}/${sYear} (${daysElapsed}d to date) *`;

                    chartRows.push([
                        epLabel,
                        planRet,
                        spyRet
                    ]);
                }
            }
        });

        return chartRows;
    }, [zoomedResults, fullAnalysisSeries, episodeChartHorizon, activeHorizonConfig]);

    return (
        <div style={{ padding: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                    <h3 style={{ margin: 0 }}>Market Regime Performance Analysis</h3>
                    <div style={{ color: '#666', fontSize: '13px' }}>
                        Evaluate plan alpha, drawdowns, and exposure partitioned by custom market conditions.
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <label style={{ margin: 0, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                            type="checkbox"
                            checked={liveTradesOnly}
                            onChange={(e) => setLiveTradesOnly(e.target.checked)}
                        />
                        Live Trades Only
                    </label>
                    <button
                        className="btn btn-primary"
                        onClick={runRegimeAnalysis}
                        disabled={isAnalyzing || isLoading}
                    >
                        {isAnalyzing ? <><CircularProgress size={16} color="inherit" /> Analyzing...</> : "Run Analysis"}
                    </button>
                </div>
            </div>

            {errorMsg && (
                <div className="alert alert-warning" style={{ padding: '8px 12px', fontSize: '13px' }}>
                    {errorMsg}
                </div>
            )}

            {/* Presets Bar */}
            <div style={{ backgroundColor: '#f8f9fa', padding: '10px 14px', borderRadius: '6px', marginBottom: '15px', border: '1px solid #e9ecef' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '12px', textTransform: 'uppercase', color: '#555' }}>
                            Quick Presets:
                        </span>
                        <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            style={{
                                fontSize: '11px',
                                padding: '1px 8px',
                                lineHeight: '18px',
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px'
                            }}
                            onClick={handleResetPresets}
                            disabled={conditions.length === 0 && !selectedPresetDate}
                            title="Reset all active preset conditions and date selection"
                        >
                            ↺ Reset
                        </button>
                    </div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <label htmlFor="market-symbol-select" style={{ margin: 0, fontSize: '12px', color: '#555', fontWeight: 'bold' }}>
                                Market Symbol:
                            </label>
                            <select
                                id="market-symbol-select"
                                className="form-control"
                                style={{ width: 'auto', display: 'inline-block', height: '26px', padding: '2px 8px', fontSize: '12px', lineHeight: '20px' }}
                                value={marketSymbol}
                                onChange={(e) => setMarketSymbol(e.target.value)}
                            >
                                {MARKET_SYMBOL_OPTIONS.map(val => (
                                    <option key={val} value={val}>{val}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                style={{ fontSize: '11px', padding: '1px 6px', lineHeight: '18px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                                onClick={() => handleOpenMarketChart(selectedPresetDate)}
                                title={`Open ${marketSymbol} candlestick chart${selectedPresetDate ? ` for ${normalizeDate(selectedPresetDate)}` : ''}`}
                            >
                                📈 Chart
                            </button>
                        </div>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <label htmlFor="bull-bear-ma-select" style={{ margin: 0, fontSize: '12px', color: '#555', fontWeight: 'bold' }}>
                                Bull/Bear MA Length:
                            </label>
                            <select
                                id="bull-bear-ma-select"
                                className="form-control"
                                style={{ width: 'auto', display: 'inline-block', height: '26px', padding: '2px 8px', fontSize: '12px', lineHeight: '20px' }}
                                value={bullBearMovingAverage}
                                onChange={(e) => setBullBearMovingAverage(Number(e.target.value))}
                            >
                                {BULL_BEAR_MA_OPTIONS.map(val => (
                                    <option key={val} value={val}>{val}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {PRESET_ROW_DEFINITIONS.map(rowDef => (
                        <div
                            key={rowDef.id}
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px',
                                alignItems: 'center',
                                ...(rowDef.id === 'analysis' ? {
                                    paddingTop: '6px',
                                    borderTop: '1px dashed #dee2e6'
                                } : {})
                            }}
                        >
                            {rowDef.groups.map(group => {
                                const groupPresets = group.presetNames
                                    .map(name => regimePresets.find(p => p.name === name))
                                    .filter(Boolean);
                                if (groupPresets.length === 0) return null;
                                return (
                                    <div
                                        key={group.id}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '6px',
                                            padding: '3px 6px',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: '11px',
                                                fontWeight: 'bold',
                                                color: '#495057',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.4px',
                                                whiteSpace: 'nowrap',
                                                padding: '0 2px'
                                            }}
                                        >
                                            {group.label}:
                                        </span>
                                        <div className="btn-group" role="group" aria-label={group.label}>
                                            {groupPresets.map(preset => {
                                                const active = isPresetActive(preset, conditions);
                                                return (
                                                    <button
                                                        key={preset.name}
                                                        type="button"
                                                        className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                        style={{
                                                            fontSize: '12px',
                                                            padding: '2px 8px',
                                                            lineHeight: '20px',
                                                            fontWeight: active ? 600 : 'normal',
                                                            boxShadow: active ? '0 1px 3px rgba(13,110,253,0.3)' : 'none',
                                                            transition: 'all 0.15s ease-in-out',
                                                            zIndex: active ? 2 : 1
                                                        }}
                                                        onClick={() => handleApplyPreset(preset)}
                                                        title={preset.description}
                                                    >
                                                        {preset.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}

                            {rowDef.id === 'analysis' && (
                                <div
                                    id="analysis-row"
                                    style={{
                                        display: 'inline-flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        gap: '6px',
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px',
                                        padding: '3px 8px',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                            color: '#495057',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.4px',
                                            whiteSpace: 'nowrap',
                                            padding: '0 2px'
                                        }}
                                    >
                                        Evaluate Date:
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        style={{ fontSize: '12px', padding: '2px 9px', lineHeight: '20px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                                        onClick={() => {
                                            setSelectedPresetDate(null);
                                            handleApplyDateConditions(null, true, false);
                                        }}
                                        disabled={isCheckingCurrent || activeEvalPresetNames.length === 0}
                                        title={activeEvalPresetNames.length === 0 ? "Please select at least one preset to evaluate" : "Evaluate selected quick presets against current market conditions and add matching conditions to rules"}
                                    >
                                        {isCheckingCurrent && checkingTargetDate === 'current' ? (
                                            <>
                                                <CircularProgress size={12} color="inherit" />
                                                <span>Checking...</span>
                                            </>
                                        ) : (
                                            'Current'
                                        )}
                                    </button>

                                    <span style={{ color: '#ccc', margin: '0 2px' }}>|</span>

                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            style={{
                                                fontSize: '13px',
                                                padding: '0',
                                                width: '24px',
                                                height: '26px',
                                                lineHeight: '24px',
                                                fontWeight: 'bold',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '4px'
                                            }}
                                            onClick={handlePrevMarketDay}
                                            disabled={isCheckingCurrent}
                                            title="Advance backward 1 market day (skip weekends)"
                                        >
                                            −
                                        </button>

                                        <div style={{ width: '125px' }}>
                                            <DatePicker
                                                selected={selectedPresetDate}
                                                onChange={(date) => setSelectedPresetDate(date)}
                                                placeholderText="Select Date..."
                                                dateFormat="yyyy-MM-dd"
                                                maxDate={new Date()}
                                                isClearable
                                                customInput={
                                                    <input
                                                        className="form-control"
                                                        style={{ width: '125px', height: '26px', fontSize: '12px', padding: '2px 8px', display: 'inline-block', cursor: 'pointer', lineHeight: '20px' }}
                                                    />
                                                }
                                            />
                                        </div>

                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            style={{
                                                fontSize: '13px',
                                                padding: '0',
                                                width: '24px',
                                                height: '26px',
                                                lineHeight: '24px',
                                                fontWeight: 'bold',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '4px'
                                            }}
                                            onClick={handleNextMarketDay}
                                            disabled={isNextDisabled || isCheckingCurrent}
                                            title="Advance forward 1 market day (skip weekends)"
                                        >
                                            +
                                        </button>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        style={{ fontSize: '12px', padding: '2px 9px', lineHeight: '20px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                                        onClick={() => handleApplyDateConditions(dateToAnalyze, true, false)}
                                        disabled={!dateToAnalyze || isCheckingCurrent || activeEvalPresetNames.length === 0}
                                        title={activeEvalPresetNames.length === 0 ? "Please select at least one preset to evaluate" : "Evaluate selected quick presets for the selected date and add matching conditions to rules"}
                                    >
                                        {isCheckingCurrent && checkingTargetDate === dateToAnalyze ? (
                                            <>
                                                <CircularProgress size={12} color="inherit" />
                                                <span>Analyzing...</span>
                                            </>
                                        ) : (
                                            'Analyze Date'
                                        )}
                                    </button>

                                    <span style={{ color: '#ccc', margin: '0 2px' }}>|</span>

                                    {/* Presets to Evaluate Selector Dropdown */}
                                    <div style={{ position: 'relative', display: 'inline-block' }} ref={evalFilterRef}>
                                        <button
                                            type="button"
                                            id="eval-presets-dropdown-btn"
                                            className="btn btn-sm"
                                            style={{
                                                fontSize: '12px',
                                                padding: '2px 8px',
                                                lineHeight: '20px',
                                                fontWeight: '600',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                backgroundColor: isAllEvalSelected ? '#ffffff' : '#eef6ff',
                                                borderColor: activeEvalPresetNames.length === 0 ? '#dc3545' : (isAllEvalSelected ? '#ced4da' : '#0d6efd'),
                                                color: activeEvalPresetNames.length === 0 ? '#dc3545' : (isAllEvalSelected ? '#495057' : '#0a58ca'),
                                                boxShadow: !isAllEvalSelected ? '0 1px 2px rgba(13,110,253,0.15)' : 'none'
                                            }}
                                            onClick={() => setIsEvalFilterOpen(prev => !prev)}
                                            title="Select which presets are evaluated for current market and date analysis"
                                        >
                                            <span>🎯 Presets:</span>
                                            <span
                                                className="badge"
                                                style={{
                                                    backgroundColor: activeEvalPresetNames.length === 0 ? '#dc3545' : (isAllEvalSelected ? '#6c757d' : '#0d6efd'),
                                                    color: '#ffffff',
                                                    fontSize: '11px',
                                                    padding: '2px 5px',
                                                    borderRadius: '10px'
                                                }}
                                            >
                                                {activeEvalPresetNames.length}/{allPresetNames.length}
                                            </span>
                                            <span style={{ fontSize: '9px', marginLeft: '1px' }}>{isEvalFilterOpen ? '▲' : '▼'}</span>
                                        </button>

                                        {isEvalFilterOpen && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: 'calc(100% + 6px)',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    zIndex: 1060,
                                                    backgroundColor: '#ffffff',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '8px',
                                                    boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
                                                    padding: '12px 14px',
                                                    width: '540px',
                                                    maxWidth: 'min(540px, 92vw)',
                                                    maxHeight: '480px',
                                                    overflowY: 'auto'
                                                }}
                                            >
                                                {/* Header */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', borderBottom: '1px solid #e9ecef', paddingBottom: '8px' }}>
                                                    <div>
                                                        <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#212529', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <span>🎯 Presets to Evaluate</span>
                                                            <span style={{ fontSize: '11px', fontWeight: 'bold', color: activeEvalPresetNames.length === 0 ? '#dc3545' : '#0d6efd' }}>
                                                                ({activeEvalPresetNames.length} of {allPresetNames.length} selected)
                                                            </span>
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
                                                            Select which presets are tested when running Current or Date analysis.
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary"
                                                            style={{ fontSize: '11px', padding: '1px 6px', lineHeight: '18px' }}
                                                            onClick={handleSelectAllEval}
                                                            title="Select all presets"
                                                        >
                                                            All
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary"
                                                            style={{ fontSize: '11px', padding: '1px 6px', lineHeight: '18px' }}
                                                            onClick={handleSelectCoreOnlyEval}
                                                            title="Select core groups (Market Cycle, VIX, Breadth)"
                                                        >
                                                            Core Only
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary"
                                                            style={{ fontSize: '11px', padding: '1px 6px', lineHeight: '18px' }}
                                                            onClick={handleClearAllEval}
                                                            title="Deselect all presets"
                                                        >
                                                            Clear
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn-close"
                                                            style={{ fontSize: '10px', marginLeft: '4px' }}
                                                            onClick={() => setIsEvalFilterOpen(false)}
                                                            aria-label="Close"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Preset Groups List */}
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
                                                    {PRESET_ROW_DEFINITIONS.filter(r => r.id !== 'analysis').map(row => (
                                                        <div key={row.id} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            {row.groups.map(group => {
                                                                const groupPresets = group.presetNames;
                                                                const selectedInGroup = groupPresets.filter(name => activeEvalPresetNames.includes(name));
                                                                const isAllInGroupSelected = selectedInGroup.length === groupPresets.length;

                                                                return (
                                                                    <div
                                                                        key={group.id}
                                                                        style={{
                                                                            padding: '6px 8px',
                                                                            backgroundColor: '#f8f9fa',
                                                                            borderRadius: '6px',
                                                                            border: '1px solid #e9ecef'
                                                                        }}
                                                                    >
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                                                            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                                                                {group.label}
                                                                                <span style={{ fontWeight: 'normal', color: '#6c757d', marginLeft: '5px' }}>
                                                                                    ({selectedInGroup.length}/{groupPresets.length})
                                                                                </span>
                                                                            </span>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-link btn-sm p-0"
                                                                                style={{ fontSize: '11px', textDecoration: 'none', color: '#0d6efd', fontWeight: '500' }}
                                                                                onClick={() => handleToggleGroupEval(groupPresets)}
                                                                            >
                                                                                {isAllInGroupSelected ? 'Deselect Group' : 'Select Group'}
                                                                            </button>
                                                                        </div>
                                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                            {groupPresets.map(presetName => {
                                                                                const isSelected = activeEvalPresetNames.includes(presetName);
                                                                                return (
                                                                                    <button
                                                                                        key={presetName}
                                                                                        type="button"
                                                                                        className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                                                        style={{
                                                                                            fontSize: '11px',
                                                                                            padding: '1px 8px',
                                                                                            lineHeight: '20px',
                                                                                            borderRadius: '4px',
                                                                                            fontWeight: isSelected ? '600' : 'normal',
                                                                                            backgroundColor: isSelected ? '#0d6efd' : '#ffffff',
                                                                                            color: isSelected ? '#ffffff' : '#6c757d',
                                                                                            borderColor: isSelected ? '#0d6efd' : '#ced4da',
                                                                                            display: 'inline-flex',
                                                                                            alignItems: 'center',
                                                                                            gap: '3px'
                                                                                        }}
                                                                                        onClick={() => handleTogglePresetEval(presetName)}
                                                                                    >
                                                                                        <span>{isSelected ? '✓' : '+'}</span>
                                                                                        <span>{presetName}</span>
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Footer */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: '1px solid #e9ecef', marginTop: '6px' }}>
                                                    <span style={{ fontSize: '11px', color: activeEvalPresetNames.length === 0 ? '#dc3545' : '#6c757d', fontWeight: activeEvalPresetNames.length === 0 ? '600' : 'normal' }}>
                                                        {activeEvalPresetNames.length === 0
                                                            ? '⚠️ Please select at least 1 preset'
                                                            : `${activeEvalPresetNames.length} preset${activeEvalPresetNames.length > 1 ? 's' : ''} will be evaluated`}
                                                    </span>
                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary btn-sm"
                                                            style={{ fontSize: '11px', padding: '2px 10px', fontWeight: 'bold' }}
                                                            onClick={() => {
                                                                setIsEvalFilterOpen(false);
                                                                if (dateToAnalyze) {
                                                                    handleApplyDateConditions(dateToAnalyze, true, false);
                                                                } else {
                                                                    handleApplyDateConditions(null, true, false);
                                                                }
                                                            }}
                                                            disabled={activeEvalPresetNames.length === 0 || isCheckingCurrent}
                                                            title={dateToAnalyze ? `Evaluate ${activeEvalPresetNames.length} presets for ${dateToAnalyze}` : `Evaluate ${activeEvalPresetNames.length} presets against current market`}
                                                        >
                                                            {dateToAnalyze ? `Evaluate (${dateToAnalyze})` : 'Evaluate Current'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary btn-sm"
                                                            style={{ fontSize: '11px', padding: '2px 8px' }}
                                                            onClick={() => setIsEvalFilterOpen(false)}
                                                        >
                                                            Done
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <span style={{ color: '#ccc', margin: '0 2px' }}>|</span>

                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        style={{ fontSize: '12px', padding: '2px 9px', lineHeight: '20px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                        onClick={() => handleOpenMarketChart(selectedPresetDate)}
                                        title={`Open ${marketSymbol} candlestick chart${selectedPresetDate ? ` with ${normalizeDate(selectedPresetDate)} highlighted` : ''}`}
                                    >
                                        📈 {marketSymbol} Chart
                                    </button>

                                    <span style={{ color: '#ccc', margin: '0 2px' }}>|</span>

                                    <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm"
                                        style={{ fontSize: '12px', padding: '2px 9px', lineHeight: '20px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                        onClick={handleResetPresets}
                                        disabled={conditions.length === 0 && !selectedPresetDate}
                                        title="Reset all active preset conditions and date selection"
                                    >
                                        ↺ Reset
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {currentStatusMsg && (
                <div className="alert alert-success" style={{ padding: '8px 12px', fontSize: '13px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>✅ {currentStatusMsg}</span>
                    <button type="button" className="btn-close" style={{ padding: '4px' }} onClick={() => setCurrentStatusMsg(null)} aria-label="Close" />
                </div>
            )}

            {/* Market Regime Trajectory Card */}
            <div style={{
                backgroundColor: '#ffffff',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '16px 20px',
                marginBottom: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}>
                {/* Card Header & Controls */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: isTrajectoryExpanded ? '14px' : '0',
                    paddingBottom: isTrajectoryExpanded ? '12px' : '0',
                    borderBottom: isTrajectoryExpanded ? '1px solid #f0f0f0' : 'none'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#212529', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            📈 Market Regime Trajectory
                        </span>
                        <span className="badge bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: '11px', padding: '3px 8px' }}>
                            Last {trajectoryLookbackDays} Market Days
                        </span>
                        <span style={{ fontSize: '12px', color: '#6c757d' }}>
                            Ending: <strong>{dateToAnalyze ? tableHelpers.dateFormmatter(dateToAnalyze) : 'Latest Market Close'}</strong>
                        </span>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', fontSize: '12px' }}>
                        {/* Lookback Days dropdown */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontWeight: 600, color: '#495057' }}>Days:</span>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: '95px', height: '28px', fontSize: '12px', padding: '2px 8px', cursor: 'pointer' }}
                                value={trajectoryLookbackDays}
                                onChange={(e) => setTrajectoryLookbackDays(parseInt(e.target.value, 10) || 22)}
                            >
                                <option value="10">10 days</option>
                                <option value="22">22 days</option>
                                <option value="44">44 days</option>
                                <option value="66">66 days</option>
                                <option value="126">126 days</option>
                                <option value="252">252 days</option>
                            </select>
                        </div>

                        {/* Left Axis Metric dropdown */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontWeight: 'bold', color: '#0d6efd' }}>Left Axis:</span>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: '185px', height: '28px', fontSize: '12px', padding: '2px 8px', cursor: 'pointer', borderColor: '#0d6efd' }}
                                value={trajectoryLeftMetric}
                                onChange={(e) => setTrajectoryLeftMetric(e.target.value)}
                            >
                                {TRAJECTORY_METRICS.map(m => (
                                    <option key={m.key} value={m.key}>{m.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Right Axis Metric dropdown */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontWeight: 'bold', color: '#198754' }}>Right Axis:</span>
                            <select
                                className="form-select form-select-sm"
                                style={{ width: '185px', height: '28px', fontSize: '12px', padding: '2px 8px', cursor: 'pointer', borderColor: '#198754' }}
                                value={trajectoryRightMetric}
                                onChange={(e) => setTrajectoryRightMetric(e.target.value)}
                            >
                                {TRAJECTORY_METRICS.map(m => (
                                    <option key={m.key} value={m.key}>{m.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Chart Style Toggle */}
                        <div className="btn-group" role="group">
                            <button
                                type="button"
                                className={`btn btn-sm ${trajectoryChartStyle === 'bars_line' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                style={{ fontSize: '11px', padding: '2px 8px', lineHeight: '20px' }}
                                onClick={() => setTrajectoryChartStyle('bars_line')}
                                title="Bars for left metric, line overlay for right metric"
                            >
                                Bars + Line
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${trajectoryChartStyle === 'both_lines' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                style={{ fontSize: '11px', padding: '2px 8px', lineHeight: '20px' }}
                                onClick={() => setTrajectoryChartStyle('both_lines')}
                                title="Dual lines with points"
                            >
                                Dual Lines
                            </button>
                        </div>

                        {/* Refresh / Recalculate button */}
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            style={{ fontSize: '12px', padding: '2px 10px', lineHeight: '20px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            onClick={() => {
                                setIsTrajectoryExpanded(true);
                                runRecentDaysTrajectory(true);
                            }}
                            disabled={isCalculatingTrajectory}
                            title="Recalculate recent days preset evaluation and regime metrics"
                        >
                            {isCalculatingTrajectory ? (
                                <>
                                    <CircularProgress size={12} color="inherit" />
                                    <span>Analyzing...</span>
                                </>
                            ) : (
                                <>↻ Refresh</>
                            )}
                        </button>

                        {/* Expand / Collapse toggle */}
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            style={{ fontSize: '11px', padding: '2px 8px', lineHeight: '20px', display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                            onClick={() => setIsTrajectoryExpanded(prev => !prev)}
                            title={isTrajectoryExpanded ? "Collapse trajectory chart" : "Expand trajectory chart"}
                        >
                            <span>{isTrajectoryExpanded ? '▲ Hide Chart' : '▼ Show Chart'}</span>
                        </button>
                    </div>
                </div>

                {isTrajectoryExpanded && (
                    <>
                        {/* Trend Summary Strip */}
                        {trajectoryTrendSummary && (
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #e9ecef',
                                borderRadius: '6px',
                                padding: '8px 14px',
                                marginBottom: '14px',
                                gap: '12px',
                                fontSize: '12px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span style={{ color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', fontSize: '11px' }}>
                                        Active Regime ({tableHelpers.dateFormmatter(trajectoryTrendSummary.latestDateStr)}):
                                    </span>
                                    <span style={{ fontWeight: 'bold', color: '#212529', backgroundColor: '#e9ecef', padding: '2px 8px', borderRadius: '4px' }}>
                                        {trajectoryTrendSummary.latestRegimeName}
                                    </span>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                    <div>
                                        <span style={{ color: '#0d6efd', fontWeight: 600 }}>{trajectoryTrendSummary.leftMetricObj.label}: </span>
                                        <span style={{ fontWeight: 'bold' }}>
                                            {formatMetricDisplay(trajectoryTrendSummary.latestLeftVal, trajectoryTrendSummary.leftMetricObj.format)}
                                        </span>
                                        <span style={{
                                            marginLeft: '5px',
                                            fontSize: '11px',
                                            color: trajectoryTrendSummary.deltaLeft >= 0 ? '#198754' : '#dc3545',
                                            fontWeight: 600
                                        }}>
                                            ({trajectoryTrendSummary.deltaLeft >= 0 ? '+' : ''}{trajectoryTrendSummary.deltaLeft.toFixed(1)}{trajectoryTrendSummary.leftMetricObj.format === 'percent' ? '%' : ''})
                                        </span>
                                    </div>

                                    <div>
                                        <span style={{ color: '#198754', fontWeight: 600 }}>{trajectoryTrendSummary.rightMetricObj.label}: </span>
                                        <span style={{ fontWeight: 'bold' }}>
                                            {formatMetricDisplay(trajectoryTrendSummary.latestRightVal, trajectoryTrendSummary.rightMetricObj.format)}
                                        </span>
                                        <span style={{
                                            marginLeft: '5px',
                                            fontSize: '11px',
                                            color: trajectoryTrendSummary.deltaRight >= 0 ? '#198754' : '#dc3545',
                                            fontWeight: 600
                                        }}>
                                            ({trajectoryTrendSummary.deltaRight >= 0 ? '+' : ''}{trajectoryTrendSummary.deltaRight.toFixed(2)})
                                        </span>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <span style={{ color: '#6c757d', fontWeight: 600 }}>Market Trajectory:</span>
                                        <span className={trajectoryTrendSummary.trendBadge} style={{ fontSize: '11px', padding: '3px 8px', fontWeight: 700 }}>
                                            {trajectoryTrendSummary.trendIcon} {trajectoryTrendSummary.trendLabel}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Chart Area */}
                        {isCalculatingTrajectory && (!trajectoryData || trajectoryData.length === 0) ? (
                            <div style={{ textAlign: 'center', padding: '50px 20px', color: '#6c757d' }}>
                                <CircularProgress size={28} />
                                <div style={{ marginTop: '10px', fontSize: '13px' }}>
                                    Evaluating preset conditions and regime metrics across recent {trajectoryLookbackDays} market days...
                                </div>
                            </div>
                        ) : trajectoryError ? (
                            <div className="alert alert-danger" style={{ padding: '10px 14px', fontSize: '12px' }}>
                                ⚠️ {trajectoryError}
                            </div>
                        ) : trajectoryChartData && trajectoryChartData.length > 1 ? (
                            <div>
                                <Chart
                                    chartType="ComboChart"
                                    width="100%"
                                    height="320px"
                                    data={trajectoryChartData}
                                    options={trajectoryChartOptions}
                                    chartEvents={trajectoryChartEvents}
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', fontSize: '11px', color: '#888' }}>
                                    <span>💡 Tip: Click any day on the chart to set the evaluate date and inspect matching rules.</span>
                                    <span>Left Axis: <strong style={{ color: '#0d6efd' }}>{TRAJECTORY_METRICS.find(m => m.key === trajectoryLeftMetric)?.label}</strong> | Right Axis: <strong style={{ color: '#198754' }}>{TRAJECTORY_METRICS.find(m => m.key === trajectoryRightMetric)?.label}</strong></span>
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888', fontStyle: 'italic', fontSize: '13px' }}>
                                No recent trajectory data available for the selected date. Click "Analyze Date" or "Refresh" to calculate.
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Condition Builder Card (Accordion / Collapsible, defaults to hidden) */}
            <div style={{ backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '6px', padding: '12px 15px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isConditionsExpanded ? '12px' : '0', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <h5 style={{ margin: 0, fontSize: '15px' }}>Regime Conditions (ALL must match)</h5>
                        <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle" style={{ fontSize: '11px', padding: '2px 6px' }}>
                            {conditions.length} rule{conditions.length === 1 ? '' : 's'}
                        </span>
                        {conditions.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                style={{ fontSize: '11px', padding: '1px 7px' }}
                                onClick={() => setConditions([])}
                                title="Clear all active rules"
                            >
                                Clear All
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '14px', fontSize: '13px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} title="Number of consecutive trading days conditions must match before entering a regime (1 = enters immediately on first day)">
                            <span style={{ fontWeight: 500, color: '#495057' }}>Consecutive Days to Enter:</span>
                            <input
                                type="number"
                                min="1"
                                max="30"
                                value={consecutiveDaysToEnter}
                                onChange={(e) => setConsecutiveDaysToEnter(Math.max(1, parseInt(e.target.value, 10) || 1))}
                                style={{ width: '50px', padding: '2px 5px', textAlign: 'center', borderRadius: '4px', border: '1px solid #ced4da' }}
                            />
                            <span style={{ color: '#6c757d' }}>days</span>
                        </div>
                        <div style={{ width: '1px', height: '18px', backgroundColor: '#dee2e6' }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} title="Once entered, the minimum number of days the strategy remains in that regime before checking for an exit condition">
                            <span style={{ fontWeight: 500, color: '#495057' }}>Min Regime Duration:</span>
                            <input
                                type="number"
                                min="1"
                                max="90"
                                value={minDuration}
                                onChange={(e) => setMinDuration(Math.max(1, parseInt(e.target.value, 10) || 1))}
                                style={{ width: '50px', padding: '2px 5px', textAlign: 'center', borderRadius: '4px', border: '1px solid #ced4da' }}
                            />
                            <span style={{ color: '#6c757d' }}>days</span>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            style={{ fontSize: '12px', padding: '2px 10px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                            onClick={() => setIsConditionsExpanded(prev => !prev)}
                            title={isConditionsExpanded ? "Hide regime conditions and rule editor" : "Show regime conditions and rule editor"}
                        >
                            <span>{isConditionsExpanded ? '▲ Hide Rules' : '▼ Show Rules & Editor'}</span>
                        </button>
                    </div>
                </div>

                {!isConditionsExpanded ? (
                    <div style={{ fontSize: '12px', color: '#6c757d', fontStyle: 'italic', paddingTop: '4px' }}>
                        {conditions.length === 0
                            ? 'No conditions active. Click "Show Rules & Editor" to build custom rules, or click any preset above.'
                            : `${conditions.length} active rule${conditions.length > 1 ? 's' : ''}: ${conditions.map(c => `${c.symbol} ${c.field1}`).join(', ')}. Click "Show Rules & Editor" to inspect or edit.`}
                    </div>
                ) : (
                    <>
                        {/* Active conditions list */}
                        <div style={{ marginBottom: '12px' }}>
                            {conditions.length === 0 ? (
                                <div style={{ color: '#888', fontStyle: 'italic', padding: '8px 4px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {isCheckingCurrent && checkingTargetDate === 'current' ? (
                                        <>
                                            <CircularProgress size={14} />
                                            <span>Analyzing current market conditions...</span>
                                        </>
                                    ) : (
                                        <span>No conditions defined. Click a preset above or add a rule below.</span>
                                    )}
                                </div>
                            ) : (
                                conditions.map((cond, index) => (
                                    <div
                                        key={cond.id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            backgroundColor: '#f1f3f5',
                                            padding: '6px 12px',
                                            borderRadius: '4px',
                                            marginBottom: '6px',
                                            fontSize: '13px',
                                            gap: '8px',
                                            flexWrap: 'wrap'
                                        }}
                                    >
                                        <span className="badge bg-secondary" style={{ fontSize: '10px' }}>Rule {index + 1}</span>
                                        <span style={{ fontWeight: 600 }}>
                                            {formatConditionOperand(cond.symbol, cond.field1, cond.config1, cond.subField1, cond.period1)}
                                        </span>
                                        <span style={{ fontWeight: 'bold', color: '#0d6efd' }}>{cond.operator}</span>
                                        {cond.compareType === 'value' ? (
                                            <span style={{ fontWeight: 600 }}>{cond.value}</span>
                                        ) : (
                                            <span style={{ fontWeight: 600 }}>
                                                {formatConditionOperand(cond.compareSymbol, cond.field2, cond.config2, cond.subField2, cond.period2)}
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveCondition(cond.id)}
                                            style={{
                                                marginLeft: 'auto',
                                                background: 'none',
                                                border: 'none',
                                                color: '#dc3545',
                                                cursor: 'pointer',
                                                fontWeight: 'bold'
                                            }}
                                            title="Remove Condition"
                                        >
                                            ❌
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Add new condition row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', paddingTop: '10px', borderTop: '1px solid #f1f3f5', fontSize: '13px' }}>
                            <span style={{ fontWeight: 'bold' }}>Add Rule:</span>
                            <select value={newSymbol} onChange={(e) => setNewSymbol(e.target.value)} style={{ padding: '3px 6px' }}>
                                {symbols.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>

                            <select value={newField1} onChange={(e) => handleField1Change(e.target.value)} style={{ padding: '3px 6px' }}>
                                <optgroup label="Price Fields">
                                    {PRICE_FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
                                </optgroup>
                                <optgroup label="Indicators">
                                    {apiIndicators.map(i => <option key={i.type} value={i.type}>{i.type}</option>)}
                                </optgroup>
                            </select>

                            {INDICATOR_SUBFIELDS[newField1] && (
                                <select
                                    value={newSubField1}
                                    onChange={(e) => setNewSubField1(e.target.value)}
                                    style={{ padding: '3px 6px', fontSize: '12px', borderColor: '#0d6efd' }}
                                    title="Select Output / Band"
                                >
                                    {INDICATOR_SUBFIELDS[newField1].map(sf => (
                                        <option key={sf.key} value={sf.key}>{sf.label}</option>
                                    ))}
                                </select>
                            )}

                            {!PRICE_FIELDS.includes(newField1) && (
                                <IndicatorConfigInputs
                                    indicatorType={newField1}
                                    config={newConfig1}
                                    onChange={handleConfig1Change}
                                    indicatorDef={apiIndicators.find(i => i.type === newField1)}
                                    patterns={patterns}
                                />
                            )}

                            <select value={newOperator} onChange={(e) => setNewOperator(e.target.value)} style={{ padding: '3px 6px', fontWeight: 'bold' }}>
                                <option value=">">&gt;</option>
                                <option value="<">&lt;</option>
                                <option value=">=">&gt;=</option>
                                <option value="<=">&lt;=</option>
                                <option value="=">=</option>
                                <option value="<>">&lt;&gt;</option>
                            </select>

                            <select value={newCompareType} onChange={(e) => setNewCompareType(e.target.value)} style={{ padding: '3px 6px' }}>
                                <option value="value">Numeric Value</option>
                                <option value="indicator">Stock / Indicator</option>
                            </select>

                            {newCompareType === 'value' ? (
                                <input
                                    type="number"
                                    step="any"
                                    value={newValue}
                                    onChange={(e) => setNewValue(e.target.value)}
                                    style={{ width: '80px', padding: '3px 6px' }}
                                    placeholder="Value"
                                />
                            ) : (
                                <>
                                    <select value={newCompareSymbol} onChange={(e) => setNewCompareSymbol(e.target.value)} style={{ padding: '3px 6px' }}>
                                        {symbols.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                    <select value={newField2} onChange={(e) => handleField2Change(e.target.value)} style={{ padding: '3px 6px' }}>
                                        <optgroup label="Price Fields">
                                            {PRICE_FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
                                        </optgroup>
                                        <optgroup label="Indicators">
                                            {apiIndicators.map(i => <option key={i.type} value={i.type}>{i.type}</option>)}
                                        </optgroup>
                                    </select>

                                    {INDICATOR_SUBFIELDS[newField2] && (
                                        <select
                                            value={newSubField2}
                                            onChange={(e) => setNewSubField2(e.target.value)}
                                            style={{ padding: '3px 6px', fontSize: '12px', borderColor: '#0d6efd' }}
                                            title="Select Output / Band"
                                        >
                                            {INDICATOR_SUBFIELDS[newField2].map(sf => (
                                                <option key={sf.key} value={sf.key}>{sf.label}</option>
                                            ))}
                                        </select>
                                    )}

                                    {!PRICE_FIELDS.includes(newField2) && (
                                        <IndicatorConfigInputs
                                            indicatorType={newField2}
                                            config={newConfig2}
                                            onChange={handleConfig2Change}
                                            indicatorDef={apiIndicators.find(i => i.type === newField2)}
                                            patterns={patterns}
                                        />
                                    )}
                                </>
                            )}

                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={handleAddCondition}
                                style={{ marginLeft: '4px' }}
                            >
                                + Add Rule
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Results Section */}
            {isLoading || isAnalyzing ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <CircularProgress />
                    <div style={{ marginTop: '10px', color: '#666' }}>
                        {isLoading ? "Fetching backtest history..." : "Evaluating regime indicators and backtest results..."}
                    </div>
                </div>
            ) : zoomedResults ? (
                <>
                    {/* Timeframe & Zoom Bar */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#f8f9fa',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        marginBottom: '15px',
                        border: '1px solid #e9ecef',
                        flexWrap: 'wrap',
                        gap: '8px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 'bold', fontSize: '12px', textTransform: 'uppercase', color: '#555', marginRight: '4px' }}>
                                📅 Timeframe Zoom:
                            </span>
                            <button
                                type="button"
                                className={`btn btn-sm ${selectedTimeframe === 'All' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                style={{ fontSize: '12px', padding: '2px 8px' }}
                                onClick={() => handleTimeframeSelect('All')}
                            >
                                All
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${selectedTimeframe === 'YTD' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                style={{ fontSize: '12px', padding: '2px 8px' }}
                                onClick={() => handleTimeframeSelect('YTD')}
                            >
                                YTD
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${selectedTimeframe === '1Y' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                style={{ fontSize: '12px', padding: '2px 8px' }}
                                onClick={() => handleTimeframeSelect('1Y')}
                            >
                                1Y
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${selectedTimeframe === '3Y' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                style={{ fontSize: '12px', padding: '2px 8px' }}
                                onClick={() => handleTimeframeSelect('3Y')}
                            >
                                3Y
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${selectedTimeframe === '5Y' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                style={{ fontSize: '12px', padding: '2px 8px' }}
                                onClick={() => handleTimeframeSelect('5Y')}
                            >
                                5Y
                            </button>

                            <span style={{ color: '#ccc', margin: '0 4px' }}>|</span>

                            {/* Individual Year buttons down to year granularity */}
                            {availableYears.map(yr => (
                                <button
                                    key={yr}
                                    type="button"
                                    className={`btn btn-sm ${selectedTimeframe === yr.toString() ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    style={{ fontSize: '12px', padding: '2px 8px' }}
                                    onClick={() => handleTimeframeSelect(yr.toString())}
                                >
                                    {yr}
                                </button>
                            ))}

                            <span style={{ color: '#ccc', margin: '0 4px' }}>|</span>

                            {/* Custom Date Range */}
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ fontSize: '12px', color: '#666' }}>Custom:</span>
                                <input
                                    type="date"
                                    value={customStartDate}
                                    onChange={(e) => setCustomStartDate(e.target.value)}
                                    style={{ fontSize: '11px', padding: '1px 5px', border: '1px solid #ced4da', borderRadius: '4px' }}
                                    title="Custom Start Date"
                                />
                                <span style={{ fontSize: '11px', color: '#888' }}>to</span>
                                <input
                                    type="date"
                                    value={customEndDate}
                                    onChange={(e) => setCustomEndDate(e.target.value)}
                                    style={{ fontSize: '11px', padding: '1px 5px', border: '1px solid #ced4da', borderRadius: '4px' }}
                                    title="Custom End Date"
                                />
                                <button
                                    type="button"
                                    className={`btn btn-sm ${selectedTimeframe === 'Custom' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    style={{ fontSize: '11px', padding: '1px 7px' }}
                                    onClick={handleApplyCustomDates}
                                    disabled={!customStartDate}
                                >
                                    Apply
                                </button>
                            </div>
                        </div>

                        {selectedTimeframe !== 'All' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                                <span style={{ color: '#0d6efd', fontWeight: 'bold' }}>
                                    Zoomed: {zoomLabel} ({zoomedResults.totalDays} trading days)
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    style={{ fontSize: '11px', padding: '1px 6px' }}
                                    onClick={() => handleTimeframeSelect('All')}
                                >
                                    Reset to Full
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Top KPI Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                        <div style={{ backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '6px', padding: '12px', borderLeft: '4px solid #0d6efd' }}>
                            <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>Time in Regime</div>
                            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                {zoomedResults.inDays} <span style={{ fontSize: '13px', fontWeight: 'normal', color: '#666' }}>days ({tableHelpers.percentFormatter(zoomedResults.inPct)})</span>
                            </div>
                            <div style={{ fontSize: '11px', color: '#888' }}>
                                Out of {zoomedResults.totalDays} days in {zoomLabel}
                            </div>
                            <div style={{ fontSize: '11px', color: '#555', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #f0f0f0' }}>
                                <div>Avg Days in Regime: <strong>{zoomedResults.avgRegimeDays.toFixed(1)} days</strong></div>
                                <div style={{ marginTop: '2px' }}>Regime Changes: <strong>{zoomedResults.regimeChanges}</strong> ({zoomedResults.regimeEpisodes.length} episode{zoomedResults.regimeEpisodes.length === 1 ? '' : 's'})</div>
                            </div>
                        </div>

                        <div style={{ backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '6px', padding: '12px', borderLeft: '4px solid #198754' }}>
                            <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>In-Regime Return</div>
                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: zoomedResults.inPlanStats.cumulativeReturn >= 0 ? '#198754' : '#dc3545' }}>
                                {tableHelpers.percentChangeFormatter(zoomedResults.inPlanStats.cumulativeReturn)}
                            </div>
                            <div style={{ fontSize: '11px', color: '#666' }}>
                                SPY: {tableHelpers.percentChangeFormatter(zoomedResults.inSpyStats.cumulativeReturn)}
                            </div>
                            <div style={{ fontSize: '11px', color: '#555', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #f0f0f0' }}>
                                <div>1W Return: <strong style={{ color: forward1W && forward1W.count > 0 && forward1W.avgPlanReturn >= 0 ? '#198754' : (forward1W && forward1W.count > 0 ? '#dc3545' : 'inherit') }}>{formatForwardRet(forward1W?.avgPlanReturn, forward1W?.count)}</strong> | SPY: <strong>{formatForwardRet(forward1W?.avgSpyReturn, forward1W?.count)}</strong></div>
                                <div style={{ marginTop: '2px' }}>2W Return: <strong style={{ color: forward2W && forward2W.count > 0 && forward2W.avgPlanReturn >= 0 ? '#198754' : (forward2W && forward2W.count > 0 ? '#dc3545' : 'inherit') }}>{formatForwardRet(forward2W?.avgPlanReturn, forward2W?.count)}</strong> | SPY: <strong>{formatForwardRet(forward2W?.avgSpyReturn, forward2W?.count)}</strong></div>
                                <div style={{ marginTop: '2px' }}>1W Win Rate: <strong style={{ color: forward1W && forward1W.count > 0 && forward1W.planWinRate >= 50 ? '#198754' : (forward1W && forward1W.count > 0 ? '#dc3545' : 'inherit') }}>{formatForwardWinRate(forward1W?.planWinRate, forward1W?.count)}</strong> | SPY: <strong>{formatForwardWinRate(forward1W?.spyWinRate, forward1W?.count)}</strong></div>
                                <div style={{ marginTop: '2px' }}>2W Win Rate: <strong style={{ color: forward2W && forward2W.count > 0 && forward2W.planWinRate >= 50 ? '#198754' : (forward2W && forward2W.count > 0 ? '#dc3545' : 'inherit') }}>{formatForwardWinRate(forward2W?.planWinRate, forward2W?.count)}</strong> | SPY: <strong>{formatForwardWinRate(forward2W?.spyWinRate, forward2W?.count)}</strong></div>
                            </div>
                        </div>

                        <div style={{ backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '6px', padding: '12px', borderLeft: '4px solid #dc3545' }}>
                            <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>In-Regime Max Drawdown</div>
                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#dc3545' }}>
                                -{tableHelpers.percentFormatter(zoomedResults.inPlanStats.maxDrawdown)}
                            </div>
                            <div style={{ fontSize: '11px', color: '#666' }}>
                                SPY: -{tableHelpers.percentFormatter(zoomedResults.inSpyStats.maxDrawdown)}
                            </div>
                            <div style={{ fontSize: '11px', color: '#555', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #f0f0f0' }}>
                                <div>1W Avg DD: <strong style={{ color: '#dc3545' }}>{formatForwardDd(forward1W?.avgMaxDdPlan, forward1W?.count)}</strong> | SPY: <strong>{formatForwardDd(forward1W?.avgMaxDdSpy, forward1W?.count)}</strong></div>
                                <div style={{ marginTop: '2px' }}>1W Peak DD: <strong style={{ color: '#dc3545' }}>{formatForwardDd(forward1W?.worstDdPlan, forward1W?.count)}</strong> | SPY: <strong>{formatForwardDd(forward1W?.worstDdSpy, forward1W?.count)}</strong></div>
                                <div style={{ marginTop: '2px' }}>2W Avg DD: <strong style={{ color: '#dc3545' }}>{formatForwardDd(forward2W?.avgMaxDdPlan, forward2W?.count)}</strong> | SPY: <strong>{formatForwardDd(forward2W?.avgMaxDdSpy, forward2W?.count)}</strong></div>
                                <div style={{ marginTop: '2px' }}>2W Peak DD: <strong style={{ color: '#dc3545' }}>{formatForwardDd(forward2W?.worstDdPlan, forward2W?.count)}</strong> | SPY: <strong>{formatForwardDd(forward2W?.worstDdSpy, forward2W?.count)}</strong></div>
                            </div>
                        </div>

                        <div style={{ backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '6px', padding: '12px', borderLeft: '4px solid #6f42c1' }}>
                            <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>In-Regime Sharpe Ratio</div>
                            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                {tableHelpers.decimalFormatter(zoomedResults.inPlanStats.sharpe)}
                            </div>
                            <div style={{ fontSize: '11px', color: '#666' }}>
                                SPY: {tableHelpers.decimalFormatter(zoomedResults.inSpyStats.sharpe)} | Out: {tableHelpers.decimalFormatter(zoomedResults.outPlanStats.sharpe)}
                            </div>
                        </div>

                        <div style={{
                            backgroundColor: '#fff',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            padding: '12px',
                            borderLeft: `4px solid ${efficiencyComparison?.badgeColor || '#20c997'}`
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>Capital Efficiency</div>
                                {efficiencyComparison?.badge && (
                                    <span style={{
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        padding: '1px 5px',
                                        borderRadius: '3px',
                                        backgroundColor: efficiencyComparison.badgeColor,
                                        color: '#fff'
                                    }}>
                                        {efficiencyComparison.badge}
                                    </span>
                                )}
                            </div>
                            <div style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: efficiencyComparison?.badgeColor || '#20c997',
                                marginTop: '2px'
                            }}>
                                {efficiencyComparison?.ratioDisplay}
                            </div>
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }} title="Annualized return per 100% active capital exposure: (Avg Daily Return × 252) / (Avg Exposure / 100)">
                                {efficiencyComparison?.subtitle}
                            </div>
                        </div>
                    </div>

                    {/* Comparative Performance Scorecard Table */}
                    <div style={{ backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h5 style={{ margin: 0, fontSize: '15px' }}>
                                Comparative Performance Scorecard {selectedTimeframe !== 'All' ? `(${zoomLabel})` : ''}
                            </h5>
                            <span style={{ fontSize: '12px', color: '#666' }}>
                                {zoomedResults.totalDays} Trading Days Analyzed
                            </span>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-bordered table-striped table-hover" style={{ fontSize: '13px', margin: 0 }}>
                                <thead style={{ backgroundColor: '#f8f9fa' }}>
                                    <tr>
                                        <th style={{ width: '22%' }}>Performance Metric</th>
                                        <th style={{ width: '26%', textAlign: 'center', backgroundColor: '#e8f4fd' }}>
                                            In-Regime (Plan / SPY)
                                        </th>
                                        <th style={{ width: '26%', textAlign: 'center' }}>
                                            Out-of-Regime (Plan / SPY)
                                        </th>
                                        <th style={{ width: '26%', textAlign: 'center', backgroundColor: '#fafafa' }}>
                                            Period Total (Plan / SPY)
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><strong>Cumulative Return</strong></td>
                                        <td style={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#e8f4fd' }}>
                                            <span style={{ color: zoomedResults.inPlanStats.cumulativeReturn >= 0 ? '#198754' : '#dc3545' }}>
                                                {tableHelpers.percentChangeFormatter(zoomedResults.inPlanStats.cumulativeReturn)}
                                            </span>
                                            <span style={{ color: '#888' }}> / </span>
                                            <span style={{ color: zoomedResults.inSpyStats.cumulativeReturn >= 0 ? '#198754' : '#dc3545' }}>
                                                {tableHelpers.percentChangeFormatter(zoomedResults.inSpyStats.cumulativeReturn)}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span style={{ color: zoomedResults.outPlanStats.cumulativeReturn >= 0 ? '#198754' : '#dc3545' }}>
                                                {tableHelpers.percentChangeFormatter(zoomedResults.outPlanStats.cumulativeReturn)}
                                            </span>
                                            <span style={{ color: '#888' }}> / </span>
                                            <span style={{ color: zoomedResults.outSpyStats.cumulativeReturn >= 0 ? '#198754' : '#dc3545' }}>
                                                {tableHelpers.percentChangeFormatter(zoomedResults.outSpyStats.cumulativeReturn)}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#fafafa' }}>
                                            <span style={{ color: zoomedResults.allPlanStats.cumulativeReturn >= 0 ? '#198754' : '#dc3545' }}>
                                                {tableHelpers.percentChangeFormatter(zoomedResults.allPlanStats.cumulativeReturn)}
                                            </span>
                                            <span style={{ color: '#888' }}> / </span>
                                            <span style={{ color: zoomedResults.allSpyStats.cumulativeReturn >= 0 ? '#198754' : '#dc3545' }}>
                                                {tableHelpers.percentChangeFormatter(zoomedResults.allSpyStats.cumulativeReturn)}
                                            </span>
                                        </td>
                                    </tr>

                                    <tr>
                                        <td><strong>Annualized Return (CAGR)</strong></td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#e8f4fd' }}>
                                            {tableHelpers.percentChangeFormatter(zoomedResults.inPlanStats.cagr)} / {tableHelpers.percentChangeFormatter(zoomedResults.inSpyStats.cagr)}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {tableHelpers.percentChangeFormatter(zoomedResults.outPlanStats.cagr)} / {tableHelpers.percentChangeFormatter(zoomedResults.outSpyStats.cagr)}
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#fafafa' }}>
                                            {tableHelpers.percentChangeFormatter(zoomedResults.allPlanStats.cagr)} / {tableHelpers.percentChangeFormatter(zoomedResults.allSpyStats.cagr)}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td><strong>Max Drawdown</strong></td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#e8f4fd', color: '#dc3545' }}>
                                            -{tableHelpers.percentFormatter(zoomedResults.inPlanStats.maxDrawdown)} / -{tableHelpers.percentFormatter(zoomedResults.inSpyStats.maxDrawdown)}
                                        </td>
                                        <td style={{ textAlign: 'center', color: '#dc3545' }}>
                                            -{tableHelpers.percentFormatter(zoomedResults.outPlanStats.maxDrawdown)} / -{tableHelpers.percentFormatter(zoomedResults.outSpyStats.maxDrawdown)}
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#fafafa', color: '#dc3545' }}>
                                            -{tableHelpers.percentFormatter(zoomedResults.allPlanStats.maxDrawdown)} / -{tableHelpers.percentFormatter(zoomedResults.allSpyStats.maxDrawdown)}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td><strong>Avg Drawdown</strong></td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#e8f4fd', color: '#dc3545' }}>
                                            -{tableHelpers.percentFormatter(zoomedResults.inPlanStats.avgDrawdown)} / -{tableHelpers.percentFormatter(zoomedResults.inSpyStats.avgDrawdown)}
                                        </td>
                                        <td style={{ textAlign: 'center', color: '#dc3545' }}>
                                            -{tableHelpers.percentFormatter(zoomedResults.outPlanStats.avgDrawdown)} / -{tableHelpers.percentFormatter(zoomedResults.outSpyStats.avgDrawdown)}
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#fafafa', color: '#dc3545' }}>
                                            -{tableHelpers.percentFormatter(zoomedResults.allPlanStats.avgDrawdown)} / -{tableHelpers.percentFormatter(zoomedResults.allSpyStats.avgDrawdown)}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td><strong>Sharpe Ratio (Annualized)</strong></td>
                                        <td style={{ textAlign: 'center', fontWeight: 'bold', backgroundColor: '#e8f4fd' }}>
                                            {tableHelpers.decimalFormatter(zoomedResults.inPlanStats.sharpe)} / {tableHelpers.decimalFormatter(zoomedResults.inSpyStats.sharpe)}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {tableHelpers.decimalFormatter(zoomedResults.outPlanStats.sharpe)} / {tableHelpers.decimalFormatter(zoomedResults.outSpyStats.sharpe)}
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#fafafa' }}>
                                            {tableHelpers.decimalFormatter(zoomedResults.allPlanStats.sharpe)} / {tableHelpers.decimalFormatter(zoomedResults.allSpyStats.sharpe)}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td><strong>Time Spent (Days)</strong></td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#e8f4fd' }}>
                                            <div>{zoomedResults.inDays} days ({tableHelpers.percentFormatter(zoomedResults.inPct)})</div>
                                            <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>Avg: {zoomedResults.avgRegimeDays.toFixed(1)} days/episode</div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div>{zoomedResults.outDays} days ({tableHelpers.percentFormatter(100 - zoomedResults.inPct)})</div>
                                            <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>Avg: {zoomedResults.avgOutRegimeDays.toFixed(1)} days/episode</div>
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#fafafa' }}>
                                            {zoomedResults.totalDays} days (100.0%)
                                        </td>
                                    </tr>

                                    <tr>
                                        <td><strong>Average Net Exposure</strong></td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#e8f4fd' }}>
                                            {tableHelpers.percentFormatter(zoomedResults.inPlanStats.avgExposure)} / 100.0%
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {tableHelpers.percentFormatter(zoomedResults.outPlanStats.avgExposure)} / 100.0%
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#fafafa' }}>
                                            {tableHelpers.percentFormatter(zoomedResults.allPlanStats.avgExposure)} / 100.0%
                                        </td>
                                    </tr>

                                    <tr>
                                        <td><strong>Peak Net Exposure</strong></td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#e8f4fd' }}>
                                            {tableHelpers.percentFormatter(zoomedResults.inPlanStats.peakExposure)} / 100.0%
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {tableHelpers.percentFormatter(zoomedResults.outPlanStats.peakExposure)} / 100.0%
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#fafafa' }}>
                                            {tableHelpers.percentFormatter(zoomedResults.allPlanStats.peakExposure)} / 100.0%
                                        </td>
                                    </tr>

                                    <tr>
                                        <td>
                                            <strong>Capital Efficiency (ROIC)</strong>
                                            <div style={{ fontSize: '11px', color: '#888' }}>Ann. Return per 100% Exposure</div>
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#e8f4fd', fontWeight: 'bold' }}>
                                            <span style={{ color: zoomedResults.inPlanStats.capitalEfficiency >= 0 ? '#198754' : '#dc3545' }}>
                                                {tableHelpers.percentChangeFormatter(zoomedResults.inPlanStats.capitalEfficiency)}
                                            </span>
                                            <span style={{ color: '#888' }}> / </span>
                                            <span style={{ color: zoomedResults.inSpyStats.capitalEfficiency >= 0 ? '#198754' : '#dc3545' }}>
                                                {tableHelpers.percentChangeFormatter(zoomedResults.inSpyStats.capitalEfficiency)}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span style={{ color: zoomedResults.outPlanStats.capitalEfficiency >= 0 ? '#198754' : '#dc3545' }}>
                                                {tableHelpers.percentChangeFormatter(zoomedResults.outPlanStats.capitalEfficiency)}
                                            </span>
                                            <span style={{ color: '#888' }}> / </span>
                                            <span style={{ color: zoomedResults.outSpyStats.capitalEfficiency >= 0 ? '#198754' : '#dc3545' }}>
                                                {tableHelpers.percentChangeFormatter(zoomedResults.outSpyStats.capitalEfficiency)}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#fafafa' }}>
                                            <span style={{ color: zoomedResults.allPlanStats.capitalEfficiency >= 0 ? '#198754' : '#dc3545' }}>
                                                {tableHelpers.percentChangeFormatter(zoomedResults.allPlanStats.capitalEfficiency)}
                                            </span>
                                            <span style={{ color: '#888' }}> / </span>
                                            <span style={{ color: zoomedResults.allSpyStats.capitalEfficiency >= 0 ? '#198754' : '#dc3545' }}>
                                                {tableHelpers.percentChangeFormatter(zoomedResults.allSpyStats.capitalEfficiency)}
                                            </span>
                                        </td>
                                    </tr>

                                    <tr>
                                        <td>
                                            <strong>Regime Efficiency Ratio</strong>
                                            <div style={{ fontSize: '11px', color: '#888' }}>Normalized Productivity Ratio</div>
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#e8f4fd', fontWeight: 'bold' }}>
                                            <span style={{ color: efficiencyComparison?.badgeColor }}>
                                                {efficiencyComparison?.ratioDisplay} ({efficiencyComparison?.badge})
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center', color: '#888' }}>
                                            {efficiencyComparison?.inEff > 0 && efficiencyComparison?.outEff > 0 && efficiencyComparison.inEff !== 0
                                                ? `${(efficiencyComparison.outEff / efficiencyComparison.inEff).toFixed(2)}x`
                                                : '-'}
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#fafafa', color: '#888' }}>
                                            Baseline (1.0x)
                                        </td>
                                    </tr>

                                    <tr>
                                        <td><strong>Win Rate (Positive Days)</strong></td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#e8f4fd' }}>
                                            {tableHelpers.percentFormatter(zoomedResults.inPlanStats.winRate)} / {tableHelpers.percentFormatter(zoomedResults.inSpyStats.winRate)}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {tableHelpers.percentFormatter(zoomedResults.outPlanStats.winRate)} / {tableHelpers.percentFormatter(zoomedResults.outSpyStats.winRate)}
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#fafafa' }}>
                                            {tableHelpers.percentFormatter(zoomedResults.allPlanStats.winRate)} / {tableHelpers.percentFormatter(zoomedResults.allSpyStats.winRate)}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td>
                                            <strong>Regime Win Rate</strong>
                                            <div style={{ fontSize: '11px', color: '#888' }}>% Profitable Regimes (&gt;0% Ret)</div>
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#e8f4fd', fontWeight: 'bold' }}>
                                            {zoomedResults.regimeEpisodes && zoomedResults.regimeEpisodes.length > 0 ? (
                                                <>
                                                    <span style={{ color: zoomedResults.inPlanRegimeWinRate >= 50 ? '#198754' : '#dc3545' }}>
                                                        {tableHelpers.percentFormatter(zoomedResults.inPlanRegimeWinRate)}
                                                    </span>
                                                    <span style={{ color: '#888' }}> / </span>
                                                    <span style={{ color: zoomedResults.inSpyRegimeWinRate >= 50 ? '#198754' : '#dc3545' }}>
                                                        {tableHelpers.percentFormatter(zoomedResults.inSpyRegimeWinRate)}
                                                    </span>
                                                    <div style={{ fontSize: '11px', color: '#888', fontWeight: 'normal' }}>
                                                        ({zoomedResults.inPlanWinningEpisodes}/{zoomedResults.regimeEpisodes.length} vs {zoomedResults.inSpyWinningEpisodes}/{zoomedResults.regimeEpisodes.length} won)
                                                    </div>
                                                </>
                                            ) : (
                                                <span style={{ color: '#888' }}>-</span>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {zoomedResults.outRegimeEpisodes && zoomedResults.outRegimeEpisodes.length > 0 ? (
                                                <>
                                                    <span style={{ color: zoomedResults.outPlanRegimeWinRate >= 50 ? '#198754' : '#dc3545' }}>
                                                        {tableHelpers.percentFormatter(zoomedResults.outPlanRegimeWinRate)}
                                                    </span>
                                                    <span style={{ color: '#888' }}> / </span>
                                                    <span style={{ color: zoomedResults.outSpyRegimeWinRate >= 50 ? '#198754' : '#dc3545' }}>
                                                        {tableHelpers.percentFormatter(zoomedResults.outSpyRegimeWinRate)}
                                                    </span>
                                                    <div style={{ fontSize: '11px', color: '#888', fontWeight: 'normal' }}>
                                                        ({zoomedResults.outPlanWinningEpisodes}/{zoomedResults.outRegimeEpisodes.length} vs {zoomedResults.outSpyWinningEpisodes}/{zoomedResults.outRegimeEpisodes.length} won)
                                                    </div>
                                                </>
                                            ) : (
                                                <span style={{ color: '#888' }}>-</span>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#fafafa' }}>
                                            {zoomedResults.allEpisodesCount > 0 ? (
                                                <>
                                                    <span style={{ color: zoomedResults.allPlanRegimeWinRate >= 50 ? '#198754' : '#dc3545' }}>
                                                        {tableHelpers.percentFormatter(zoomedResults.allPlanRegimeWinRate)}
                                                    </span>
                                                    <span style={{ color: '#888' }}> / </span>
                                                    <span style={{ color: zoomedResults.allSpyRegimeWinRate >= 50 ? '#198754' : '#dc3545' }}>
                                                        {tableHelpers.percentFormatter(zoomedResults.allSpyRegimeWinRate)}
                                                    </span>
                                                    <div style={{ fontSize: '11px', color: '#888', fontWeight: 'normal' }}>
                                                        ({zoomedResults.allPlanWinningEpisodes}/{zoomedResults.allEpisodesCount} vs {zoomedResults.allSpyWinningEpisodes}/{zoomedResults.allEpisodesCount} won)
                                                    </div>
                                                </>
                                            ) : (
                                                <span style={{ color: '#888' }}>-</span>
                                            )}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td><strong>Avg Daily Return</strong></td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#e8f4fd' }}>
                                            {tableHelpers.percentChangeFormatter(zoomedResults.inPlanStats.avgDailyReturn)} / {tableHelpers.percentChangeFormatter(zoomedResults.inSpyStats.avgDailyReturn)}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {tableHelpers.percentChangeFormatter(zoomedResults.outPlanStats.avgDailyReturn)} / {tableHelpers.percentChangeFormatter(zoomedResults.outSpyStats.avgDailyReturn)}
                                        </td>
                                        <td style={{ textAlign: 'center', backgroundColor: '#fafafa' }}>
                                            {tableHelpers.percentChangeFormatter(zoomedResults.allPlanStats.avgDailyReturn)} / {tableHelpers.percentChangeFormatter(zoomedResults.allSpyStats.avgDailyReturn)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Chart Tabs & Visuals */}
                    <div style={{ backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: '6px', padding: '15px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #dee2e6', paddingBottom: '10px', marginBottom: '15px', flexWrap: 'wrap', gap: '8px' }}>
                            <div className="btn-group" role="group">
                                <button
                                    type="button"
                                    className={`btn btn-sm ${activeTab === 'segmented' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setActiveTab('segmented')}
                                >
                                    Segmented Return (%)
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${activeTab === 'timeline' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setActiveTab('timeline')}
                                >
                                    Regime Shaded Timeline
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${activeTab === 'episodes' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setActiveTab('episodes')}
                                >
                                    Per-Regime Return (%)
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${activeTab === 'exposure' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setActiveTab('exposure')}
                                >
                                    Daily Exposure & Regimes
                                </button>
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                                {activeTab === 'segmented' && "Shows cumulative % return if you ONLY held positions during that regime vs staying in cash."}
                                {activeTab === 'timeline' && "Plan % return vs SPY with active regime shading and per-regime return annotations."}
                                {activeTab === 'episodes' && "Plan vs. SPY percentage return for each regime episode across selectable forward horizons (1M, 1W, 2W, etc.)."}
                                {activeTab === 'exposure' && "Plan net exposure over time with regime periods highlighted."}
                            </div>
                        </div>

                        {activeTab === 'segmented' && (
                            <Chart
                                chartType="LineChart"
                                width="100%"
                                height="420px"
                                data={zoomedResults.chartRowsSegmented}
                                options={{
                                    title: `Cumulative Return by Regime (${zoomLabel})`,
                                    curveType: "none",
                                    legend: { position: "top" },
                                    chartArea: { width: "85%", height: "75%" },
                                    vAxis: { format: 'percent' },
                                    colors: ['#0d6efd', '#fd7e14', '#198754', '#6c757d'],
                                    lineWidth: 2
                                }}
                            />
                        )}

                        {activeTab === 'timeline' && (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '8px' }}>
                                    <label style={{ margin: 0, fontSize: '12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#555' }}>
                                        <input
                                            type="checkbox"
                                            checked={showTimelineAnnotations}
                                            onChange={(e) => setShowTimelineAnnotations(e.target.checked)}
                                        />
                                        <span>Show Regime Return Annotations {zoomedResults.isZoomed ? '(Zoomed In)' : '(Full History)'}</span>
                                    </label>
                                </div>
                                <Chart
                                    chartType="ComboChart"
                                    width="100%"
                                    height="420px"
                                    data={zoomedResults.chartRowsTimeline}
                                    options={{
                                        title: `Plan Return vs SPY with Active Regime Periods (${zoomLabel})`,
                                        seriesType: "line",
                                        series: {
                                            0: { targetAxisIndex: 0, color: '#0d6efd', lineWidth: 2 },
                                            1: { targetAxisIndex: 0, color: '#6c757d', lineWidth: 1.5 },
                                            2: { targetAxisIndex: 1, type: "area", color: '#d1e7dd', lineWidth: 0 }
                                        },
                                        vAxes: {
                                            0: { title: "Return (%)", format: 'percent' },
                                            1: { title: "Regime Active", ticks: [0, 1], viewWindow: { min: 0, max: 1.5 } }
                                        },
                                        chartArea: { width: "80%", height: "75%" },
                                        legend: { position: "top" },
                                        annotations: {
                                            alwaysOutside: true,
                                            textStyle: {
                                                fontSize: 10,
                                                bold: true,
                                                color: '#1a1a1a',
                                                auraColor: '#ffffff'
                                            },
                                            stem: {
                                                length: 12,
                                                color: '#0d6efd'
                                            }
                                        }
                                    }}
                                />
                                {zoomedResults.regimeEpisodes && zoomedResults.regimeEpisodes.length > 0 && (
                                    <div style={{ marginTop: '12px', padding: '10px 14px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef', fontSize: '12px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
                                            <span style={{ fontWeight: 600, color: '#333' }}>
                                                Regime Episodes in Current View ({zoomedResults.regimeEpisodes.length} episode{zoomedResults.regimeEpisodes.length > 1 ? 's' : ''}):
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary btn-sm"
                                                style={{ fontSize: '11px', padding: '2px 8px' }}
                                                onClick={() => setActiveTab('episodes')}
                                            >
                                                View Column Chart & Details →
                                            </button>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            {zoomedResults.regimeEpisodes.map((ep) => (
                                                <div
                                                    key={ep.id}
                                                    style={{
                                                        backgroundColor: '#fff',
                                                        border: '1px solid #dee2e6',
                                                        borderRadius: '4px',
                                                        padding: '4px 8px',
                                                        fontSize: '11px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => handleZoomToEpisode(ep)}
                                                    title={ep.startTotal ? `Click to zoom: ${tableHelpers.dateFormmatter(ep.startDate)} to ${ep.endDate ? tableHelpers.dateFormmatter(ep.endDate) : 'Present (Active)'}\nPlan Close: $${Math.round(ep.startTotal).toLocaleString()} → $${Math.round(ep.endTotal).toLocaleString()} (${formatDecimalPercent(ep.planReturn)})\nSPY Close: $${Math.round(ep.startSpy).toLocaleString()} → $${Math.round(ep.endSpy).toLocaleString()} (${formatDecimalPercent(ep.spyReturn)})` : "Click to zoom in on this regime episode"}
                                                >
                                                    <span style={{ fontWeight: 'bold' }}>#{ep.id}:</span>
                                                    <span>{tableHelpers.dateFormmatter(ep.startDate)} → {ep.endDate ? tableHelpers.dateFormmatter(ep.endDate) : <span className="badge bg-success-subtle text-success border border-success-subtle" style={{ fontSize: '9px', padding: '1px 5px' }}>Active</span>} ({ep.days}d)</span>
                                                    <span style={{ color: ep.planReturn >= 0 ? '#198754' : '#dc3545', fontWeight: 600 }}>
                                                        Plan: {formatDecimalPercent(ep.planReturn)}
                                                    </span>
                                                    <span style={{ color: ep.spyReturn >= 0 ? '#198754' : '#dc3545', fontWeight: 600 }}>
                                                        SPY: {formatDecimalPercent(ep.spyReturn)}
                                                    </span>
                                                    <span className={`badge ${ep.alpha >= 0 ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '9px' }}>
                                                        {formatDecimalPercent(ep.alpha)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {activeTab === 'episodes' && (
                            <>
                                {(!zoomedResults.regimeEpisodes || zoomedResults.regimeEpisodes.length === 0) ? (
                                    <div className="alert alert-info" style={{ margin: '30px auto', maxWidth: '600px', textAlign: 'center' }}>
                                        <h6 style={{ fontWeight: 'bold', marginBottom: '8px' }}>No Regime Episodes in Selected Timeframe</h6>
                                        <p style={{ margin: 0, fontSize: '13px' }}>
                                            No trading days matched the active regime conditions during <strong>{zoomLabel}</strong>.
                                            Try selecting a broader timeframe (e.g. Full History or 5Y) or adjusting the regime rules above.
                                        </p>
                                    </div>
                                ) : (
                                    <div>
                                        {/* Episodes Summary Strip */}
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '15px' }}>
                                            <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '6px', padding: '10px 12px' }}>
                                                <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>Total Episodes</div>
                                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#0d6efd' }}>
                                                    {zoomedResults.regimeEpisodes.length}
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#888' }}>
                                                    In {zoomLabel}
                                                </div>
                                            </div>

                                            <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '6px', padding: '10px 12px' }}>
                                                <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>Time in Regime</div>
                                                <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                                    {zoomedResults.inDays} days
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#888' }}>
                                                    {tableHelpers.percentFormatter(zoomedResults.inPct)} of period | Avg: {zoomedResults.avgRegimeDays.toFixed(1)}d
                                                </div>
                                            </div>

                                            <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '6px', padding: '10px 12px' }}>
                                                <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>
                                                    {episodeChartHorizon === 'full' ? 'Avg Return / Episode' : `Avg ${activeHorizonConfig.label} Return`}
                                                </div>
                                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: activeHorizonStats.avgPlanReturn >= 0 ? '#198754' : '#dc3545' }}>
                                                    {formatDecimalPercent(activeHorizonStats.avgPlanReturn)}
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#888' }}>
                                                    SPY Avg: {formatDecimalPercent(activeHorizonStats.avgSpyReturn)}
                                                </div>
                                            </div>

                                            <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '6px', padding: '10px 12px' }}>
                                                <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>
                                                    {episodeChartHorizon === 'full' ? 'Outperformed SPY' : `Outperformed SPY (${activeHorizonConfig.label})`}
                                                </div>
                                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: activeHorizonStats.winRate >= 50 ? '#198754' : '#dc3545' }}>
                                                    {activeHorizonStats.winCount} / {activeHorizonStats.totalCount} ({activeHorizonStats.winRate.toFixed(0)}%)
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#888' }}>
                                                    {episodeChartHorizon === 'full' ? 'Regimes where Plan beat SPY' : `Plan beat SPY over ${activeHorizonConfig.label} horizon`}
                                                </div>
                                            </div>

                                            <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '6px', padding: '10px 12px' }}>
                                                <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>
                                                    {episodeChartHorizon === 'full' ? 'Regime Win Rate' : `${activeHorizonConfig.label} Win Rate (% > 0)`}
                                                </div>
                                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: activeHorizonStats.planWinRate >= 50 ? '#198754' : '#dc3545' }}>
                                                    {tableHelpers.percentFormatter(activeHorizonStats.planWinRate)}
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#888' }}>
                                                    SPY: {tableHelpers.percentFormatter(activeHorizonStats.spyWinRate)} ({activeHorizonStats.planWonCount} vs {activeHorizonStats.spyWonCount} won)
                                                </div>
                                            </div>
                                        </div>

                                        {/* Chart Header & Horizon Dropdown Controls */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', marginTop: '16px', flexWrap: 'wrap', gap: '10px' }}>
                                            <div>
                                                <h6 style={{ fontSize: '14px', fontWeight: 'bold', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span>
                                                        {episodeChartHorizon === 'full'
                                                            ? 'Plan vs. SPY Return During Each Regime Episode'
                                                            : `Plan vs. SPY ${activeHorizonConfig.label} Return After Regime Starts`}
                                                    </span>
                                                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: '11px', fontWeight: 500 }}>
                                                        {zoomLabel}
                                                    </span>
                                                </h6>
                                                <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
                                                    {episodeChartHorizon === 'full'
                                                        ? 'Total percentage return of Plan and SPY from regime start to regime exit.'
                                                        : `Percentage return of Plan and SPY over the first ${activeHorizonConfig.days} market days following each regime start.`}
                                                </div>
                                            </div>
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                <label htmlFor="regime-chart-horizon-select" style={{ fontSize: '12px', fontWeight: 600, color: '#495057', margin: 0, whiteSpace: 'nowrap' }}>
                                                    Return Horizon:
                                                </label>
                                                <select
                                                    id="regime-chart-horizon-select"
                                                    className="form-select form-select-sm"
                                                    style={{ width: 'auto', fontSize: '12px', fontWeight: 600, minWidth: '175px' }}
                                                    value={episodeChartHorizon}
                                                    onChange={(e) => setEpisodeChartHorizon(e.target.value)}
                                                >
                                                    <option value="1m">1M Return (22 days)</option>
                                                    <option value="1w">1W Return (5 days)</option>
                                                    <option value="2w">2W Return (10 days)</option>
                                                    <option value="2m">2M Return (44 days)</option>
                                                    <option value="3m">3M Return (66 days)</option>
                                                    <option value="full">Full Episode Duration</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* Column Chart */}
                                        <Chart
                                            chartType="ColumnChart"
                                            width="100%"
                                            height="420px"
                                            data={episodeChartData || zoomedResults.chartRowsEpisodes}
                                            options={{
                                                title: episodeChartHorizon === 'full'
                                                    ? `Plan vs. SPY Return During Each Regime Episode (${zoomLabel})`
                                                    : `Plan vs. SPY ${activeHorizonConfig.label} Return Following Each Regime Start (${zoomLabel})`,
                                                legend: { position: 'top' },
                                                chartArea: { width: '82%', height: '65%' },
                                                hAxis: {
                                                    title: episodeChartHorizon === 'full' ? 'Regime Episodes (Chronological)' : `Regime Episodes (${activeHorizonConfig.label} Horizon)`,
                                                    slantedText: true,
                                                    slantedTextAngle: 35,
                                                    textStyle: { fontSize: 11 }
                                                },
                                                vAxis: {
                                                    title: episodeChartHorizon === 'full' ? 'Total Return (%)' : `${activeHorizonConfig.label} Return (%)`,
                                                    format: 'percent'
                                                },
                                                colors: ['#0d6efd', '#6c757d'],
                                                bar: { groupWidth: zoomedResults.regimeEpisodes.length <= 4 ? '35%' : '65%' }
                                            }}
                                        />

                                        {/* Post-Regime Forward Performance Table */}
                                        {forwardPerformance && (
                                            <div style={{ marginTop: '25px', marginBottom: '25px', backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: '8px', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                                                    <div>
                                                        <h6 style={{ fontSize: '15px', fontWeight: 'bold', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span>Forward Performance After Regime Starts</span>
                                                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: '11px', fontWeight: 500 }}>
                                                                {zoomLabel}
                                                            </span>
                                                        </h6>
                                                        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '3px' }}>
                                                            Average and best returns, win rates, and starting price to trough drawdowns for fixed horizons following each regime start (trading days).
                                                        </div>
                                                    </div>
                                                    <div className="btn-group btn-group-sm" role="group" aria-label="Forward view mode">
                                                        <button
                                                            type="button"
                                                            className={`btn ${forwardViewMode === 'all' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                            style={{ fontSize: '11px', padding: '3px 10px' }}
                                                            onClick={() => setForwardViewMode('all')}
                                                        >
                                                            Plan vs. SPY
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`btn ${forwardViewMode === 'plan' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                            style={{ fontSize: '11px', padding: '3px 10px' }}
                                                            onClick={() => setForwardViewMode('plan')}
                                                        >
                                                            Plan Only
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`btn ${forwardViewMode === 'spy' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                            style={{ fontSize: '11px', padding: '3px 10px' }}
                                                            onClick={() => setForwardViewMode('spy')}
                                                        >
                                                            SPY Only
                                                        </button>
                                                    </div>
                                                </div>

                                                <div style={{ overflowX: 'auto' }}>
                                                    <table className="table table-sm table-bordered table-hover" style={{ fontSize: '12px', marginBottom: 0, verticalAlign: 'middle' }}>
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th style={{ minWidth: '220px', backgroundColor: '#f8f9fa' }}>
                                                                    Forward Horizon / Metric
                                                                </th>
                                                                {forwardPerformance.map(col => (
                                                                    <th key={col.key} style={{ textAlign: 'center', minWidth: '130px', backgroundColor: '#f8f9fa' }}>
                                                                        <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#212529' }}>{col.label}</div>
                                                                        <div style={{ fontSize: '10px', color: '#6c757d', fontWeight: 'normal' }}>
                                                                            {col.days} market days
                                                                        </div>
                                                                    </th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {forwardViewMode === 'all' && (
                                                                <>
                                                                    {/* Average & Best Return Section */}
                                                                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                                                                        <td colSpan={1 + forwardPerformance.length} style={{ fontWeight: 600, fontSize: '11px', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '6px 10px' }}>
                                                                            Avg &amp; Best Return
                                                                        </td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td style={{ paddingLeft: '18px' }}>
                                                                            <strong>Plan Avg Return</strong>
                                                                        </td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center', color: col.count === 0 ? '#6c757d' : (col.avgPlanReturn >= 0 ? '#198754' : '#dc3545'), fontWeight: 600 }}>
                                                                                {col.count === 0 ? '—' : formatDecimalPercent(col.avgPlanReturn)}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <td style={{ paddingLeft: '18px' }}>
                                                                            <strong>SPY Avg Return</strong>
                                                                        </td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center', color: col.count === 0 ? '#6c757d' : (col.avgSpyReturn >= 0 ? '#198754' : '#dc3545'), fontWeight: 600 }}>
                                                                                {col.count === 0 ? '—' : formatDecimalPercent(col.avgSpyReturn)}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <td style={{ paddingLeft: '18px' }}>
                                                                            <strong>Alpha (Plan - SPY)</strong>
                                                                        </td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center' }}>
                                                                                {col.count === 0 ? (
                                                                                    <span style={{ color: '#6c757d' }}>—</span>
                                                                                ) : (
                                                                                    <span
                                                                                        className={`badge ${col.avgAlpha >= 0 ? 'bg-success-subtle text-success border border-success-subtle' : 'badge bg-danger-subtle text-danger border border-danger-subtle'}`}
                                                                                        style={{ fontSize: '11px', padding: '3px 8px' }}
                                                                                    >
                                                                                        {formatDecimalPercent(col.avgAlpha)}
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <td style={{ paddingLeft: '18px' }}>
                                                                            <strong>Plan Best Return</strong>
                                                                        </td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center', color: col.count === 0 ? '#6c757d' : (col.bestPlanReturn >= 0 ? '#198754' : '#dc3545'), fontWeight: 600 }}>
                                                                                {col.count === 0 ? '—' : formatDecimalPercent(col.bestPlanReturn)}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <td style={{ paddingLeft: '18px' }}>
                                                                            <strong>SPY Best Return</strong>
                                                                        </td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center', color: col.count === 0 ? '#6c757d' : (col.bestSpyReturn >= 0 ? '#198754' : '#dc3545'), fontWeight: 600 }}>
                                                                                {col.count === 0 ? '—' : formatDecimalPercent(col.bestSpyReturn)}
                                                                            </td>
                                                                        ))}
                                                                    </tr>

                                                                    {/* Win Rate Section */}
                                                                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                                                                        <td colSpan={1 + forwardPerformance.length} style={{ fontWeight: 600, fontSize: '11px', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '6px 10px' }}>
                                                                            Win Rate
                                                                        </td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td style={{ paddingLeft: '18px' }}>
                                                                            <strong>Plan Win Rate (% &gt; 0)</strong>
                                                                        </td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center' }}>
                                                                                {col.count === 0 ? (
                                                                                    <span style={{ color: '#6c757d' }}>—</span>
                                                                                ) : (
                                                                                    <div>
                                                                                        <span style={{ fontWeight: 600, color: col.planWinRate >= 50 ? '#198754' : '#dc3545' }}>
                                                                                            {tableHelpers.percentFormatterOneDecimal(col.planWinRate)}
                                                                                        </span>
                                                                                        <div style={{ fontSize: '10px', color: '#6c757d' }}>
                                                                                            {col.planPositiveCount}/{col.count} won
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <td style={{ paddingLeft: '18px' }}>
                                                                            <strong>SPY Win Rate (% &gt; 0)</strong>
                                                                        </td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center' }}>
                                                                                {col.count === 0 ? (
                                                                                    <span style={{ color: '#6c757d' }}>—</span>
                                                                                ) : (
                                                                                    <div>
                                                                                        <span style={{ fontWeight: 600, color: col.spyWinRate >= 50 ? '#198754' : '#dc3545' }}>
                                                                                            {tableHelpers.percentFormatterOneDecimal(col.spyWinRate)}
                                                                                        </span>
                                                                                        <div style={{ fontSize: '10px', color: '#6c757d' }}>
                                                                                            {col.spyPositiveCount}/{col.count} won
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <td style={{ paddingLeft: '18px' }}>
                                                                            <strong>Plan Outperformed SPY (%)</strong>
                                                                        </td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center' }}>
                                                                                {col.count === 0 ? (
                                                                                    <span style={{ color: '#6c757d' }}>—</span>
                                                                                ) : (
                                                                                    <div>
                                                                                        <span style={{ fontWeight: 600, color: col.beatSpyRate >= 50 ? '#198754' : '#dc3545' }}>
                                                                                            {tableHelpers.percentFormatterOneDecimal(col.beatSpyRate)}
                                                                                        </span>
                                                                                        <div style={{ fontSize: '10px', color: '#6c757d' }}>
                                                                                            {col.planBeatSpyCount}/{col.count} beat
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                        ))}
                                                                    </tr>

                                                                    {/* Max Drawdown Section */}
                                                                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                                                                        <td colSpan={1 + forwardPerformance.length} style={{ fontWeight: 600, fontSize: '11px', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '6px 10px' }}>
                                                                            Max Drawdown (Start-to-Trough)
                                                                        </td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td style={{ paddingLeft: '18px' }}>
                                                                            <strong>Plan Max Drawdown</strong>
                                                                            <div style={{ fontSize: '11px', color: '#888' }}>Average / Worst Start-to-Trough</div>
                                                                        </td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center' }}>
                                                                                {col.count === 0 ? (
                                                                                    <span style={{ color: '#6c757d' }}>—</span>
                                                                                ) : (
                                                                                    <div>
                                                                                        <div style={{ color: '#dc3545', fontWeight: 600 }}>
                                                                                            {col.avgMaxDdPlan > 0 ? `-${tableHelpers.percentFormatterOneDecimal(col.avgMaxDdPlan)}` : '0.0%'}
                                                                                        </div>
                                                                                        <div style={{ fontSize: '10px', color: '#6c757d' }}>
                                                                                            Worst: {col.worstDdPlan > 0 ? `-${tableHelpers.percentFormatterOneDecimal(col.worstDdPlan)}` : '0.0%'}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <td style={{ paddingLeft: '18px' }}>
                                                                            <strong>SPY Max Drawdown</strong>
                                                                            <div style={{ fontSize: '11px', color: '#888' }}>Average / Worst Start-to-Trough</div>
                                                                        </td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center' }}>
                                                                                {col.count === 0 ? (
                                                                                    <span style={{ color: '#6c757d' }}>—</span>
                                                                                ) : (
                                                                                    <div>
                                                                                        <div style={{ color: '#dc3545', fontWeight: 600 }}>
                                                                                            {col.avgMaxDdSpy > 0 ? `-${tableHelpers.percentFormatterOneDecimal(col.avgMaxDdSpy)}` : '0.0%'}
                                                                                        </div>
                                                                                        <div style={{ fontSize: '10px', color: '#6c757d' }}>
                                                                                            Worst: {col.worstDdSpy > 0 ? `-${tableHelpers.percentFormatterOneDecimal(col.worstDdSpy)}` : '0.0%'}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                </>
                                                            )}

                                                            {forwardViewMode === 'plan' && (
                                                                <>
                                                                    <tr>
                                                                        <td><strong>Avg Return</strong></td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center', color: col.count === 0 ? '#6c757d' : (col.avgPlanReturn >= 0 ? '#198754' : '#dc3545'), fontWeight: 600 }}>
                                                                                {col.count === 0 ? '—' : formatDecimalPercent(col.avgPlanReturn)}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <td><strong>Best Return</strong></td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center', color: col.count === 0 ? '#6c757d' : (col.bestPlanReturn >= 0 ? '#198754' : '#dc3545'), fontWeight: 600 }}>
                                                                                {col.count === 0 ? '—' : formatDecimalPercent(col.bestPlanReturn)}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <td><strong>Win Rate (% &gt; 0)</strong></td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center' }}>
                                                                                {col.count === 0 ? (
                                                                                    <span style={{ color: '#6c757d' }}>—</span>
                                                                                ) : (
                                                                                    <div>
                                                                                        <span style={{ fontWeight: 600, color: col.planWinRate >= 50 ? '#198754' : '#dc3545' }}>
                                                                                            {tableHelpers.percentFormatterOneDecimal(col.planWinRate)}
                                                                                        </span>
                                                                                        <span style={{ fontSize: '10px', color: '#6c757d', marginLeft: '4px' }}>
                                                                                            ({col.planPositiveCount}/{col.count})
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <td>
                                                                            <strong>Avg Max Drawdown</strong>
                                                                        </td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center', color: '#dc3545', fontWeight: 600 }}>
                                                                                {col.count === 0 ? '—' : (col.avgMaxDdPlan > 0 ? `-${tableHelpers.percentFormatterOneDecimal(col.avgMaxDdPlan)}` : '0.0%')}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <td>
                                                                            <strong>Worst Drawdown</strong>
                                                                        </td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center', color: '#dc3545', fontWeight: 600 }}>
                                                                                {col.count === 0 ? '—' : (col.worstDdPlan > 0 ? `-${tableHelpers.percentFormatterOneDecimal(col.worstDdPlan)}` : '0.0%')}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                </>
                                                            )}

                                                            {forwardViewMode === 'spy' && (
                                                                <>
                                                                    <tr>
                                                                        <td><strong>Avg Return (SPY)</strong></td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center', color: col.count === 0 ? '#6c757d' : (col.avgSpyReturn >= 0 ? '#198754' : '#dc3545'), fontWeight: 600 }}>
                                                                                {col.count === 0 ? '—' : formatDecimalPercent(col.avgSpyReturn)}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <td><strong>Best Return (SPY)</strong></td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center', color: col.count === 0 ? '#6c757d' : (col.bestSpyReturn >= 0 ? '#198754' : '#dc3545'), fontWeight: 600 }}>
                                                                                {col.count === 0 ? '—' : formatDecimalPercent(col.bestSpyReturn)}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <td><strong>Win Rate (% &gt; 0)</strong></td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center' }}>
                                                                                {col.count === 0 ? (
                                                                                    <span style={{ color: '#6c757d' }}>—</span>
                                                                                ) : (
                                                                                    <div>
                                                                                        <span style={{ fontWeight: 600, color: col.spyWinRate >= 50 ? '#198754' : '#dc3545' }}>
                                                                                            {tableHelpers.percentFormatterOneDecimal(col.spyWinRate)}
                                                                                        </span>
                                                                                        <span style={{ fontSize: '10px', color: '#6c757d', marginLeft: '4px' }}>
                                                                                            ({col.spyPositiveCount}/{col.count})
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <td>
                                                                            <strong>Avg Max Drawdown</strong>
                                                                        </td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center', color: '#dc3545', fontWeight: 600 }}>
                                                                                {col.count === 0 ? '—' : (col.avgMaxDdSpy > 0 ? `-${tableHelpers.percentFormatterOneDecimal(col.avgMaxDdSpy)}` : '0.0%')}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                    <tr>
                                                                        <td>
                                                                            <strong>Worst Drawdown</strong>
                                                                        </td>
                                                                        {forwardPerformance.map(col => (
                                                                            <td key={col.key} style={{ textAlign: 'center', color: '#dc3545', fontWeight: 600 }}>
                                                                                {col.count === 0 ? '—' : (col.worstDdSpy > 0 ? `-${tableHelpers.percentFormatterOneDecimal(col.worstDdSpy)}` : '0.0%')}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                </>
                                                            )}

                                                            {/* Episodes Evaluated Row */}
                                                            <tr style={{ backgroundColor: '#fafafa', borderTop: '2px solid #dee2e6' }}>
                                                                <td>
                                                                    <strong>Episodes Evaluated</strong>
                                                                </td>
                                                                {forwardPerformance.map(col => (
                                                                    <td key={col.key} style={{ textAlign: 'center', fontSize: '11px', color: '#495057' }}>
                                                                        <strong>{col.count}</strong>
                                                                        <span style={{ color: '#888' }}> / {col.totalEpisodes}</span>
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>

                                                <div style={{ fontSize: '11px', color: '#888', marginTop: '10px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px' }}>
                                                    <span>* Durations reflect market trading days (1W = 5d, 2W = 10d, 1M = 22d, 2M = 44d, 3M = 66d) measured from regime episode start date.</span>
                                                    <span>Episodes with fewer remaining trading days in dataset than the horizon are omitted from that horizon's sample.</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Episode Breakdown Table */}
                                        <div style={{ marginTop: '20px' }}>
                                            <h6 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>
                                                Regime Episode Performance Breakdown ({zoomLabel})
                                            </h6>
                                            <div style={{ overflowX: 'auto' }}>
                                                <table className="table table-sm table-striped table-hover" style={{ fontSize: '12px', marginBottom: 0 }}>
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Start Date</th>
                                                            <th>End Date</th>
                                                            <th style={{ textAlign: 'right' }}>Duration</th>
                                                            <th style={{ textAlign: 'center' }}>Exit Trigger</th>
                                                            <th style={{ textAlign: 'right' }}>Plan Return</th>
                                                            <th style={{ textAlign: 'right' }}>SPY Return</th>
                                                            <th style={{ textAlign: 'right' }}>Alpha (Plan - SPY)</th>
                                                            <th style={{ textAlign: 'center' }}>Outcome</th>
                                                            <th style={{ textAlign: 'center' }}>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {zoomedResults.regimeEpisodes.map((ep) => {
                                                            const planWon = ep.planReturn > ep.spyReturn;
                                                            const tie = ep.planReturn === ep.spyReturn;
                                                            return (
                                                                <tr key={ep.id}>
                                                                    <td style={{ fontWeight: 'bold' }}>
                                                                        {ep.id}
                                                                        {ep.isOngoing && <span className="badge bg-success-subtle text-success border border-success-subtle ms-1" style={{ fontSize: '9px', padding: '1px 5px' }}>Active</span>}
                                                                    </td>
                                                                    <td>
                                                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                                            <Link
                                                                                to="#"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    handleOpenDrilldown(ep.startDate, ep.endDate || ep.startDate);
                                                                                }}
                                                                                title={ep.startTotal ? `Entered regime: ${tableHelpers.dateFormmatter(ep.startDate)} (Plan Close: $${Math.round(ep.startTotal).toLocaleString()} | SPY: $${Math.round(ep.startSpy).toLocaleString()}) - Click to view active trades in AlgoFinder` : "View active trades in AlgoFinder for this regime episode"}
                                                                            >
                                                                                {tableHelpers.dateFormmatter(ep.startDate)}
                                                                            </Link>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-outline-primary btn-sm"
                                                                                style={{
                                                                                    fontSize: '10px',
                                                                                    padding: '0 4px',
                                                                                    lineHeight: '16px',
                                                                                    fontWeight: 600,
                                                                                    borderRadius: '3px'
                                                                                }}
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    handleAnalyzeDate(ep.startDate);
                                                                                }}
                                                                                title={`Prepopulate date picker with ${tableHelpers.dateFormmatter(ep.startDate)} and run regime analysis`}
                                                                            >
                                                                                ⚡ Analyze
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        {ep.isOngoing || !ep.endDate ? (
                                                                            <span className="badge bg-success-subtle text-success border border-success-subtle" style={{ fontSize: '11px', padding: '3px 8px' }}>
                                                                                Active
                                                                            </span>
                                                                        ) : (
                                                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                                                <Link
                                                                                    to="#"
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        handleOpenDrilldown(ep.startDate, ep.endDate);
                                                                                    }}
                                                                                    title={ep.endTotal ? `Exited regime: ${tableHelpers.dateFormmatter(ep.endDate)} (Plan Close: $${Math.round(ep.endTotal).toLocaleString()} | SPY: $${Math.round(ep.endSpy).toLocaleString()}) - Click to view active trades in AlgoFinder` : "View active trades in AlgoFinder for this regime episode"}
                                                                                >
                                                                                    {tableHelpers.dateFormmatter(ep.endDate)}
                                                                                </Link>
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-outline-primary btn-sm"
                                                                                    style={{
                                                                                        fontSize: '10px',
                                                                                        padding: '0 4px',
                                                                                        lineHeight: '16px',
                                                                                        fontWeight: 600,
                                                                                        borderRadius: '3px'
                                                                                    }}
                                                                                    onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        handleAnalyzeDate(ep.endDate);
                                                                                    }}
                                                                                    title={`Prepopulate date picker with ${tableHelpers.dateFormmatter(ep.endDate)} and run regime analysis`}
                                                                                >
                                                                                    ⚡ Analyze
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td style={{ textAlign: 'right' }}>{ep.days} days</td>
                                                                    <td style={{ textAlign: 'center' }}>
                                                                        {ep.exitTrigger ? (
                                                                            <button
                                                                                type="button"
                                                                                className={`btn btn-sm ${ep.exitTrigger.failedRules?.length > 0 ? 'btn-outline-danger' : (ep.isOngoing ? 'btn-outline-success' : 'btn-outline-secondary')}`}
                                                                                style={{
                                                                                    cursor: 'pointer',
                                                                                    fontSize: '11px',
                                                                                    padding: '2px 8px',
                                                                                    whiteSpace: 'nowrap',
                                                                                    lineHeight: '18px',
                                                                                    borderRadius: '4px',
                                                                                    fontWeight: 500
                                                                                }}
                                                                                onClick={() => setSelectedExitEpisode(ep)}
                                                                                title="Click to view exit trigger details"
                                                                            >
                                                                                {ep.exitTrigger.failedRules?.length > 0 ? `⚠️ ${ep.exitTrigger.label}` : ep.exitTrigger.label}
                                                                            </button>
                                                                        ) : (
                                                                            <span style={{ color: '#aaa' }}>—</span>
                                                                        )}
                                                                    </td>
                                                                    <td
                                                                        style={{ textAlign: 'right', fontWeight: 600, color: ep.planReturn >= 0 ? '#198754' : '#dc3545' }}
                                                                        title={ep.startTotal && ep.endTotal ? `Plan Close: $${Math.round(ep.startTotal).toLocaleString()} → $${Math.round(ep.endTotal).toLocaleString()} (${formatDecimalPercent(ep.planReturn)})` : undefined}
                                                                    >
                                                                        {formatDecimalPercent(ep.planReturn)}
                                                                    </td>
                                                                    <td
                                                                        style={{ textAlign: 'right', fontWeight: 600, color: ep.spyReturn >= 0 ? '#198754' : '#dc3545' }}
                                                                        title={ep.startSpy && ep.endSpy ? `SPY Close: $${Math.round(ep.startSpy).toLocaleString()} → $${Math.round(ep.endSpy).toLocaleString()} (${formatDecimalPercent(ep.spyReturn)})` : undefined}
                                                                    >
                                                                        {formatDecimalPercent(ep.spyReturn)}
                                                                    </td>
                                                                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: ep.alpha >= 0 ? '#198754' : '#dc3545' }}>
                                                                        {formatDecimalPercent(ep.alpha)}
                                                                    </td>
                                                                    <td style={{ textAlign: 'center' }}>
                                                                        {tie ? (
                                                                            <span className="badge bg-light text-dark">Tie</span>
                                                                        ) : planWon ? (
                                                                            <span className="badge bg-success">Plan Outperformed</span>
                                                                        ) : (
                                                                            <span className="badge bg-secondary">SPY Outperformed</span>
                                                                        )}
                                                                    </td>
                                                                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-primary btn-sm me-1"
                                                                            style={{ fontSize: '10px', padding: '1px 6px' }}
                                                                            onClick={() => handleZoomToEpisode(ep)}
                                                                            title="Zoom to this episode on timeline chart"
                                                                        >
                                                                            Zoom In
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-secondary btn-sm"
                                                                            style={{ fontSize: '10px', padding: '1px 6px' }}
                                                                            onClick={() => handleOpenMarketChart(ep.startDate, ep.endDate)}
                                                                            title={`View ${marketSymbol} candlestick chart for this episode (${tableHelpers.dateFormmatter(ep.startDate)} - ${tableHelpers.dateFormmatter(ep.endDate || ep.startDate)})`}
                                                                        >
                                                                            📈 Chart
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                    <tfoot className="table-light" style={{ fontWeight: 'bold' }}>
                                                        <tr>
                                                            <td colSpan="3">Total / Average ({zoomedResults.regimeEpisodes.length} Episodes)</td>
                                                            <td style={{ textAlign: 'right' }}>{zoomedResults.inDays} days</td>
                                                            <td style={{ textAlign: 'center' }}>—</td>
                                                            <td style={{ textAlign: 'right', color: zoomedResults.cumulativeInPlanRet >= 0 ? '#198754' : '#dc3545' }}>
                                                                {formatDecimalPercent(zoomedResults.cumulativeInPlanRet)} (cum.)
                                                            </td>
                                                            <td style={{ textAlign: 'right', color: zoomedResults.cumulativeInSpyRet >= 0 ? '#198754' : '#dc3545' }}>
                                                                {formatDecimalPercent(zoomedResults.cumulativeInSpyRet)} (cum.)
                                                            </td>
                                                            <td style={{ textAlign: 'right', color: (zoomedResults.cumulativeInPlanRet - zoomedResults.cumulativeInSpyRet) >= 0 ? '#198754' : '#dc3545' }}>
                                                                {formatDecimalPercent(zoomedResults.cumulativeInPlanRet - zoomedResults.cumulativeInSpyRet)}
                                                            </td>
                                                            <td style={{ textAlign: 'center' }} colSpan="2">
                                                                <span className="badge bg-primary">
                                                                    {zoomedResults.winEpisodesCount} / {zoomedResults.regimeEpisodes.length} Won ({zoomedResults.winRate.toFixed(0)}%)
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {activeTab === 'exposure' && (
                            <Chart
                                chartType="ComboChart"
                                width="100%"
                                height="420px"
                                data={zoomedResults.chartRowsExposure}
                                options={{
                                    title: `Daily Plan Exposure Level vs. Active Regime (${zoomLabel})`,
                                    seriesType: "line",
                                    series: {
                                        0: { targetAxisIndex: 0, color: '#0d6efd', lineWidth: 2 },
                                        1: { targetAxisIndex: 0, color: '#20c997', lineWidth: 1.5 },
                                        2: { targetAxisIndex: 1, type: "area", color: '#e8f4fd', lineWidth: 0 }
                                    },
                                    vAxes: {
                                        0: { title: "Exposure (%)", format: '#\'%\'' },
                                        1: { title: "Regime Active", ticks: [0, 100], viewWindow: { min: 0, max: 120 } }
                                    },
                                    chartArea: { width: "80%", height: "75%" },
                                    legend: { position: "top" }
                                }}
                            />
                        )}
                    </div>
                </>
            ) : null}

            <Drawer
                anchor={'bottom'}
                open={isDrilldownOpen}
                onClose={() => setIsDrilldownOpen(false)}
            >
                <div style={{ padding: '10px' }}>
                    <center><b><Link to="#" onClick={(e) => { e.preventDefault(); setIsDrilldownOpen(false); }}>Close</Link></b></center>
                    <br />
                    <h4>Active Trades {drilldownStartDate && drilldownEndDate ? `(${tableHelpers.dateFormmatter(drilldownStartDate)} - ${tableHelpers.dateFormmatter(drilldownEndDate)})` : ''}</h4>
                    {isDrilldownOpen && (
                        <AlgoFinder
                            role={props.role}
                            user={props.user}
                            mobileView={props.mobileView}
                            planId={props.planId}
                            startDate={drilldownStartDate}
                            endDate={drilldownEndDate}
                        />
                    )}
                </div>
            </Drawer>

            {/* Market Symbol Stock Research Drawer */}
            <Drawer
                anchor={'right'}
                open={isMarketChartOpen}
                onClose={() => setIsMarketChartOpen(false)}
            >
                <div style={{ padding: '20px', width: 'min(96vw, 1200px)', boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #dee2e6', paddingBottom: '12px', marginBottom: '15px' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <h4 style={{ margin: 0, fontWeight: 'bold' }}>
                                    📈 {marketSymbol} Stock Research
                                </h4>
                                <span className="badge bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: '11px', padding: '3px 8px' }}>
                                    {marketSymbol}
                                </span>
                            </div>
                            {marketChartTitle && (
                                <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '4px' }}>
                                    <strong>{marketChartTitle}</strong>
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            style={{ fontWeight: 600, padding: '4px 12px' }}
                            onClick={() => setIsMarketChartOpen(false)}
                        >
                            ✕ Close
                        </button>
                    </div>

                    {isMarketChartOpen && (
                        <StockResearch
                            symbol={marketSymbol}
                            startDate={marketChartStart}
                            endDate={marketChartEnd}
                            annotations={marketChartAnnotations}
                            role={props.role}
                            user={props.user}
                            userId={props.userId || props.user?.id}
                            mobileView={props.mobileView}
                            planId={props.planId}
                            accountId={props.accountId}
                            chartWidth={Math.min(window.innerWidth * 0.92, 1140)}
                        />
                    )}

                    <div style={{ marginTop: '20px', paddingBottom: '10px', textAlign: 'center' }}>
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setIsMarketChartOpen(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Drawer>

            {selectedExitEpisode && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.55)',
                        zIndex: 99999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px'
                    }}
                    onClick={() => setSelectedExitEpisode(null)}
                >
                    <div
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '8px',
                            maxWidth: '560px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.25)',
                            padding: '22px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', borderBottom: '1px solid #dee2e6', paddingBottom: '10px' }}>
                            <div>
                                <h5 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px', color: '#212529' }}>
                                    Regime Exit Trigger Details
                                </h5>
                                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '3px' }}>
                                    Episode #{selectedExitEpisode.id} • {tableHelpers.dateFormmatter(selectedExitEpisode.startDate)} → {selectedExitEpisode.endDate ? tableHelpers.dateFormmatter(selectedExitEpisode.endDate) : 'Active'} ({selectedExitEpisode.days} day{selectedExitEpisode.days > 1 ? 's' : ''})
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn-close"
                                style={{ cursor: 'pointer', padding: '6px' }}
                                aria-label="Close"
                                onClick={() => setSelectedExitEpisode(null)}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '16px', backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '6px', border: '1px solid #e9ecef', fontSize: '12px' }}>
                            <div>
                                <span style={{ color: '#6c757d', fontSize: '11px' }}>Plan Return:</span>
                                <div style={{ fontWeight: 'bold', color: selectedExitEpisode.planReturn >= 0 ? '#198754' : '#dc3545' }}>
                                    {formatDecimalPercent(selectedExitEpisode.planReturn)}
                                </div>
                            </div>
                            <div>
                                <span style={{ color: '#6c757d', fontSize: '11px' }}>SPY Return:</span>
                                <div style={{ fontWeight: 'bold', color: selectedExitEpisode.spyReturn >= 0 ? '#198754' : '#dc3545' }}>
                                    {formatDecimalPercent(selectedExitEpisode.spyReturn)}
                                </div>
                            </div>
                            <div>
                                <span style={{ color: '#6c757d', fontSize: '11px' }}>Alpha:</span>
                                <div style={{ fontWeight: 'bold', color: selectedExitEpisode.alpha >= 0 ? '#198754' : '#dc3545' }}>
                                    {formatDecimalPercent(selectedExitEpisode.alpha)}
                                </div>
                            </div>
                        </div>

                        {selectedExitEpisode.exitTrigger?.failedRules && selectedExitEpisode.exitTrigger.failedRules.length > 0 ? (
                            <div>
                                <div className="alert alert-danger" style={{ fontSize: '13px', padding: '10px 12px', marginBottom: '14px', borderRadius: '6px' }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                                        ⚠️ Regime Terminated: {selectedExitEpisode.exitTrigger.label}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#58151c' }}>
                                        On <strong>{tableHelpers.dateFormmatter(selectedExitEpisode.endDate)}</strong>, the following condition{selectedExitEpisode.exitTrigger.failedRules.length > 1 ? 's were' : ' was'} not met, causing the strategy to exit the regime:
                                    </div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {selectedExitEpisode.exitTrigger.failedRules.map((failed, idx) => (
                                        <div key={idx} style={{ border: '1px solid #f5c2c7', backgroundColor: '#fff5f5', borderRadius: '6px', padding: '12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                                                <span className="badge bg-danger" style={{ fontSize: '11px' }}>Rule {failed.ruleNumber} Failed</span>
                                                <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 'bold', color: '#842029' }}>
                                                    {failed.conditionText}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#333', lineHeight: 1.6 }}>
                                                <div>• <strong>Actual Value:</strong> {failed.val1Label} was <strong>{formatValueDisplay(failed.val1)}</strong></div>
                                                <div>• <strong>Required Target:</strong> Must be <strong>{failed.operator}</strong> {failed.val2Label} ({formatValueDisplay(failed.val2)})</div>
                                                <div style={{ color: '#dc3545', fontWeight: 600, marginTop: '4px' }}>
                                                    ✕ Condition not satisfied ({formatValueDisplay(failed.val1)} is not {failed.operator} {formatValueDisplay(failed.val2)})
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : selectedExitEpisode.isOngoing ? (
                            <div className="alert alert-success" style={{ fontSize: '13px', padding: '12px' }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>✓ Regime Still Active</div>
                                <div>This regime episode is currently ongoing as of the latest market data. All active regime conditions continue to be met.</div>
                            </div>
                        ) : (
                            <div className="alert alert-info" style={{ fontSize: '13px', padding: '12px' }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Timeframe Boundary Reached</div>
                                <div>{selectedExitEpisode.exitTrigger?.tooltip || `Episode reached end of selected timeframe on ${tableHelpers.dateFormmatter(selectedExitEpisode.endDate)}.`}</div>
                            </div>
                        )}

                        <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #dee2e6', paddingTop: '12px' }}>
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                style={{ fontSize: '11px' }}
                                onClick={() => {
                                    handleZoomToEpisode(selectedExitEpisode);
                                    setSelectedExitEpisode(null);
                                }}
                            >
                                🔍 Zoom In on Timeline Chart
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                style={{ fontSize: '11px', minWidth: '70px' }}
                                onClick={() => setSelectedExitEpisode(null)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PlanRegimes;